/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL DICCIONARIO DE LA INTERFAZ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * TOTAL SOBRE LA UNIÓN EXACTA DE CLAVES. Añadir una clave rompe la
 * compilación en los tres idiomas ANTES de que exista la pantalla que la
 * usaría.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * POR QUÉ ESTO NO ES UN `Record<string, string>`
 *
 * CICATRIZ Nº 35 (OJOS): un mapa de traducciones indexado por `string` tenía
 * cuatro entradas para once códigos, y UN USUARIO LLEGÓ A LEER
 * `motor.sistema_sin_ejercicio` EN SU RUTINA.
 *
 * Aquí el mismo fallo no sería feo: sería una cadena interna impresa en la
 * pantalla de un producto de salud, o —peor— una de las palabras que declaran
 * finalidad sanitaria colándose por la puerta de atrás.
 *
 * Con `Record<Clave, string>` y `Clave` derivada del diccionario español, una
 * clave que falte en inglés o en portugués no compila. No hay forma de
 * desplegar una traducción a medias.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * Y NINGUNA CADENA DE AQUÍ PUEDE LLEVAR UNA PALABRA PROHIBIDA.
 *
 * `verificar:i18n` pasa las tres tablas por la misma lista que gobierna el
 * catálogo y la landing. Los nombres internos —`dosisInicio`, `respaldo`—
 * viven en el código; lo que se lee en pantalla sale de aquí, y aquí no entran.
 * ───────────────────────────────────────────────────────────────────────────
 */

import type { Idioma } from "@vc/contenido";

/* ─────────────────────────────────────────────────────────────────────────
   EL ESPAÑOL ES LA FUENTE DE CLAVES

   16/09/2026 · PASADA DE IDIOMA EN LOS TRES BLOQUES, Y EL CASTELLANO ERA EL PEOR

   El dueño, leyendo la portada de la aplicación como cliente: «Tu voz se
   entrena como se entrena cualquier otra cosa del cuerpo. En serio? cualquier
   otra cosa del cuerpo. que mierda es esa? […] no hace sentido a nivel de
   idioma, es ofensivo, es depreciativo». Y detrás: «revisa todo en cada idioma,
   pero pasando a la mentalidad del idioma que vas a validar».

   Se leyeron los tres bloques enteros —577 claves cada uno— con la cabeza de un
   cliente de Latinoamérica, de Estados Unidos y de Brasil. Salieron 315
   hallazgos y aquí están aplicados los que no son gusto personal:

     · CASTELLANO DE ESPAÑA en un producto que se vende en Latinoamérica:
       «ordenador», «coche», «conducir», «tumbado», «apetece», «pulsar»,
       «montar un plan», «rato», «talla», «justificante», «logopeda», «ictus»,
       y el pretérito compuesto por todas partes. Dos de esas estaban en
       PREGUNTAS DE SEGURIDAD: quien no entiende «logopeda» o «¿te lleva
       alguien?» contesta que no, y se le arma un plan que no le toca.
     · EL PIE LEGAL decía «no es un producto sanitario». En Colombia y en
       México un sanitario es el inodoro. Ahora dice «no es un dispositivo
       médico» (pendiente de que lo confirme el abogado del dueño).
     · TEXTO FALSO: `micuenta.sin_suscripcion` prometía «puedes seguir usando tu
       plan» desde que se quitaron las sesiones gratis (`ba331d0`), y el muro
       dice lo contrario dos pantallas más allá. En los tres idiomas.
     · CALCOS del castellano en inglés y portugués, y frases sin sujeto o sin
       objeto («tap it», «no encontramos quem descreva assim», «sube solo»).

   Lo que decía antes está en git (este commit). El barrido de palabras de
   España lo sostiene desde hoy `verificar:jerga`, que antes solo miraba el
   británico en inglés.
   ───────────────────────────────────────────────────────────────────────── */

const ES = {
  // ── Marca y navegación ────────────────────────────────────────────────
  "app.nombre": "Voice Comfort",
  "app.biblioteca": "Biblioteca",
  "app.mi_plan": "Mi plan",
  "app.ajustes": "Ajustes",
  "app.idioma": "Idioma",
  "app.cambiar_tema": "Cambiar el tema",
  "app.tema_claro": "Claro",
  "app.tema_oscuro": "Oscuro",
  "app.tema_sistema": "Como el sistema",
  "app.volver": "Volver",
  "app.empezar": "Empezar",
  "app.seguir": "Seguir",
  "app.cargando": "…",
  "app.montando_plan": "Armando tu plan.",
  "app.montando_sesion": "Armando la sesión de hoy.",
  "app.buscando_cuenta": "Buscando tus datos.",
  /*
    CUANDO NO SE PUDO, Y QUÉ HACER.

    El onboarding no tenía ninguna: un `fetch` que fallaba dejaba el botón
    «Seguir» sin hacer nada, y en la última pantalla dejaba «Montando tu
    plan…» encendido para siempre con las cuatro opciones deshabilitadas.
    Quince preguntas contestadas y ninguna salida.
  */
  "app.reintentar": "Volver a intentarlo",
  "app.cerrar": "Cerrar",
  /*
    «NO HAY» Y «NO SE HA PODIDO PREGUNTAR» SON DOS PANTALLAS, NO UNA.

    Estaban colapsadas en la misma, y eso convirtió un callejón en un camino
    destructivo: un 500 mientras el servidor se reinicia pintaba «todavía no
    tienes plan» a quien sí lo tenía, y el botón de debajo le mandaba a rehacer
    el onboarding — que retira el plan viejo y deja el contador de sesiones a
    cero. Perder la escalera entera por hacer caso a un botón.
  */
  "fallo.no_se_pudo_titulo": "No pudimos consultar tu plan.",
  "fallo.no_se_pudo_texto": "No es que no tengas plan: es que ahora mismo no pudimos saberlo. Vuelve a intentarlo en un momento.",
  "fallo.sin_puerta_titulo": "Faltan las respuestas de seguridad.",
  "fallo.sin_puerta_texto":
    "Tu plan ya está, pero no podemos mostrártelo sin las preguntas contestadas. Son cortas y se " +
    "contestan una sola vez.",

  // ── El pie legal. NO es letra pequeña: es lo que sostiene todo. ───────
  "pie.legal":
    "Voice Comfort entrena una habilidad. No es un dispositivo médico, no diagnostica y no reemplaza la " +
    "consulta con un profesional. Cuando algo conviene revisarlo, te lo decimos y te indicamos con quién.",

  // ── Onboarding ────────────────────────────────────────────────────────
  // ── El onboarding, entero ─────────────────────────────────────────
  "onb.bienvenida_titulo": "Haz esto durante veinte segundos.",
  "onb.bienvenida_p1":
    "Junta los labios sin apretar y haz un mmm cómodo, como si asintieras. Deberías notar " +
    "cosquilleo en los labios o en la nariz.",
  "onb.bienvenida_p2":
    "Ya está. Eso es un ejercicio de voz de verdad, y lo acabas de hacer sin registrarte, sin " +
    "pagar y sin contarnos nada.",
  "onb.bienvenida_aviso":
    "Ahora vienen unas preguntas. Unas cuantas son de seguridad y no se pueden saltar: hay ejercicios que " +
    "no se practican en casa, y eso hay que saberlo antes de armarte un plan.",
  "onb.seguir": "Seguir",
  /* 16/09/2026: el hilo de arriba («Paso 2 de 4 · Micrófono y voz») y el anillo que cuenta los veinte
     segundos del primer ejercicio. Ver `IlustracionDelPaso.tsx`. */
  "onb.hilo_paso": "Paso {n} de {total}",
  "onb.hilo.ejercicio": "Un ejercicio",
  "onb.hilo.voz": "Micrófono y voz",
  "onb.hilo.seguridad": "Seguridad",
  "onb.hilo.plan": "Tu plan",
  "onb.contar_veinte": "Contar los veinte segundos",
  "onb.veinte_otra_vez": "Contarlos otra vez",
  /* 17/09, recorrido como clienta: aquí iba «onb.veinte_listo» («Ya está: veinte segundos»), y justo
     debajo `bienvenida_p2` empieza también por «Ya está». Salían los dos seguidos, y `bienvenida_p2`
     además ANTES de contar. Ahora solo sale `bienvenida_p2`, y al acabar los veinte segundos. */
  /* ── Los treinta segundos que miden la voz ────────────────────────── */
  /* 17/09, recorrido como clienta: invitaba a medirse sin decir dónde, y desde el ejercicio no hay botón. */
  "mov.referencia_prestada":
    "El dibujo usa una altura de referencia general, no la tuya. Mide tu voz en «Tu cuenta», con «Medir mi " +
    "voz»: son treinta segundos, y el dibujo se ajusta a ti.",
  "mov.llegaron": "Se entendieron {n} de {total} palabras.",
  "mov.no_te_oimos": "No te escuchamos. No es que lo hayas hecho mal: no llegó ninguna grabación para comparar.",
  /* ── La puerta naranja: hay plan, y hay algo que decir antes ──────── */
  "aviso.titulo": "Tu plan está listo. Antes, algo que tienes que saber.",
  "aviso.seguimiento":
    "Nos dijiste que ya hay un profesional atendiendo tu voz. Lo que esa persona te diga vale más que lo " +
    "que diga esta plataforma, siempre.",
  "aviso.plan_ajustado":
    "Tu plan sale con menos carga: dejamos fuera los ejercicios que más fuerzan la voz. No es que puedas " +
    "menos; es que ahora no es el momento.",
  "aviso.seguir": "Entendido, ver mi plan",
  "onb.tesitura": "Tu voz",
  "onb.tesitura_titulo": "Treinta segundos, y sabemos tu rango de voz.",
  "onb.tesitura_texto":
    "Primero lees una frase en voz alta, como hablas normalmente. Después haces una sirena " +
    "cómoda, sin forzar. Con esas dos cosas sabemos a qué altura hablas tú, y todos los " +
    "ejercicios se ajustan a ella en vez de a un número cualquiera.",
  "onb.tesitura_empezar": "Empezar",
  "onb.tesitura_lectura": "Léela en voz alta, como si se lo contaras a alguien.",
  /*
    LA FRASE QUE SE LEE.
    Corriente a propósito: se mide el tono al que alguien habla SIN pensar en
    ello, y una frase bonita o difícil hace que se lea distinto. Ni una palabra
    de salud, por la misma razón que el resto del producto.
  */
  /* 16/09/2026: decía «Esta mañana he salido temprano y he ido andando hasta la plaza. Hacía
     fresco, pero se estaba bien. He comprado el pan de siempre y he vuelto por el parque.». El
     dueño, sobre esa frase: «revisa ese español, no esta muy bueno». Era castellano de España
     —«he salido», «he ido andando», «se estaba bien»— en una plataforma que habla español neutro,
     y encima es la frase que MÁS se lee de todo el producto. El portugués ya usaba el pasado
     simple («saí cedo»). */
  "onb.tesitura_frase":
    "Esta mañana salí temprano y caminé hasta la plaza. Hacía fresco, pero se sentía bien. " +
    "Compré el pan de siempre y volví por el parque.",
  /* 16/09/2026: decía «Ahora una sirena cómoda: sube y baja con una «u», sin forzar nada.». El
     dueño: «toca una explicación mas corta y directa, como hay un contador la persona se pierde
     toda a la hora de hacer lo que se pide». Se lee con un contador bajando delante. */
  "onb.tesitura_sirena": "Ahora di «uuu» subiendo y bajando, suave.",
  "onb.tesitura_calculando": "Revisando lo que hiciste…",
  /* 17/09, recorrido como clienta: «Hablas alrededor de 208 Hz, y tienes 11 semitonos cómodos» no lo
     entiende un niño de 8 años. Los números se quedan, y cada uno dice qué es. */
  "onb.tesitura_lista":
    "Ya está. Tu voz habla alrededor de {hz} Hz (lo aguda o grave que es) y se mueve cómoda {rango} " +
    "semitonos (un semitono es de una tecla del piano a la de al lado).",
  "onb.tesitura_otra_vez": "Probar otra vez",
  "onb.tesitura_no_guardada": "La medida salió bien, pero no pudimos guardarla. Vuelve a intentarlo cuando quieras.",
  "onb.tesitura_seguir_sin": "Seguir sin medir",
  "onb.tesitura_pocas_muestras": "No te escuchamos lo suficiente. Acerca el micrófono o habla un poco más fuerte.",
  "onb.tesitura_sin_voz_clara": "No se entendió bien el sonido. Casi siempre es ruido de fondo: prueba en un lugar más silencioso.",
  "onb.tesitura_sin_recorrido": "La sirena salió plana. Sube y baja un poco más, siempre sin forzar.",
  "onb.microfono_titulo": "Te vamos a pedir el micrófono. Esto es lo que hacemos con él.",
  "onb.microfono_texto":
    "Se usa solo dentro de tu navegador, para dibujarte lo que estás haciendo mientras lo haces. " +
    "El audio no se sube, no se guarda y no llega a ningún servidor nuestro. Lo único que viaja " +
    "son números: cuántos segundos, cuántos semitonos.",
  "onb.perfil_ayuda": "Con esto decidimos qué ejercicios haces y cuántos.",
  "onb.de": "de",
  // ── Las trece de seguridad · ninguna nombra una dolencia ───────────
  "pregunta.esAdulto": "¿Tienes 18 años o más?",
  "pregunta.esAdulto.ayuda": "Esta plataforma es solo para personas adultas, y lo decimos claro.",
  /* 13/09 (B·76, decisión del dueño): decía «coger»; «coger» suena mal en Latinoamérica. */
  /*
    06/10/2026: esta es la ÚNICA emergencia de la puerta (urgencia «ahora»), así
    que no puede cerrarse a nadie que no la tenga. Antes «te silba el pecho»
    cazaba a todo asmático, que está bien y ya lo recoge la pregunta de la vía
    aérea en naranja. Ahora se acota al ruido NUEVO al tomar aire —estridor, que
    es al inspirar— y a la falta de aire de AHORA; el silbido de siempre o al
    soltar el aire —el del asma— se dice que no cuenta aquí.
  */
  "pregunta.dificultadParaRespirar": "¿Te cuesta respirar ahora mismo, o haces un ruido nuevo al tomar aire?",
  "pregunta.dificultadParaRespirar.ayuda": "Un pitido nuevo al tomar aire, o que te falte el aire ahora. El silbido del pecho de siempre, o al soltar el aire, no cuenta aquí.",
  /*
    ══════════════════════════════════════════════════════════════════════════
    LAS SEIS ACOTACIONES DEL 06/09/2026 · y lo que estaba pasando sin ellas
    ══════════════════════════════════════════════════════════════════════════

    Seis de las trece preguntas no llevaban línea de ayuda, y **las seis dejan
    sin plan** a quien contesta que sí: cuatro derivan a un profesional y dos
    cierran. O sea que un «sí» de más no es una molestia: es alguien que hizo
    trece preguntas y se fue sin nada.

    Y el «sí» de más es fácil de dar:

      · con anginas, todo el mundo dice que sí a «¿te cuesta tragar?»;
      · un ganglio de un catarro es un bulto en el cuello;
      · después de un partido se pierde la voz de golpe.

    A la ronquera se le puso su acotación —«sin un resfriado o una alergia que
    lo explique»— y funcionó. Éstas son las suyas.

    ── CÓMO ESTÁN ESCRITAS, QUE NO ES AL GUSTO ──────────────────────────────

    **Dicen qué NO cuenta**, no qué cuenta. Es lo que frena el sí que no quería
    decir sí, y deja intacto el que sí.

    Y se acota de dos maneras distintas, según lo que de verdad separe los dos
    casos:

      · POR TIEMPO —tres semanas— donde lo que distingue una causa pasajera de
        una que hay que mirar es cuánto dura: tragar, el dolor que llega al
        oído, el bulto. Tres semanas es el mismo listón que ya usa la ronquera
        en este mismo bloque, y se repite a propósito: dos listones distintos
        para lo mismo se contestan mal.
      · POR ALCANCE donde el tiempo no separa nada: sangre al toser lo es a la
        primera, y una operación de tres meses ya trae su plazo dentro. Lo que
        les faltaba no era «desde cuándo» sino «de qué estamos hablando».

    ── LO QUE NO SE HA TOCADO ───────────────────────────────────────────────

    **Ninguna regla de `puerta.ts`.** Los seis desenlaces siguen siendo los que
    eran. Lo único que cambia es que la persona entiende qué le están
    preguntando antes de contestar.

    Y van a revisión: el dueño dijo el 06/09 que en producción una
    fonoaudióloga valida el catálogo entero. Éstas son las primeras seis líneas
    que tiene que leer, y la de tragar es la que más — hay guías que derivan a
    la primera y no a las tres semanas.
  */
  "pregunta.sangreAlToser": "¿Has visto sangre al toser o en la flema?",
  "pregunta.sangreAlToser.ayuda": "Sangre en lo que sale de la garganta o del pecho. La de la nariz o la de las encías no cuenta.",
  "pregunta.reposoVocalIndicado": "¿Un profesional te indicó que no uses la voz en estos días?",
  "pregunta.reposoVocalIndicado.ayuda": "Reposo vocal indicado por un profesional, en este momento.",
  /*
    DECÍA «de la laringe o de las cuerdas», y es UNA PREGUNTA DE SEGURIDAD: la
    contesta alguien que a lo mejor no sabe cómo se llama lo que le operaron.
    Ahora dice dónde, que es lo que esa persona sí sabe. Cierra la puerta
    igual y la entiende más gente, que en una pregunta que cierra la puerta es
    justamente lo que hay que maximizar.
  */
  "pregunta.cirugiaReciente": "¿Te han operado de la garganta o de las cuerdas en los últimos tres meses?",
  "pregunta.cirugiaReciente.ayuda": "De la garganta, de las cuerdas, del cuello o de la tiroides. De las muelas o de la nariz, no.",
  "pregunta.roncoMasDeTresSemanas": "¿Llevas más de tres semanas con la voz cambiada?",
  "pregunta.roncoMasDeTresSemanas.ayuda": "Sin un resfriado o una alergia que lo explique.",
  "pregunta.dificultadAlTragar": "¿Te cuesta tragar, o notas algo en la garganta que no se va?",
  "pregunta.dificultadAlTragar.ayuda": "Más de tres semanas, y sin un catarro ni unas anginas que lo expliquen.",
  "pregunta.dolorIrradiadoAlOido": "¿Te duele al hablar o al tragar, y el dolor llega hasta el oído?",
  "pregunta.dolorIrradiadoAlOido.ayuda": "Más de tres semanas, y sin anginas ni una infección de oído que lo expliquen.",
  "pregunta.bultoEnElCuello": "¿Te has notado un bulto en el cuello?",
  "pregunta.bultoEnElCuello.ayuda": "Uno que sigue ahí después de tres semanas. Los que salen con un catarro y bajan solos no cuentan.",
  "pregunta.perdidaRepentinaDeVoz": "¿Has perdido la voz de golpe y del todo?",
  "pregunta.perdidaRepentinaDeVoz.ayuda": "Y que siga así. Si volvió sola en un día o dos y no ha vuelto a pasar, no cuenta.",
  "pregunta.enSeguimientoProfesional": "¿Ahora mismo te está atendiendo un fonoaudiólogo o un otorrino?",
  "pregunta.enSeguimientoProfesional.ayuda": "No es un problema: cambia cómo montamos tu plan.",
  "pregunta.diagnosticoNeurologico": "¿Algún médico te está atendiendo por algo neurológico o de la tiroides?",
  /* La ayuda de la pregunta 12 NOMBRA condiciones, a propósito (10/09): son
     ejemplos para que cada uno reconozca si le toca, y de la respuesta sale la
     puerta que cierra lo que no es para esa persona. El dueño: «si esa
     pregunta es necesaria, es necesaria». `verificar:dolencias` la resta con
     ese motivo en los tres idiomas, y es la única excepción de la regla. */
  "pregunta.diagnosticoNeurologico.ayuda":
    "Párkinson, derrame cerebral (ACV), ELA, hipotiroidismo. Tampoco es un problema: con eso ajustamos tu " +
    "plan.",
  "pregunta.viaAerea":
    "¿Usas inhalador, tienes alergia estos días, te sube ácido al pecho o duermes con una máquina para " +
    "respirar?",
  "pregunta.viaAerea.ayuda": "Cualquiera de las cuatro. Cambia qué ejercicios te ofrecemos.",
  // ── Los siete perfiles ────────────────────────────────────────────
  "perfil.A1_carga_sostenida": "Hablo todo el día",
  "perfil.A1_carga_sostenida.sub": "Clases, atención al público, salud, guías turísticos",
  "perfil.A2_rendimiento": "Canto, actúo o hago locución",
  "perfil.A2_rendimiento.sub": "La voz es mi instrumento de trabajo",
  "perfil.A3_voz_conectada": "Reuniones y micrófono",
  "perfil.A3_voz_conectada.sub": "Videollamadas, pódcast, transmisiones en vivo, clases en línea",
  "perfil.A4_post_clinico": "Ya me revisaron la voz",
  "perfil.A4_post_clinico.sub": "Me dieron de alta en fonoaudiología, o cuido la voz después de una operación",
  "perfil.A5_voz_que_envejece": "Mi voz perdió fuerza",
  "perfil.A5_voz_que_envejece.sub": "Con los años, y ya me la revisaron",
  "perfil.A6_exploracion": "Quiero que mi voz suene de otra manera",
  "perfil.A6_exploracion.sub": "Explorar cómo suena y cómo me representa",
  "perfil.A7_tension": "Noto que aprieto",
  "perfil.A7_tension.sub": "Nudo en la garganta, tensión, miedo escénico",
  // ── La portada de la aplicación ───────────────────────────────────
  "portada.etiqueta": "Voz hablada · para adultos",
  /*
    16/09/2026: decía «Tu voz se entrena como se entrena cualquier otra cosa del cuerpo.». El
    dueño, leyéndolo como cliente: «En serio? cualquier otra cosa del cuerpo […] no hace sentido a
    nivel de idioma, es ofensivo, es depreciativo». Y tiene razón: «cosa del cuerpo» reduce la voz
    de alguien a un trasto, y la frase repite «se entrena» dos veces para no decir nada más. La
    página de venta ya lo dice bien y él lo aprobó: «La voz también se entrena.».
  */
  "portada.titulo": "Tu voz también se entrena.",
  "portada.cuerpo":
    "Te hacemos unas preguntas y, de {n} ejercicios, elegimos los tuyos: entre {min} y {max}. Con cuánto " +
    "de cada uno, de dónde sale ese número y cuándo parar.",
  "portada.ver_ejercicios": "Ver los ejercicios",
  // ── La aplicación, lo que quedaba escrito a mano ──────────────────
  "lib.titulo": "La biblioteca",
  "lib.cuerpo":
    "Todos los ejercicios, aunque no estén en tu plan. Tu plan salió de lo que contestaste; aquí está " +
    "todo lo demás.",
  "lib.sin_revisar_corto": "Dibujos sin revisar todavía",
  "lib.sin_carga": "no pide nada a la voz",
  /*
    LOS TRES NIVELES, ESCRITOS. No `"carga {c}"` con el identificador dentro.

    Era una plantilla rellenada con `e.carga`, que es un valor interno en
    castellano, así que la biblioteca en inglés decía «baja load», «media
    load» y «alta load» —139 de las 196 fichas— y la portuguesa «carga baja»
    y «carga media», que no son palabras portuguesas.

    Es la cicatriz nº 35 otra vez: un identificador interno impreso en la
    pantalla de un producto de salud. Con una clave por nivel el compilador
    exige el texto de un nivel nuevo antes de que exista la pantalla.
  */
  "lib.carga_baja": "carga baja",
  "lib.carga_media": "carga media",
  "lib.carga_alta": "carga alta",

  /*
    EL BUSCADOR DE LA BIBLIOTECA.

    Eran 196 tarjetas seguidas, sin buscador y sin familias. Ver
    `apps/web/src/Biblioteca.tsx`.
  */
  "lib.buscar": "Buscar un ejercicio",
  "lib.familia": "Familia",
  "lib.todas_las_familias": "Todas las familias",
  "lib.filtro_sin_carga": "No piden nada a la voz",
  "lib.filtro_calladas": "Sin que nadie te oiga",
  "lib.limpiar": "Quitar los filtros",
  "lib.cuantas": "{n} de {total}",
  "lib.nada": "No encontramos ninguno con esos filtros. Prueba con menos filtros o con otra palabra.",

  /* Lo de hoy, cuando no es lo publicado. Ver `PantallaDelMovimiento`. */
  "mov.hoy_ajustado":
    "Hoy haces menos de lo que dice el ejercicio ({base}), por el peldaño en el que estás. Sube por sí " +
    "solo, con las sesiones.",
  "ia.aviso":
    "El asistente solo habla de lo que está en el catálogo, y siempre dice de dónde sale. No opina sobre " +
    "cómo suena tu voz: no puede escucharte, y ese no es su trabajo.",
  /*
    ══════════════════════════════════════════════════════════════════════
    LO QUE HACE FALTA TENER A MANO, Y LO QUE EL NÚMERO NO DICE
    ══════════════════════════════════════════════════════════════════════

    Los dos textos salen del encargo del dueño del 06/09, y de la condición que
    puso, que vale más que el sí:

        «que sea algo que el usuario diga "y a mí qué me importa"… textos que
         no sean imprescindibles, solo contaminan la UI y empeoran el UX»

    Así que ninguno de los dos es un párrafo de cortesía:

      `mov.necesitas`   son quince fichas que piden un tubito, un tubo o un
                        vaso de agua, y hasta hoy no lo decía ninguna pantalla.
                        Es lo más accionable que hay aquí, y llega ANTES de
                        empezar: enterarse a mitad de un ejercicio de que hacía
                        falta un tubito es peor que no haberlo empezado.

      `mov.que_mide`    es el titular de un desplegable, y solo sale cuando hay
                        un NÚMERO en pantalla. Contesta la pregunta que la
                        persona sí se hace al verlo —«¿esto qué me está
                        diciendo?»— en una línea, y el resto está a un toque.
                        Los ochenta y cinco textos largos siguen escritos ficha
                        por ficha; lo que no van a hacer es plantarse debajo de
                        cada medición como un párrafo de cuarenta palabras.
  */
  "mov.necesitas": "Para esto necesitas",
  "mov.necesitas_espejo": "Un espejo.",
  "mov.necesitas_silencio": "Un sitio en silencio.",
  /* ═══════════════════════════════════════════════════════════════════════
     EL GUION DE LA ESCUCHA, PALABRA POR PALABRA
     ═══════════════════════════════════════════════════════════════════════

     Seis fichas mandan grabarse y escucharse después. Tres de ellas se
     escribieron el 06/09, al quitar de las animaciones los pasos que exigían
     una segunda persona: la salida buena era grabarse, y lo sigue siendo.

     Pero devolverle a alguien su propia voz no es neutro. Jogia, Thomas,
     Barbato y Bentall (2024), PMID 39169467: el disgusto por la propia voz
     grabada se asocia a ansiedad social, **y el efecto es mayor en la lengua
     materna** — que es justo en la que se va a usar esto.

     `06 §F13` lo resuelve con un guion obligatorio antes de reproducir nada, y
     dice que no se toca. Aquí está, en los tres idiomas. Lo que hace no es
     tranquilizar: es CAMBIAR LA PREGUNTA. La persona no viene a juzgar si su
     voz le gusta, viene a ver en cuál de las dos se entiende mejor.
     ═══════════════════════════════════════════════════════════════════════ */
  /*
    EL AUDIO DEL EJERCICIO, Y POR QUÉ SON DOS COSAS Y NO UNA.

    · «Así suena» es el MODELO SONORO: cómo suena el ejercicio cuando sale
      bien. Solo lo tienen los que se oyen, y es lo que un paso a paso
      escrito no puede enseñar — «una /s/ fina y pareja» solo significa algo
      si ya la has oído.
    · «Que te lo cuenten» es la LOCUCIÓN: la voz leyendo el paso a paso, para
      poder hacer el ejercicio sin mirar la pantalla. La llevan todas.

    Los dos botones dicen lo que va a pasar al pulsarlos, y al lado va escrito
    lo que se va a oír: quien no puede oírlo tiene que poder leerlo, y quien
    sí puede tiene que saber qué le espera antes de darle.
  */
  "mov.asi_suena": "Así suena",
  "mov.escuchar": "Escuchar",
  "mov.parar_de_escuchar": "Parar",
  "mov.que_te_lo_cuenten": "Que te lo cuenten",
  "mov.que_te_lo_cuenten_pie":
    "La voz te lee los pasos, para que puedas hacerlo sin mirar la pantalla.",
  "mov.antes_de_escucharte": "Antes de escucharte",
  "mov.guion_de_la_escucha":
    "Tu voz grabada te va a sonar rara. Le pasa a casi todo el mundo: por dentro te oyes " +
    "también por los huesos, y la grabación no. Esto no es un concurso de a quién le gusta " +
    "más su voz. Escucha solo una cosa: ¿en cuál de las dos se entienden mejor las palabras?",
  "mov.que_mide": "Qué mide esto, y qué no",
  "mov.por_dentro": "Qué pasa por dentro",
  /* Va ENCIMA del paso a paso (07/09/2026). La lista entera sigue abajo; esto
     es solo lo que pasa en este ejercicio y hay que saber antes de empezarlo. */
  "mov.en_este_para_si": "En este, detente si…",
  "mov.como_se_hace": "Cómo se hace",
  "mov.lo_que_te_dices": "Lo que te dices mientras lo haces",
  "mov.es_imagen": "Es una imagen, no anatomía",
  "mov.en_que_se_falla": "En qué se suele fallar",
  "mov.como_lo_notas": "Cómo lo notas",
  "mov.como_se_arregla": "Cómo se arregla",
  "mov.para_y_consulta": "Detente y consúltalo si…",
  "mov.que_salio": "Qué salió al medirlo",
  "mov.a_favor": "A favor",
  "mov.en_contra": "En contra",
  "mov.quien_lo_propone": "Quién lo propone",
  /* La cita, plegada. Lo pidio el dueno: «oculto, pero con una flechita». */
  "mov.ver_la_fuente": "Ver la fuente",
  "mov.ver_las_fuentes": "Ver las fuentes",
  "mov.sin_atribucion":
    "No hemos encontrado quién lo describe así. Es un gesto que enseña mucha gente y no hemos " +
    "localizado una fuente que lo firme. Lo incluimos igual, y lo decimos.",
  "mov.solo_manual":
    "Lo describe el manual del que nace este proyecto, y nadie más que hayamos encontrado.",
  "mov.si_no_te_sale": "Si no te sale",
  "mov.tope": "Tope: {min} min, y ahí corta el temporizador. Más no es mejor.",
  /*
    LA LEYENDA DE LA MARCA, y hace falta porque una letra suelta no se explica
    sola. No dice qué significa cada una —eso lo dice la marca al tocarla— sino
    para qué están, que es lo que nadie podía adivinar.
  */
  "mov.que_es_la_marca": "Las letras que están junto a cada número dicen de dónde sale ese número. Tócalas y te lo explicamos.",
  "sesion.sin_plan_titulo": "Todavía no tienes plan.",
  /*
    Y EL CASO DE LOS DOS APARATOS (cuestionario del hermano, P8 · L175).

    Las respuestas viajan con una cookie de ese navegador y se unen a la cuenta
    al registrarse. Quien contesta en el móvil y se registra desde el ordenador
    llega aquí habiéndolo contestado todo. El dueño, el 10/09: «una vez la
    cliente ya hizo el cuestionario y ya le montamos un plan, es eso, no le
    mostramos de nuevo el cuestionario, solo si ella así lo decide». Así que
    nada la manda a las preguntas: se le dice por qué no hay plan, y el botón
    de debajo es suyo.
  */
  "sesion.sin_plan_texto":
    "El plan se monta después de las preguntas. Son {n} y no se pueden saltar: hay cosas que no se " +
    "practican en casa y hay que saberlo antes de montarte nada. Si ya las contestaste en otro " +
    "aparato, esas respuestas se quedaron allí y a esta cuenta no llegan.",
  "escena.alt":
    "Gráfico de la curva de tono del ejercicio. Las instrucciones completas están escritas debajo, " +
    "en el paso a paso.",
  "sello.sin_revisar":
    "Los dibujos y el video de este ejercicio todavía no los ha revisado un profesional. Lo decimos aquí " +
    "porque nos parece que hay que decirlo.",
  "sello.revisado": "Dibujos y video revisados por {quien}.",
  "mic.sin_https":
    "Sin conexión segura no se puede usar el micrófono. Es una regla del navegador, no nuestra.",
  "mic.denegado":
    "No tenemos permiso para usar el micrófono. Puedes hacer los ejercicios igual: lo único que pierdes " +
    "es verte mientras los haces.",
  /*
    16/09/2026: decía «Altura relativa a tu voz · el nivel es relativo a esta sala y a este
    momento», y en mayúsculas de etiqueta (`vc-etiqueta`) justo debajo del dibujo. El dueño, sobre
    la pantalla que mide la voz: «ese texto confunde con el texto de arriba, la persona no sabe muy
    bien cual leer» y «ese tiene mas destaque que el que deberia ser leido». Pintado más fuerte que
    la frase que hay que leer en voz alta, competía con ella. Ahora es una línea de ayuda, corta, y
    dice para qué sirve el dibujo en vez de explicar dos relatividades.
  */
  "mic.escuchando": "Esto es solo para que te veas mientras hablas. No es una calificación.",
  /* 16/09/2026: el visualizador apagado era un rectángulo gris vacío en tres pantallas (el micrófono del
     onboarding, la medida de la voz y la ficha del ejercicio), sin una pista de qué era. Ahora dice qué
     va a pasar ahí. */
  "mic.en_reposo": "Aquí verás tu voz mientras hablas: más arriba cuanto más aguda.",
  "mic.pidiendo": "Pidiendo el micrófono al navegador…",
  "temp.se_acabo": "Se acabó.",
  "temp.tope_duro": "Este ejercicio tiene un tope de {t} y no se puede alargar.",
  "temp.al_medirlo": "Al medirlo, pasado ese punto subía la tensión y bajaba la calidad de la voz.",
  /* 17/09, recorrido como clienta: «Hiciste lo de hoy» salía al acabar CADA ejercicio, y en el primero
     de nueve parecía que se había terminado la sesión. */
  "temp.lo_de_hoy": "Hiciste lo de hoy de este ejercicio.",
  "temp.parado":
    "Parado. Queda anotado lo que hiciste, y ya está: parar cuando lo necesitas es parte del ejercicio, " +
    "no un error.",
  "temp.parar": "Parar",
  "temp.otra_vez": "Empezar otra vez",
  /* ── LA LÁMINA ANATÓMICA ──────────────────────────────────────────
     Los nombres de las partes. Van AQUÍ y no dentro del dibujo: un SVG con
     letras dentro habría que mantenerlo tres veces, no lo lee un lector de
     pantalla en orden, y una etiqueta portuguesa se sale de su hueco.

     Y son nombres LLANOS, no latín. Quien lee esto está aprendiendo dónde
     tiene la lengua, no estudiando anatomía: «el velo del paladar» enseña
     algo y «velum palatinum» no enseña nada. */
  /*
    ESTAS ONCE CLAVES SUSTITUYEN A LAS DEL CORTE SAGITAL, Y NO ES UN CAMBIO DE
    REDACCIÓN: ES OTRO DIBUJO Y OTROS SITIOS.

    Las viejas decían «el velo del paladar», «la faringe», «la laringe», «las
    cuerdas vocales» y «el diafragma». Pasaban los verificadores porque
    `comprobar-jerga.ts` **excluye a propósito las claves `lamina.*`** — cuando
    se escribió, nombrar las partes era el trabajo de una lámina.

    El dueño decidió el 05/09 rehacer la pieza: «se queda la lámina, pero
    dibujando solo lo que se ve en un espejo o se nota con la mano».

    Cada una de éstas dice DÓNDE está y CÓMO se encuentra, porque un nombre que
    no se puede localizar no sirve de nada. El porqué entero está en la
    cabecera de `packages/ui/src/Lamina.tsx`.
  */
  "lamina.donde.titulo": "Los lugares donde tú puedes sentirlo",
  "lamina.donde.nariz": "La nariz. Tápala con dos dedos mientras haces el sonido: si cambia, algo estaba saliendo por ahí",
  "lamina.donde.labios": "Los labios",
  "lamina.donde.dientes": "Los dientes de arriba",
  "lamina.donde.reborde":
    "El reborde de detrás de los dientes de arriba. Tócalo con la punta de la lengua",
  "lamina.donde.fondo": "El fondo de la boca. Míralo en un espejo mientras bostezas: se ensancha",
  "lamina.donde.lengua": "La lengua",
  "lamina.donde.bultito": "El bultito del cuello que sube y baja cuando tragas",
  "lamina.donde.cuello":
    "La parte de delante del cuello. Pon dos dedos ahí: tiembla cuando hay voz y no tiembla cuando solo hay aire",
  "lamina.donde.pecho":
    "El centro del pecho. Con la mano abierta encima y un «mmm» grave, cosquillea",
  "lamina.donde.aire": "Por aquí SALE el aire. Pon la palma a unos diez centímetros y lo sientes",
  /* ── Lámina 2 · la mandíbula ─────────────────────────────────────── */
  "lamina.mandibula.titulo": "La mandíbula y su bisagra, vistas de frente",
  "lamina.mandibula.bisagra": "La bisagra. Pon los dedos ahí y abre: se mueve justo delante de la entrada del oído",
  "lamina.mandibula.masetero":
    "El músculo que se abulta en la mejilla cuando aprietas los dientes",
  "lamina.mandibula.arcada": "El arco de los dientes, la herradura entera. Es una sola pieza con dos bisagras",
  "lamina.mandibula.pomulo": "El pómulo. Ese músculo arranca justo aquí debajo",
  "lamina.mandibula.caida": "La mandíbula CAE por su peso, girando sobre las bisagras",
  "lamina.mandibula.adelantar":
    "Adelantar la barbilla no es soltar: es apretar por otro sitio",
  /* ── Lámina 3 · el cuello y los hombros ──────────────────────────── */
  "lamina.cuello.titulo": "El cuello y los hombros, vistos de frente",
  "lamina.cuello.hioides":
    "Un hueso pequeño que flota: es el único que no toca ningún otro. De él cuelga la lengua por " +
    "arriba, y por abajo lo que sube cuando tragas",
  "lamina.cuello.esternocleido":
    "Los dos músculos que van de detrás de la oreja al hueso plano del pecho. Se marcan al girar la cabeza",
  "lamina.cuello.trapecio": "El músculo que va del cuello al hombro",
  "lamina.cuello.laringe": "El bultito que sube y baja cuando tragas, colgando de ese hueso",
  "lamina.cuello.hombro": "La clavícula y el hombro",
  "lamina.cuello.caen": "Los hombros CAEN. El gesto es soltar, nunca encoger",
  "lamina.cuello.sujetar":
    "Con la cabeza adelantada, estos músculos tienen que sostenerla. Y son los mismos que conviene tener " +
    "sueltos al hablar",
  /* ── Lámina 4 · la lengua ────────────────────────────────────────── */
  "lamina.lengua.titulo": "La lengua entera, vista de lado y por dentro",
  "lamina.lengua.punta": "La punta. Es la que vibra en la erre y la que hace la ele y la ene",
  "lamina.lengua.alveolos":
    "El reborde que hay JUSTO DETRÁS de los dientes de arriba. El reborde, no los dientes",
  "lamina.lengua.dorso": "La parte de en medio. Es la que sube contra el techo de la boca en la eñe",
  "lamina.lengua.raiz": "La raíz. Son dos tercios de la lengua, no se ven en el espejo, y llegan hasta ese hueso",
  "lamina.lengua.suelo": "El suelo de la boca, debajo. Es lo que se afloja al soltar la mandíbula",
  "lamina.lengua.hioides": "El hueso que flota, del que cuelga la raíz",
  "lamina.lengua.adelante": "Al llevar el sonido adelante, la lengua SE ADELANTA",
  "lamina.lengua.retraer":
    "La base tira hacia atrás para ayudar al cierre. Es un apoyo que casi nunca hace falta",
  /* ── Lámina 5 · la caja y el aire ────────────────────────────────── */
  "lamina.caja.titulo": "La caja y el aire, vistos de frente",
  "lamina.caja.subglotica": "Aquí está la presión que importa: justo DEBAJO de las cuerdas vocales, en lo más alto del tubo",
  "lamina.caja.traquea": "La tráquea, el tubo que baja al pecho",
  "lamina.caja.costillas": "Las costillas. La caja se abre por los dos lados a la vez",
  "lamina.caja.diafragma":
    "La cúpula que hay debajo de las costillas. Es una CÚPULA, no una faja: en reposo está arriba y abombada",
  "lamina.caja.tripa": "La pared de la barriga. Lo que se mueve ahí no es el aire",
  "lamina.caja.abren": "Las costillas ABREN hacia los lados, no hacia delante",
  /* 13/09 (B·76, decisión del dueño): decía «coger»; «coger» suena mal en Latinoamérica. */
  "lamina.caja.baja": "Al tomar aire, la cúpula BAJA y empuja lo de debajo",
  "lamina.caja.sale": "Y el aire SALE hacia arriba",
  "lamina.caja.empujar": "Empujar con la barriga no sube la presión de arriba: esa fuerza se va en apretar",
  /* ── Lámina 6 · los labios ───────────────────────────────────────── */
  "lamina.labios.titulo": "Los labios y la salida, vistos de frente y de cerca",
  "lamina.labios.cierre": "El cierre entre los dos labios: la eme, la be y la pe",
  "lamina.labios.labio_diente": "Labio de abajo contra dientes de arriba: la efe y la uve",
  "lamina.labios.frunce": "Fruncidos, la salida se estrecha y se ajusta sin tocar nada con nada",
  "lamina.labios.comisuras": "Las comisuras. Por aquí se escapa el aire si no cierran",
  "lamina.labios.mejilla": "La mejilla, la pared de los lados. Ni hinchada ni apretada",
  "lamina.labios.cigomaticos": "Los que tiran de la comisura hacia arriba y atrás",
  "lamina.labios.salida": "El aire sale POR EL MEDIO, y esa es toda la idea",
  "lamina.labios.escape": "Si se escapa por una comisura no hay presión de vuelta, y el ejercicio ya no sirve",
  // ── El planner ────────────────────────────────────────────────────
  "planner.etiqueta": "Tu plan",
  "planner.titulo": "Lo que viene, y por qué.",
  "planner.peldano_es": "Estás en el peldaño {n}.",
  /* 13/09: «Estás en el peldaño −2» era lo primero que leía quien habla todo el
     día, sin saber qué es un peldaño. Una línea, antes del desglose. */
  "planner.que_es_el_peldano":
    "El peldaño es cuánto te pide cada ejercicio: en el 0 haces lo que dice el ejercicio, y por debajo de " +
    "0 te pide menos. No es una calificación.",
  "planner.por_progreso": "por lo practicado — {n} sesión | por lo practicado — {n} sesiones",
  "planner.por_carga": "por tu carga de voz — {h}",
  "planner.por_que_resta":
    "Sí: hablar muchas horas RESTA. Quien habla todo el día ya usó su voz todo lo que podía usarla hoy, y " +
    "pedirle más es justo lo que la cansa. No te damos menos por hacer menos: tu día ya cuenta como " +
    "trabajo.",
  "planner.falta_para_subir": "Sube un peldaño en la próxima sesión. | Sube un peldaño dentro de {n} sesiones.",
  "planner.en_el_techo": "Ya estás en lo más alto. De aquí en adelante cambia la variedad, no la cantidad.",
  "planner.lo_que_viene": "Las próximas seis",
  "planner.sin_fechas":
    "No hay fechas. Avanza por sesiones, así que si un día no puedes, no pierdes nada.",
  "planner.sesion_n": "Sesión {n}",
  "planner.forma": "{min} min · {n} ejercicio | {min} min · {n} ejercicios",
  "planner.descanso": "Descanso",
  "planner.sube_aqui": "Aquí sube el peldaño",
  "planner.hacer_hoy": "Hacer esta sesión",
  /* 13/09, recorrido como clienta activa: los 8-15 no se nombraban en ninguna
     pantalla, y cambiar el rato obligaba a rehacer el onboarding y perder lo hecho. */
  "planner.que_trae": "Qué trae",
  "planner.tus_ejercicios": "Tu ejercicio | Tus {n} ejercicios",
  "planner.tus_ejercicios_ayuda":
    "Los que te van a salir en las doce sesiones siguientes, y cuántas veces. Cada uno lo tienes " +
    "entero en la biblioteca.",
  // 16/09: sin acceso la biblioteca no se abre (el dueño, en `apps/api/src/muro.ts`), y la frase de
  // arriba prometía algo que ese plan no puede dar. Sin acceso se dice sin la biblioteca.
  "planner.tus_ejercicios_ayuda_sin_acceso":
    "Los que te van a salir en las doce sesiones siguientes, y cuántas veces.",
  "planner.veces": "{n} vez | {n} veces",
  "planner.ajustar_titulo": "Si cambió tu día",
  "planner.ajustar_ayuda":
    "Cambia el rato o las horas de voz y el plan se rehace desde la próxima sesión. Lo que ya has " +
    "hecho se queda, y sigues en tu peldaño.",
  "planner.ajustado": "Listo. La próxima sesión ya sale así.",
  "planner.sin_racha": "Aquí no hay rachas que romper ni porcentajes que completar. No los vas a encontrar, y es a propósito.",
  "app.planner": "Mi plan",
  // ── Las dos últimas del onboarding ────────────────────────────────
  "onb.horas_titulo": "¿Cuántas horas al día usas la voz?",
  "onb.horas_ayuda": "Cuenta el trabajo y todo lo demás. Es la pregunta que más cambia tu plan.",
  "horas.poco": "Menos de dos",
  "horas.poco.sub": "Alguna llamada y poco más",
  "horas.algo": "Entre dos y cuatro",
  "horas.algo.sub": "Reuniones casi todos los días",
  "horas.bastante": "Entre cuatro y seis",
  "horas.bastante.sub": "Media jornada hablando",
  "horas.muchas": "Más de seis",
  "horas.muchas.sub": "Clases, atención al público, guías: el día entero",
  "onb.rato_titulo": "¿Cuánto tiempo tienes al día?",
  "onb.rato_ayuda":
    "Si dudas, elige el más corto. Es un límite: el temporizador corta ahí y no pregunta si quieres un " +
    "minuto más.",
  "rato.bolsillo": "Tres minutos",
  "rato.bolsillo.sub": "En el carro, antes de entrar",
  "rato.corta": "Siete minutos",
  "rato.corta.sub": "Un hueco entre dos tareas",
  "rato.media": "Quince minutos",
  "rato.media.sub": "Lo que hace la mayoría",
  "rato.larga": "Veinticinco minutos",
  "rato.larga.sub": "Con tiempo y sin prisa",
  // ── El cuadro de la persona, los motivos y los compromisos (17/09) ─────────────────────────────────────────
  "onb.senales_titulo": "¿Qué notas en tu voz?",
  "onb.senales_ayuda": "Marca todo lo que te pase. Con esto elegimos qué ejercicios van primero.",
  "senal.cansancio": "Se me cansa al final del día",
  "senal.ronquera": "Ronquera o carraspeo",
  "senal.aire": "Me quedo sin aire al hablar",
  "senal.volumen": "No me oyen",
  "senal.tension": "Aprieto, o me cuesta hablar",
  "senal.quiebres": "Se me quiebra, o no sube",
  "senal.claridad": "No me entienden",
  "senal.nervios": "Nervios al hablar en público",
  "senal.ninguna": "Nada de esto",
  "onb.objetivo_titulo": "¿Qué quieres conseguir?",
  "onb.objetivo_ayuda": "Una sola. Es la que más pesa al armar tu plan.",
  "objetivo.aguantar": "Aguantar el día",
  "objetivo.proyectar": "Que me oigan sin esfuerzo",
  "objetivo.claro": "Hablar más claro",
  "objetivo.soltar": "Soltar la tensión",
  "objetivo.rango": "Más rango o agilidad",
  "objetivo.mantener": "Mantener lo ganado",
  "objetivo.explorar": "Explorar cómo suena",
  "onb.donde_titulo": "¿Dónde vas a practicar casi siempre?",
  "onb.donde_ayuda": "Lo habitual. Cada día puedes marcar «hoy no puedo hacer ruido».",
  "donde.casa": "En casa, a solas",
  "donde.gente_cerca": "Con gente cerca",
  "donde.gente_cerca.sub": "Oficina, familia, pared fina",
  "donde.carro": "En el carro",
  "donde.cambia": "Cambia cada día",
  "onb.experiencia_titulo": "¿Has hecho ejercicios de voz antes?",
  "onb.experiencia_ayuda": "Sirve para no darte al principio lo que pide oído o técnica.",
  "experiencia.nunca": "Nunca",
  "experiencia.algo": "Algo, por mi cuenta",
  "experiencia.profesional": "Con un profesional, o canto",
  "onb.tubito_titulo": "¿Tienes a mano un tubito ancho?",
  "onb.tubito_ayuda": "Una pajilla de las de batido. Quince ejercicios lo usan; sin él, tu plan los deja fuera y te lo dice.",
  "onb.tubito_si": "Sí, tengo",
  "onb.tubito_no": "No tengo",
  "motivo.rotacion": "Para variar",
  /* 17/09, recorrido como clienta: «Para variar» salía en la preparación que toca las doce sesiones
     y en ocho de los nueve ejercicios del día de voz cansada, donde no explica nada. Cuando la ficha
     entra solo por rotación, se dice para qué está en ESE sitio. Y «se te cansa» sin decir qué. */
  "motivo.etapa.preparacion": "Para empezar",
  "motivo.etapa.calentamiento": "Para calentar la voz",
  "motivo.etapa.enfriamiento": "Para cerrar la sesión",
  "motivo.sin_voz": "No le pide nada a tu voz",
  "motivo.senal.cansancio": "Porque la voz se te cansa al final del día",
  "motivo.senal.ronquera": "Por la ronquera o el carraspeo",
  "motivo.senal.aire": "Porque te quedas sin aire",
  "motivo.senal.volumen": "Porque no te oyen",
  "motivo.senal.tension": "Porque notas que aprietas",
  "motivo.senal.quiebres": "Porque la voz se te quiebra o no sube",
  "motivo.senal.claridad": "Porque no te entienden",
  "motivo.senal.nervios": "Por los nervios al hablar en público",
  "motivo.objetivo.aguantar": "Para aguantar el día",
  "motivo.objetivo.proyectar": "Para que te oigan sin esfuerzo",
  "motivo.objetivo.claro": "Para hablar más claro",
  "motivo.objetivo.soltar": "Para soltar la tensión",
  "motivo.objetivo.rango": "Para ganar rango y agilidad",
  "motivo.objetivo.mantener": "Para mantener lo ganado",
  "motivo.objetivo.explorar": "Para explorar cómo suena",
  "motivo.donde.gente_cerca": "Porque practicas con gente cerca",
  "motivo.donde.carro": "Porque practicas en el carro",
  "compromiso.sin_tubito": "Con un tubito ancho entrarían {n} ejercicios más. Lo cambias en «Mi plan».",
  "compromiso.techo": "{n} ejercicios más entran desde la sesión {s}: piden oído o técnica, y llegan cuando toca.",
  "planner.tubito": "¿Tienes tubito ancho?",
  "planner.donde": "¿Dónde practicas casi siempre?",
  // ── Lo que iba pegado a una llave y por eso no se veía ────────────
  "sesion.numero": "sesión {n}",
  "sesion.minutos": "{n} minuto | {n} minutos",
  "sesion.voz_cansada_hoy": "Hoy tengo la voz cansada",
  "sesion.sin_ruido_hoy": "Hoy no puedo hacer ruido",
  "sesion.sin_ruido_explicado":
    "Quitamos los ejercicios que se oyen desde lejos y ponemos primero los que no se oyen. El plan sigue " +
    "completo: no se cancela nada.",
  "sesion.min": "{n} min",
  "sesion.hechos_hoy": "{n} de {total} hechos hoy",
  /* 17/09, recorrido como clienta: con siete de nueve hechos, «Empezar» volvía al primero, que ya
     estaba hecho. Con alguno hecho, el botón dice «Seguir» y lleva al primero que falta. */
  "sesion.seguir": "Seguir",
  /*
    EL CIERRE DE LA SESIÓN, y no lleva racha, ni porcentaje, ni felicitación.

    Dice tres cosas y las tres son ciertas: que ya está, que queda apuntado, y
    que hoy no se hace otra. Lo último es la regla de la casa —«más no es
    mejor»—, la misma que el servidor contesta mañana si se vuelve demasiado
    pronto, dicha aquí con sus palabras para que no parezcan dos reglas.
  */
  "sesion.terminada_titulo": "Ya está. Sesión hecha.",
  "sesion.terminada_texto":
    "Queda apuntada, y con ella avanzas hacia el peldaño siguiente. Hoy no hagas otra: dos " +
    "seguidas no suman, se estorban.",
  "sesion.hoy_descanso": "Hoy, descanso.",
  "sesion.me_equivoque": "Me equivoqué, mi voz está bien",

  /*
    LO QUE SI SE PUEDE HACER EL DIA QUE LA VOZ ESTA MAL.

    La pagina de venta lo promete —«57 no le piden nada a tu voz, las puedes
    hacer el dia que la tienes mal»— y la pantalla del descanso no ofrecia
    nada. Ver el motor y `Sesion.tsx`.
  */
  "sesion.hoy_si_puedes": "Y si tienes ganas, esto sí",
  "sesion.hoy_si_puedes_ayuda":
    "{n} ejercicios que no le piden nada a tu voz: respirar, soltar el cuello, la postura. No cuentan " +
    "como sesión y no cambian tu peldaño.",
  "sesion.cortado_por_el_tope": "cortado por el tope",
  /* Escrito, y no solo atenuado: el gris no lo lee un lector de pantalla ni
     lo distingue quien ve poco (cuestionario del hermano, P42). */
  "sesion.hecho": "hecho",
  "sesion.silencioso": "silencioso",
  /*
    13/09, caminando la sesión como cliente: «Parar» a los dos segundos en cada
    ejercicio daba la sesión por hecha y subía el peldaño. Estas cuatro son lo
    que se dice ahora. Sin castigo y sin deuda, como el resto: lo hecho queda
    apuntado; lo único que no hace es subir. La regla, en `dominio/practica.ts`.
  */
  "sesion.parado_a": "parado a los {t}",
  "sesion.a_medias_titulo": "Por hoy está bien.",
  "sesion.a_medias_texto":
    "Has practicado {hecho} de {total}. Queda apuntado, pero con menos de la mitad no subes de " +
    "peldaño: la próxima vez te toca esta misma, sin nada pendiente. Hoy no hagas otra.",
  "sesion.siguiente": "Siguiente: {nombre}",
  "sesion.ver_la_sesion": "Ver cómo te fue en la sesión",
  "sesion.hoy_no_puedo_este": "Hoy no puedo hacer este",
  // ── La cuenta ─────────────────────────────────────────────────────
  /* 16/09: decían «Guardar», «Guarda este plan y vuelve cuando quieras.» y «Ya lo tienes hecho.
     Esto solo sirve para que siga estando aquí mañana, y en el móvil.». Sin sesiones gratis (el
     dueño, en `apps/api/src/muro.ts`) registrarse no es guardar: es por donde se llega a practicar,
     con el código de acceso o la suscripción, que están en esta misma pantalla. */
  "cuenta.guardar": "Crear la cuenta",
  "cuenta.guardar_titulo": "Crea tu cuenta para empezar a practicar.",
  "cuenta.guardar_ayuda":
    "Para practicar tu plan hace falta una suscripción o un código de acceso: pon aquí tu código, o " +
    "suscríbete más abajo.",
  "cuenta.entrar_titulo": "Vuelve a tu plan.",
  /*
    ══════════════════════════════════════════════════════════════════════
    15/09/2026 · SE ENTRA CON UN CÓDIGO DE SEIS CIFRAS («el login es por OTP»)
    ══════════════════════════════════════════════════════════════════════

    Se quitan de aquí, en los tres idiomas: el ojo de la contraseña
    (`cuenta.ver`, `cuenta.ocultar`), «Mira tu correo» con «te hemos mandado
    un enlace, un clic y entras» (`cuenta.mira_el_correo`, `…_texto`), «Ya he
    abierto el enlace · Entrar» (`cuenta.ya_confirme`), «Esa contraseña es
    corta» (`cuenta.error_corta`), «El correo o la contraseña no cuadran»
    (`cuenta.error_entrar`), las pantallas del enlace y de la contraseña nueva
    (`correo.enlace_titulo` … `correo.cambiada_texto`, `correo.reenviar`),
    `cuenta.contrasena`, `cuenta.contrasena_minimo`, `cuenta.olvidada` y
    `micuenta.sin_confirmar`. Están en git.

    Y entran las del código. Una regla en todas: NINGUNA DICE SI UN CORREO
    TIENE CUENTA. Al entrar se dice «si ese correo tiene cuenta, le llega»;
    al crearla sí se dice «te lo hemos mandado», porque ahí sale siempre.
  */
  "cuenta.entrar_ayuda": "Escribe tu correo y te mandamos un código para entrar. No hace falta contraseña.",
  "cuenta.mandar_codigo": "Mándame el código",
  "cuenta.codigo_titulo": "Escribe el código del correo.",
  "cuenta.codigo_texto_nueva": "Te mandamos un número de seis cifras a {correo}.",
  "cuenta.codigo_texto": "Si {correo} tiene cuenta, le llega un número de seis cifras.",
  "cuenta.codigo_campo": "Código",
  "cuenta.codigo_caduca": "Son seis cifras y valen diez minutos.",
  "cuenta.codigo_otro": "Mándame otro código",
  "cuenta.codigo_otro_en": "Podrás pedir otro en {s} s",
  "cuenta.codigo_otro_mandado": "Te mandamos otro. El anterior ya no sirve.",
  "cuenta.codigo_no_vale":
    "Ese código no vale. Puede estar mal escrito, haber caducado o haberse usado ya: pide otro si hace falta.",
  "cuenta.otro_correo": "Usar otro correo",
  "cuenta.ya_tengo": "Ya tengo cuenta",
  "cuenta.no_tengo": "No tengo cuenta todavía",
  "cuenta.mira_spam": "Si no llega en un par de minutos, mira en la carpeta de correo no deseado.",
  /*
    EL CÓDIGO SE GASTABA EN SILENCIO.

    Quien se registra con un código de acceso pasa a una pantalla que solo
    habla del correo: no queda ni una palabra sobre lo que ha escrito, y un
    código que se entrega y del que nadie vuelve a decir nada se lee como un
    código perdido.

    Y NO DICE «canjeado», que sería una fuga: con un correo que ya tiene cuenta
    la transacción se sale antes y el código NO se gasta, y esa pantalla tiene
    que ser idéntica en los dos casos. Dice adónde mirar, que es verdad
    siempre y es lo que hace falta.
  */
  "cuenta.codigo_va_con_la_cuenta":
    "El código de acceso va con esta cuenta: lo verás en «Tu cuenta» al entrar.",
  "cuenta.error_adulto": "Hay que confirmar que tienes 18 años o más.",
  /*
    EL FRENO SE DICE. Callarlo le decia a quien acabamos de cerrarle la puerta
    un rato que su contrasena esta mal, y esa persona se pasa la tarde probando
    variantes de algo que esta bien.

    (15/09/2026: decía «la contraseña puede estar bien». Sin contraseñas, lo
    que puede estar bien es el código.)
  */
  "cuenta.demasiados_intentos":
    "Demasiados intentos seguidos. Espera quince minutos y vuelve a probar: tu código puede estar bien; " +
    "lo que cerramos un rato es la entrada.",
  /*
    15/09/2026: decía «Tu contraseña es correcta» y «Contesta al último correo
    que te mandamos». Lo primero ya no existe; lo segundo no llegaba a nadie,
    porque los correos salen de `noreply@efamaa.com` y ahí no lee nadie
    (`config.ts`). El WhatsApp y el correo de ayuda están en el pie de la
    plataforma, debajo de este aviso.
  */
  "cuenta.bloqueada":
    "Cerramos el acceso a esta cuenta. Tu código es correcto: el bloqueo lo pusimos nosotros. Escríbenos " +
    "por WhatsApp o al correo de ayuda que está abajo y lo revisamos.",
  "cuenta.error_generico": "Algo falló. Vuelve a intentarlo en un momento.",
  // 16/09: decía «Este plan vive ahora mismo en este navegador. Guárdalo y lo tendrás mañana, y en
  // el móvil.». Sin sesiones gratis, lo que ofrece es el registro para practicar (ver `cuenta.guardar`).
  "planner.guardalo":
    "Para practicar este plan, crea tu cuenta: ahí pones tu código de acceso o te suscribes. Y tu plan te " +
    "sigue también al celular.",
  // ── El gesto dibujado ─────────────────────────────────────────────
  "gesto.esquema": "Esquema, no una foto",
  "gesto.asi_si": "Así sí",
  "gesto.asi_no": "Así no",
  "gesto.paso": "Paso",
  // ── Las familias del catálogo, y lo que quedaba suelto ────────────
  "familia.respiracion": "Respiración y apoyo",
  "familia.sovt_estable": "Boca semiabierta",
  "familia.sovt_fluctuante": "Boca semiabierta con vibración",
  "familia.resonancia": "Resonancia",
  "familia.articulacion": "Articulación y dicción",
  "familia.rango": "Rango y flexibilidad",
  "familia.proyeccion": "Potencia y proyección",
  "familia.relajacion": "Relajación y postura",
  "familia.calentamiento": "Calentamiento",
  "familia.enfriamiento": "Enfriamiento",
  "familia.higiene": "Hábitos",
  "familia.entorno": "Entorno",
  "familia.evaluacion": "Autochequeo",
  "familia.voz_profesional": "Voz de trabajo",
  "familia.escucha": "Escucha y oído",
  "familia.habla_clara": "Habla clara",
  "familia.protocolo": "Secuencia publicada",
  "familia.contexto": "Para un momento concreto",
  "escena.grave": "grave",
  "escena.agudo": "agudo",
  "temp.empezar": "Empezar",
  "temp.quedan": "Quedan {t}",
  "mov.cuanto": "Cuánto",
  "mov.repeticiones": "{n} repetición | {n} repeticiones",
  // ── El tubo con dos puertas ───────────────────────────────────────
  "tubo.esquema": "Esquema, no una foto",
  "tubo.titulo": "Por qué cerrar un poco la boca cuida la voz.",
  "tubo.abierto": "Boca abierta",
  "tubo.estrecho": "Salida estrecha",
  "tubo.puerta_arriba": "La puerta de arriba: los labios, o el tubito",
  "tubo.puerta_abajo": "La puerta de abajo: donde nace la voz",
  "tubo.aire": "El aire",
  "tubo.choca_fuerte": "La puerta de abajo choca fuerte.",
  "tubo.choca_suave": "La puerta de abajo choca suave.",
  "tubo.moraleja":
    "Si tapas un poco la salida, el aire se amontona dentro y empuja hacia abajo. Y así la puerta " +
    "de abajo no tiene que cerrarse de golpe para hacer el mismo sonido.",
  "app.secciones": "Secciones",
  "app.ajustes_nav": "Ajustes",
  "gesto.anterior": "Paso anterior",
  "gesto.siguiente": "Paso siguiente",
  "onb.antes_de_nada": "Antes que nada",
  "onb.el_microfono": "El micrófono",
  "onb.seguridad": "Seguridad",
  "onb.lo_ultimo": "Lo último",
  "onb.si": "Sí",
  "onb.no": "No",
  "onb.conceder": "Permitir el micrófono",
  "onb.ahora_no": "Ahora no",
  /* 13/09: el navegador negaba el micrófono y se seguía sin decir nada. */
  "onb.sin_microfono_seguimos":
    "El navegador no nos dejó usar el micrófono. No pasa nada, seguimos: puedes medir tu voz cuando " +
    "quieras desde “Tu cuenta”, y el plan se ajusta a ella.",
  "onb.que_te_trae": "¿Qué te trae por aquí?",
  "onb.ver_mi_plan": "Ver mi plan",
  "onb.montando": "Armando tu plan…",

  // ── La puerta ─────────────────────────────────────────────────────────
  // ── Lo que la persona contó, tal y como se lo devolvemos ──────────
  "motivo.R-MENOR": "La plataforma es solo para adultos.",
  /* 13/09 (B·76, decisión del dueño): decía «coger»; «coger» suena mal en Latinoamérica. */
  "motivo.R-RESPIRACION": "Dificultad para respirar o ruido al tomar aire.",
  "motivo.R-SANGRE": "Sangre al toser o en la flema.",
  "motivo.R-REPOSO": "Hay reposo vocal indicado por un profesional.",
  "motivo.R-CIRUGIA-3M": "Cirugía de garganta o de cuerdas en los últimos tres meses.",
  "motivo.R-DISFAGIA": "Dificultad para tragar, o la sensación de tener algo en la garganta que no se va.",
  "motivo.R-OTALGIA": "Dolor al hablar o al tragar que llega hasta el oído.",
  "motivo.R-BULTO": "Un bulto en el cuello.",
  "motivo.R-AFONIA-SUBITA": "Pérdida de voz repentina y completa.",
  "puerta.no_hay_plan": "Hoy no podemos armarte un plan.",
  /*
    LA SALIDA DE LA PANTALLA DE PUERTA CERRADA, que no tenia ninguna.

    No es una puerta trasera: vuelve a la pregunta uno y se contestan las trece
    otra vez. Lo que quita es el castigo a un toque de mas.
  */
  "puerta.me_equivoque": "Me equivoqué al contestar. Volver a las preguntas",
  "puerta.lo_que_contaste": "Lo que nos contaste:",
  "cierre.clinico.porque":
    "No estamos diciendo que tengas nada. Estamos diciendo que eso lo tiene que mirar alguien que " +
    "pueda verte, y nosotros no podemos. Un plan de ejercicios encima de eso no ayuda: tapa.",
  "cierre.clinico.vuelve":
    "Si te lo miran y te dicen que puedes practicar, vuelve cuando quieras. No te hemos cobrado " +
    "nada y no te hemos guardado un plan a medias.",
  "cierre.edad.porque":
    "Esto no tiene nada que ver con tu voz. Es una regla de la casa: la plataforma es solo para adultos, " +
    "y preferimos decirlo aquí y no después de haberte cobrado.",
  "cierre.edad.vuelve":
    "Vuelve cuando cumplas los dieciocho. No te hemos cobrado nada y no te hemos guardado un plan " +
    "a medias.",
  // ── Cuánta prisa corre · tarjetas al estilo NHS ────────────────────
  "urgencia.ahora": "Ve a urgencias ahora.",
  "urgencia.ahora_texto": "No esperes a mañana, y no manejes tú para llegar: que te lleve alguien. Esto se revisa hoy mismo.",
  "urgencia.hoy": "Que te vean hoy.",
  "urgencia.hoy_texto":
    "Llama hoy mismo a tu médico o al servicio de urgencias que te corresponda. Hoy, no esta " +
    "semana.",
  "urgencia.esta_semana": "Pide cita esta semana.",
  "urgencia.esta_semana_texto":
    "No es una urgencia de hoy, pero tampoco es un «cuando me acuerde». Esto se revisa en semanas, no en " +
    "meses.",
  "urgencia.cuando_puedas": "Coméntalo en tu próxima cita.",
  "urgencia.cuando_puedas_texto": "No corre prisa, pero conviene que lo sepa el médico que te atiende.",
  "urgencia.no_molestas":
    "No le estás haciendo perder el tiempo a nadie por pedir que te revisen. El servicio de salud es " +
    "exactamente para esto.",
  "urgencia.numero": "En tu país, el número de emergencias es el {n}.",
  "urgencia.mientras_tanto":
    "Mientras tanto puedes seguir aquí. Vamos a bajarte la carga de voz y a enseñarte a usarla " +
    "sin forzar.",
  "urgencia.sigue_aqui": "Seguir con mi plan",
  "puerta.urgencias": "Ve a urgencias.",
  "puerta.medico": "Pide cita con tu médico.",
  "puerta.otorrino": "Pide cita con un otorrinolaringólogo.",
  "puerta.quien_indico_reposo": "Habla con quien te indicó el reposo antes de practicar nada.",
  "puerta.quien_opero": "Habla con quien te operó antes de practicar nada.",

  /* ───────────────────────────────────────────────────────────────────────
     LO QUE DICE EL ASISTENTE CUANDO PARA · y por qué está aquí y no allí

     Estos textos vivían escritos a mano dentro de `packages/ia`, en
     castellano y solo en castellano. O sea que **el mensaje más importante
     que manda el producto —«para, esto lo tiene que mirar alguien»— le salía
     en español a quien escribía en inglés o en portugués.** Medido el
     07/09/2026 pidiendo la respuesta en `en` y en `pt`: las dos idénticas, en
     castellano.

     Al traerlos aquí pasan por lo mismo que todo lo demás: `Record<Clave,
     string>` no compila si falta un idioma, y `verificar:i18n`,
     `verificar:jerga` y `verificar:dolencias` los miran como a cualquier otra
     cadena de pantalla. Escritos a mano dentro de un paquete no los miraba
     nadie.

     Y las derivaciones NO se repiten: `bandera.a_quien_*` no existe porque
     ya existen `puerta.urgencias`, `puerta.medico` y `puerta.otorrino`.
     `banderas.ts` tenía su propia copia en castellano, que es exactamente
     cómo se desincronizan dos listas.
     ─────────────────────────────────────────────────────────────────────── */
  "bandera.paro_aqui": "Voy a parar aquí un momento.",
  "bandera.no_es_lo_mio":
    "Me contaste **{que}**, y con eso no te puedo ayudar con ejercicios de voz. No estoy diciendo que " +
    "tengas nada: estoy diciendo que eso lo tiene que revisar alguien que pueda verte, y yo no puedo.",
  "bandera.aqui_estare":
    "Cuando te lo hayan mirado y te digan que puedes practicar, aquí estaré.",

  /* Lo que la persona ha descrito, en sus palabras y no en las de nadie más.
     Van una por regla de `banderas.ts`, y el identificador de la regla vive
     en el código: aquí solo está lo que se lee. */
  "bandera.respiracion": "dificultad para respirar",
  "bandera.sangre": "sangre al toser",
  "bandera.bulto": "un bulto en el cuello",
  "bandera.tragar": "dificultad para tragar",
  "bandera.oido": "dolor al hablar o al tragar que llega al oído",
  "bandera.ronquera": "un cambio de voz que dura semanas",
  "bandera.sin_voz": "una pérdida de voz repentina",
  "bandera.dolor": "dolor al hablar que no se va",

  "asistente.no_lo_se":
    "Eso no lo sé. Solo puedo hablarte de lo que está en nuestro catálogo, y ahí no lo tengo. Si es algo " +
    "que te preocupa de tu voz, lo mejor es que lo revise alguien que pueda escucharte.",
  "asistente.no_puedo_ahora":
    "Ahora mismo no puedo contestarte, y no es culpa tuya. Mientras tanto, en la biblioteca están todos " +
    "los ejercicios completos.",

  // ── La ficha del movimiento. Nombres de PANTALLA, nunca de campo. ─────
  "ficha.cuanto": "Cuánto",
  "ficha.que_pasa_por_dentro": "Qué pasa por dentro",
  "ficha.como_se_hace": "Cómo se hace",
  "ficha.lo_que_te_dices": "Lo que te dices mientras lo haces",
  "ficha.en_que_se_falla": "En qué se suele fallar",
  "ficha.para_y_consulta": "Detente y consúltalo si…",
  "ficha.que_salio_al_medirlo": "Qué salió al medirlo",
  "ficha.quien_lo_propone": "Quién lo propone",
  "ficha.si_no_te_sale": "Si no te sale",
  "ficha.a_favor": "A favor",
  "ficha.en_contra": "En contra",
  "ficha.como_lo_notas": "Cómo lo notas",
  "ficha.como_se_arregla": "Cómo se arregla",
  "ficha.es_una_imagen": "Es una imagen, no anatomía",
  "ficha.sin_fuente":
    "No hemos encontrado quién lo describe así. Es un gesto que enseña mucha gente y no hemos " +
    "localizado una fuente que lo firme. Lo incluimos igual, y lo decimos.",
  "ficha.solo_el_manual": "Lo describe el manual del que nace este proyecto, y nadie más que hayamos encontrado.",
  "ficha.sin_revisar":
    "Los dibujos y el video de este ejercicio todavía no los ha revisado un profesional. Lo decimos aquí " +
    "porque nos parece que hay que decirlo.",

  // ── El temporizador ───────────────────────────────────────────────────
  "reloj.parar": "Parar",
  "reloj.se_acabo": "Se acabó.",
  "reloj.hay_tope": "Al medirlo, pasado ese punto subía la tensión y bajaba la calidad de la voz.",
  "reloj.hiciste_lo_que_tocaba": "Hiciste lo de hoy.",
  "reloj.parado":
    "Parado. Queda anotado lo que hiciste, y ya está: parar cuando lo necesitas es parte del ejercicio, " +
    "no un error.",

  // ── El micrófono ──────────────────────────────────────────────────────
  "mic.grave": "grave",
  "mic.agudo": "agudo",

  // ── La sesión ─────────────────────────────────────────────────────────
  "sesion.de_hoy": "La sesión de hoy",
  "sesion.preparacion": "Preparación",
  "sesion.calentamiento": "Calentamiento",
  "sesion.trabajo": "Trabajo",
  "sesion.enfriamiento": "Enfriamiento",
  "sesion.descanso_voz_cansada":
    "Hoy toca descansar. Nos dijiste que tienes la voz cansada y el plan te hizo caso: bajar el ritmo " +
    "cuando la voz está cansada es parte del entrenamiento, no una interrupción.",
  "sesion.descanso_reciente":
    "Hiciste una sesión hace poco. Dos seguidas no suman: se estorban. Vuelve dentro de unas horas.",
  "sesion.descanso_cadencia": "Hoy no hay sesión. Descansar es parte del plan.",
  "sesion.descanso_sin_presupuesto":
    "Con el tiempo que elegiste no alcanza para una sesión completa, y el calentamiento y el enfriamiento " +
    "no se quitan. Elige un tiempo más largo.",

  // ── Cuenta y cobro ────────────────────────────────────────────────────
  "cuenta.correo": "Correo",
  "cuenta.crear": "Crear la cuenta",
  "cuenta.entrar": "Entrar",
  "cuenta.salir": "Salir",
  "cuenta.soy_adulto": "Tengo 18 años o más",
  "cuenta.borrar": "Borrar mi cuenta",
  "cuenta.borrar_aviso":
    "Se borra de verdad y no se puede deshacer. Tus respuestas sobre salud se van con ella.",
  "cuenta.borrar_teclea": "Escribe {p} para confirmarlo",
  "precio.al_ano": "al año",
  // ── Dentro ya, con el código de regalo puesto ─────────────────────────
  /*
    15/09/2026: se llamaban «los enlaces del correo» y los pintaba `Correo.tsx`
    al abrir el enlace de confirmar. Sin enlaces, los pinta `Cuenta.tsx` al
    entrar con el código cuando se ha traído un código de regalo. Las claves
    conservan el nombre: la frase es la misma y sigue siendo verdad.
  */
  "correo.confirmado_titulo": "Ya estás dentro.",
  "correo.confirmado_texto":
    "Tu correo está confirmado y la sesión abierta. Te llevamos a tu plan.",
  "correo.confirmado_boton": "Ir a mi plan",
  /* 13/09, recorrido del dueño: el código de acceso no se volvía a nombrar al entrar. */
  "correo.confirmado_de_por_vida":
    "Y tu código ya está puesto: tienes Voice Comfort entero, de por vida, sin pagos ni renovaciones.",
  "correo.confirmado_regalada": "Y tu código ya está puesto: tienes Voice Comfort entero, sin pagar.",
  "cuenta.codigo_opcional": "¿Tienes un código de acceso? (opcional)",
  "cuenta.codigo_ayuda": "Se escribe tal cual viene, con guiones y todo. Si no tienes, deja el campo vacío.",
  "cuenta.error_codigo":
    "Ese código no sirve, o ya se usó. Tu cuenta no se creó: prueba otra vez, con el código correcto o " +
    "sin ninguno.",
  /*
    ══════════════════════════════════════════════════════════════════════
    LA CASILLA DE ACEPTAR (D32)
    ══════════════════════════════════════════════════════════════════════

    Palabras del dueño: «estos contratos según el idioma son los que aparecen
    en el registro para la persona marcar el checkbox, diciendo "acepto el
    contrato de servicios"».

    Es UNA casilla, como pidió, y cubre DOS documentos. El porqué está en
    `packages/contenido/src/legales.ts`: este producto pregunta por la salud de
    quien lo usa antes de que tenga cuenta, y aceptar solo el contrato de
    servicios dejaría eso sin cubrir. Queda apuntado por si prefiere
    exactamente su frase.

    `{a}` y `{b}` los sustituye la pantalla por los dos enlaces, así que el
    orden de las palabras lo decide cada idioma y no el código.
  */
  "cuenta.acepto": "Acepto el {a} y la {b}.",
  /*
    Y la de UNO SOLO. Publicar es una acción por (tipo, idioma), así que
    tener el contrato publicado y la política todavía no es el estado normal
    entre las dos publicaciones — no un caso raro. Con la frase de dos, el
    hueco que falta se quedaba vacío: «Acepto el Contrato de servicios y la .»
  */
  "cuenta.aceptoUno": "Acepto el {a}.",
  "cuenta.legalEnOtroIdioma": "Por ahora solo está publicado en otro idioma, y es esa versión la que estás aceptando.",
  "cuenta.legalNoSeSabe": "No se ha podido comprobar qué hay que aceptar.",
  "cuenta.legalReintentar": "Volver a intentarlo",
  "cuenta.acepto_hay_que": "Para crear la cuenta hay que aceptarlos.",
  "precio.lanzamiento": "Precio de lanzamiento",
  // 15/09: aquí estaba `cta.sin_tarjeta` («sin tarjeta, sin cuenta»). El dueño: «es mentira, la
  // persona tiene que suscribirse». Solo lo usaba la página de venta; se quita en los tres idiomas.
  "precio.normal": "Precio normal",
  // 06/10/2026: el dueño fija el lanzamiento de por vida (sin subidas) y revierte D27, así que
  // `PRECIO.renuevaAlNormal` pasa a false. La frase NO puede llevar {normal} ni la cifra normal:
  // `comprobar-copy.ts` lo exige por la cara de false. Con renuevaAlNormal en false la landing no
  // la pinta; se deja redactada y veraz por si otra pantalla la usa.
  "precio.renovacion": "Pago único al año. El precio no sube: conservas esta tarifa mientras tu cuenta siga activa.",
  /*
    18/09 · LA MONEDA DEL COBRO, y no es letra pequeña: es un requisito legal.

    La cifra grande está en dólares —es la referencia del producto— y el cobro se
    hace en pesos colombianos, porque la cuenta de la pasarela es colombiana y
    solo liquida COP (el porqué, en `apps/api/src/precio.ts`).

    Enseñar un precio y cobrar otro, aunque sea el mismo valor en otra moneda,
    incumple lo que la FTC, la Directiva 2011/83 y el CDC art. 31 exigen enseñar
    ANTES de tomar el dato de pago — los tres que ya se citan por la renovación.
    Y Google lo marca como precio engañoso.

    `{cop}` lo sustituye la landing con la cifra formateada, por lo mismo que
    `{normal}`: el número vive en un solo sitio.
  */
  "precio.nota_moneda": "El cobro se realiza en pesos colombianos: {cop}.",
  "precio.cancelar": "Cancelar la suscripción",
  "precio.cancelada":
    "Cancelada. Sigues teniendo acceso hasta la fecha de renovación: no te cortamos algo que ya " +
    "has pagado.",
  "precio.un_clic": "Se cancela en un clic desde tu cuenta, sin escribir a nadie.",

  /*
    ══════════════════════════════════════════════════════════════════════
    EL BOTÓN DE PAGAR, Y LO QUE DICE MIENTRAS NO HAY PASARELA (D31)
    ══════════════════════════════════════════════════════════════════════

    Hasta el 05/09 no existía ninguno, y estaba razonado en `MiCuenta.tsx`:
    sin ruta de cobro, ofrecer «pagar» sería otro botón que no lleva a ninguna
    parte. El dueño decidió lo contrario: «que salga un error que diga tipo,
    suscripciones desactivadas en ese momento».

    Y va con la frase de al lado, que es la que convierte un callejón en un
    camino. Sin ella, quien pulsa pagar se queda mirando una negativa; con
    ella se entera de que puede usar el producto igual y de dónde se canjea un
    código. Es la misma regla que la pantalla de puerta cerrada, que tampoco
    podía quedarse sin salida.

    Son DOS frases y no una porque quien lo pulsa desde la página de venta
    todavía no tiene cuenta —no hay ningún «aquí abajo» donde canjear— y quien
    lo pulsa desde la suya sí.
  */
  "pagar.boton": "Suscribirme",
  /*
    EL TITULAR DEL MODAL. Dice el HECHO y no pide perdón: quien acaba de pulsar
    «Suscribirme» quiere saber si puede pagar o no, y en esa frase está.
  */
  "pagar.cerradas_titulo": "Todavía no se puede pagar.",
  "pagar.desactivadas": "Las suscripciones están desactivadas en este momento.",
  /*
    UN 401 EN «Tu cuenta» SIGNIFICA QUE LA SESIÓN SE CAYÓ. Desde el 18/09 el
    botón solo vive en pantallas con sesión (la cuenta, el plan, el muro), así
    que ya no hay un segundo significado —«todavía no hay cuenta»— que cubrir.
  */
  "pagar.sinSesion": "Se cayó la sesión. Vuelve a entrar y tócalo otra vez.",
  /*
    18/09/2026 · CUANDO LA PASARELA NO CONTESTA, QUE NO ES LO MISMO QUE ESTAR
    CERRADA.

    Caía en `micuenta.algo_fue_mal` («algo no ha salido»), y eso está mal por dos
    cosas: no dice que NO SE HA COBRADO NADA —que es lo primero que quiere saber
    quien acaba de pulsar un botón de pagar— y no dice que volver a intentarlo
    sirve, que aquí sí sirve.

    La diferencia con `pagar.desactivadas` es real: aquella es una decisión del
    negocio y reintentar no vale para nada; ésta es un tercero que no ha
    contestado, y en un minuto probablemente sí.
  */
  "pagar.no_disponible":
    "No hemos podido abrir la página de pago. No se ha cobrado nada: vuelve a intentarlo en un minuto.",
  "pagar.desactivadas_en_la_cuenta":
    "Mientras tanto, si tienes un código de acceso puedes canjearlo aquí abajo.",
  /*
    Decía «practicar sin pagar nada», y eso se lee como gratis para siempre:
    son las primeras sesiones, y después está el muro (D42). (10/09, P58.)

    16/09: y después decía «puedes montar tu plan y empezar a practicar: las
    primeras sesiones no se pagan». El dueño quitó las sesiones gratis (en
    `apps/api/src/muro.ts`): sin acceso se monta el plan, pero no se practica.
  */
  "pagar.desactivadas_en_la_venta":
    "Mientras tanto puedes montar tu plan y, si tienes un código de acceso, ponerlo al crear tu cuenta.",

  /*
    ══════════════════════════════════════════════════════════════════════
    EL MURO · lo que lee quien tiene cuenta y no tiene acceso
    ══════════════════════════════════════════════════════════════════════

    `D42`, palabras del dueño: «en un modal que dice que las suscripciones aun
    no estan abiertas». O sea que esto NO es una pantalla de venta: hoy no hay
    nada que vender —`D31` dejó el cobro apagado— y decir «suscríbete» delante
    de un botón que contesta que están desactivadas sería mandar a alguien a
    una puerta cerrada.

    Así que dice la verdad y da la única salida que existe hoy, que es el
    código de acceso. Y no promete fecha: «cuando se pueda» no es «pronto».

    16/09/2026 · SIN NÚMEROS. Hasta hoy el muro salía al acabar las ocho
    sesiones gratis y contaba: `muro.hechas` («Has practicado {n} veces.»),
    `muro.gratis` («Sin suscripción se hacen {n} sesiones, y ya están
    hechas.») y, en el plan, `muro.quedan` («Te quedan {n} sesiones sin
    suscripción.»). El dueño quitó las sesiones gratis (en
    `apps/api/src/muro.ts`): el muro sale desde la primera y no tiene nada que
    contar. `verificar:muro` mira que ninguna frase `muro.*` vuelva a llevar
    una cifra.
  */
  "muro.titulo": "Las suscripciones todavía no están abiertas",
  /*
    18/09/2026 · EL TITULAR CUANDO SÍ SE PUEDE PAGAR.

    `muro.titulo` de arriba se queda, y sigue siendo la verdad mientras no haya
    pasarela. Pero desde que la hay (Rapyd, D19) esa frase dice lo contrario de
    lo que pasa, en la única pantalla donde alguien la va a leer: le cuenta que
    no puede pagar justo cuando sí puede.

    Éste no promete nada y no cierra nada: dice qué falta. Se usa también
    mientras el servidor no ha contestado todavía si se puede comprar, porque es
    verdad en los dos casos — el porqué está en `sePuedeComprar.ts`.
  */
  "muro.titulo_hace_falta": "Te falta el acceso para practicar",
  "muro.sin_acceso": "Para practicar hace falta una suscripción o un código de acceso.",
  /*
    Lo que se dice cuando el cobro está abierto. Sin fecha, sin urgencia y sin
    contar sesiones: el muro de este producto no mete prisa, y `verificar:muro`
    comprueba que ninguna frase `muro.*` lleve una cifra.
  */
  "muro.se_puede":
    "Puedes suscribirte ahora y seguir con tu plan, o canjear un código de acceso en tu cuenta.",
  "muro.mientras":
    "Cuando puedas suscribirte, lo verás en tu cuenta. Si tienes un código de acceso, ya puedes canjearlo " +
    "ahí.",

  /*
    ══════════════════════════════════════════════════════════════════════
    A QUIÉN SE ESCRIBE (D37) · y antes de esto no había NADA
    ══════════════════════════════════════════════════════════════════════

    Cero menciones de contacto o soporte en las tres aplicaciones y en este
    mismo diccionario. Un producto que le dice a alguien «esto hay que
    mirárselo» y no tiene detrás a quién escribirle es un producto al que no
    se le puede preguntar.

    Las horas NO se escriben aquí: salen de `HORARIO` en `@vc/contenido`, y
    estas frases traen `{a}` y `{b}` donde van. Escritas a mano en tres
    idiomas, el día que cambie el horario cambia en dos.

    Y la zona horaria va DICHA. «De 8 a 18» sin zona es una hora que cada
    quien lee en la suya: quien escribe desde Brasil a las 8:30 de su mañana
    se encuentra con que aquí son las 6:30 y no hay nadie.
  */
  "contacto.titulo": "Escríbenos",
  "contacto.whatsapp": "WhatsApp",
  "contacto.correo": "Correo",
  // 15/09: aquí estaba `contacto.direccion` («Dirección»). El dueño, sobre la dirección del pie:
  // «quita eso». Solo la usaba la página de venta; se quita en los tres idiomas.
  "contacto.horario": "Horario",
  "contacto.entre_semana": "De lunes a viernes, de {a} a {b}",
  "contacto.sabado": "Sábados, de {a} a {b}",
  "contacto.zona": "Hora de Bogotá ({zona})",
  /* El nombre accesible del botón flotante. Lo lee quien no ve el icono, y
     tiene que decir adónde lleva, no cómo se ve. */
  "contacto.whatsapp_flotante": "Escríbenos por WhatsApp",
  /*
    EL NOMBRE DEL BLOQUE DE ENLACES LEGALES, y existe por un fallo concreto.

    El pie de la página de venta anunciaba ese grupo con
    `NOMBRE_DEL_LEGAL[idioma].terminos`, o sea con el nombre de UNO de sus
    cinco enlaces — y de uno que además puede no estar publicado. Quien navega
    por voz oía «Términos del servicio» y dentro se encontraba la política de
    privacidad y la de reembolsos.
  */
  "pie.legales": "Documentos legales",
  /*
    ══════════════════════════════════════════════════════════════════════
    EL BLOQUEO POR SALUD · lo que se lee cuando la puerta clínica frena
    ══════════════════════════════════════════════════════════════════════

    Encargo del dueño del 06/09. Hasta hoy, disparar una bandera roja era un
    callejón amable: la plataforma decía a quién acudir y ahí se acababa. Ahora
    se le crea la cuenta, se le guarda todo y se la pasa a una persona:

        «Así un atendiente humano la puede ayudar.»

    ── Y NO SE NOMBRA NINGUNA CONDICIÓN, NI AQUÍ NI EN NINGÚN SITIO ───────

    Es LA regla del dueño y en esta pantalla es donde más cuesta cumplirla,
    porque es donde uno querría explicar. Se dice «algo que conviene mirar»,
    que es verdad, y no se dice qué — entre otras cosas porque no lo sabemos:
    lo que hay es una respuesta a una pregunta, no un diagnóstico.

    ── EL TONO ───────────────────────────────────────────────────────────

    Quien lee esto se acaba de llevar un susto. Así que lo primero es que no ha
    perdido nada, lo segundo es qué pasa ahora, y lo tercero es a quién
    escribir. Nada de «no puedes»: lo que hay es un paso más, con una persona
    al otro lado.
  */
  "salud.guardar_y_hablar": "Guardar esto y hablar con alguien",
  "salud.desde_la_puerta":
    "Puedes dejarnos tus datos y lo revisamos contigo. Guardamos lo que contestaste, nos escribes y, si " +
    "todo está en orden, activamos tu cuenta. No hace falta que vuelvas a empezar.",
  /* Lo mismo para quien ya tiene cuenta (17/09): sus datos ya están, así que lo que se
     ofrece es guardar lo contestado y que lo mire alguien. Ver `Onboarding.tsx`. */
  "salud.desde_la_puerta_con_cuenta":
    "Podemos guardar lo que contestaste y que lo revise alguien del equipo. Nos escribes y, si todo está " +
    "en orden, activamos tu cuenta. No hace falta que vuelvas a empezar.",
  "salud.titulo": "Antes de empezar, hablemos",
  "salud.texto":
    "Por lo que nos has contado, preferimos que alguien de nuestro equipo lo " +
    "mire contigo antes de que empieces a practicar. Tu cuenta está creada y " +
    "tus respuestas guardadas: no has perdido nada.",
  "salud.que_pasa_ahora":
    "Escríbenos y te decimos qué necesitamos. Normalmente es una constancia del profesional que te " +
    "atiende; en cuanto la tengamos, activamos la cuenta.",
  "salud.pausado_titulo": "Pausamos los ejercicios",
  "salud.pausado_texto":
    "Vimos algo que conviene revisar antes de que sigas practicando, así que pausamos los ejercicios por " +
    "seguridad. Tu cuenta, tu plan y todo lo que llevas hecho siguen aquí.",
  "salud.escribenos_para_seguir": "Escríbenos y lo vemos contigo:",
  /*
    ══════════════════════════════════════════════════════════════════════
    CUANDO SE LEVANTA EL BLOQUEO · la oferta que se saltó al frenarla
    ══════════════════════════════════════════════════════════════════════

    Encargo del dueño del 06/09: «cuando se haya desbloqueado […] al entrar en
    la plataforma, si no puso el codigo, o no pago, le ponemos un modal
    pidiendo pra pagar (boton y detalles para ir a la pasarela) o le ponemos en
    seguida la posibilidad de poner un codigo».

    NO DICE POR QUÉ SE LEVANTÓ, y es deliberado: quien lo lee acaba de hablarlo
    con una persona por WhatsApp. Repetírselo en un modal sería contarle su
    propia salud de vuelta, y esta plataforma no nombra eso en ningún sitio.

    Y NO PROMETE FECHA. El botón de pagar no existe hoy (`D31`), así que lo que
    dice es lo que hay. «Pronto» sería inventarse un calendario.

    16/09/2026 · SIN SESIONES GRATIS. Decía «Ya puedes practicar», «Hemos
    revisado lo que nos mandaste y hemos vuelto a abrir los ejercicios.» y
    «No hace falta que hagas nada ahora. Cierra esto y ponte cuando quieras.».
    Era verdad con las ocho sesiones gratis. Desde que el dueño las quitó, este
    modal solo le sale a quien NO tiene acceso, y le decía justo lo contrario de
    lo que puede hacer. Mismo cambio que el titular del correo «Ya puedes entrar»
    (el dueño: «si»), y la misma frase que ese correo: la cuenta está abierta.
  */
  "desbloqueo.titulo": "Tu cuenta ya está abierta",
  "desbloqueo.texto": "Revisamos lo que nos mandaste.",
  "desbloqueo.pasarela":
    "Queda un punto de antes: cómo vas a seguir practicando. Las suscripciones todavía no están abiertas; " +
    "cuando lo estén, las verás en tu cuenta.",
  "desbloqueo.mientras":
    "Si tienes un código, escríbelo en la casilla de arriba y empieza hoy mismo.",
  // ── Tu cuenta ─────────────────────────────────────────────────────────
  // ── La cabecera de la pagina de venta ─────────────────────────────────
  // Lo unico que ve un previsualizador de enlaces, que no ejecuta JavaScript.
  // El titulo se corta sobre los 60 caracteres y la descripcion sobre los 160.
  // 14/09: decía «Voice Comfort · tus ejercicios, y cuánto de cada uno»; la página nueva abre con el titular del dueño.
  // 15/09: las comillas latinas pasan a “inglesas” («ese símbolo dice que es hecho por IA») y fuera
  // «sin tarjeta y sin cuenta» («es mentira, la persona tiene que suscribirse»): va la garantía.
  "cabecera.titulo": "Voice Comfort · “Tener voz” lo dice todo. Cuida la tuya.",
  "cabecera.descripcion":
    "Un plan de voz hecho para ti, unos minutos al día desde el celular y un asistente experto en voz. " +
    "Con garantía de catorce días.",
  "cabecera.og_descripcion":
    "Un plan hecho para ti, unos minutos al día desde el celular y un asistente experto en voz siempre a " +
    "mano. Con garantía de catorce días.",
  // ── La página de venta: menú, botones, el chat de muestra y el pie (15/09) ──
  // Lo que no es prosa de un bloque. Vive aquí y no en `copy.ts` para que pase por los mismos
  // filtros que el resto del diccionario (jerga, dolencias, frases vetadas) sin campos nuevos.
  "venta.nav_secciones": "Secciones de la página",
  "venta.nav_como_funciona": "Cómo funciona",
  "venta.nav_para_quien": "Para quién",
  "venta.nav_asistente": "Asistente",
  "venta.nav_precio": "Precio",
  "venta.nav_preguntas": "Preguntas",
  "venta.menu_abrir": "Abrir el menú",
  "venta.menu_cerrar": "Cerrar el menú",
  "venta.crear_plan": "Crear mi plan",
  "venta.ver_como_funciona": "Ver cómo funciona",
  "venta.sello_garantia": "Garantía de catorce días",
  "venta.sello_cancelar": "Cancela cuando quieras",
  "venta.sello_movil": "Desde el celular, sin instalar nada",
  "venta.chat_nombre": "Asistente de Voice Comfort",
  "venta.chat_pregunta": "Mañana tengo seis horas de clase. ¿Cómo preparo la voz?",
  "venta.chat_respuesta":
    "Hoy haz tu sesión sin alargarla. Mañana, antes de la primera clase, haz el calentamiento de " +
    "tu plan, y entre clase y clase deja descansar la voz.",
  "venta.precio_plan": "Plan anual",
  "venta.precio_incluye": "Qué incluye",
  "venta.solo_adultos": "Solo para mayores de edad.",
  "venta.otra_pregunta": "¿Tienes otra pregunta?",
  "venta.escribenos": "Escríbenos por WhatsApp",
  "venta.pie_lema": "La mejor y más completa plataforma para cuidar tu voz.",
  "venta.pie_ayuda": "Ayuda",
  "venta.pie_derechos": "Todos los derechos reservados.",
  "micuenta.titulo": "Tu cuenta",

  /*
    LOS AJUSTES, QUE NO TENIAN PANTALLA. El idioma era un select suelto en la
    cabecera y el tema un boton al lado; quien busca como cambiar el idioma
    mira en su cuenta, que es donde esta en todas partes.
  */
  "micuenta.ajustes": "Ajustes",
  /* 16/09: sin contraseñas no quedaba forma de cerrar las demás sesiones; el dueño lo quiso aquí. */
  "micuenta.salir_de_todos": "Cerrar sesión en todos los dispositivos",
  "micuenta.salir_de_todos_ayuda":
    "Sales de Voice Comfort en todos los dispositivos donde hayas entrado, también en este. Para volver a " +
    "entrar te mandamos un código a tu correo.",
  "micuenta.tema": "Cómo se ve",

  /* Y los treinta segundos, que solo se podían hacer una vez. */
  "micuenta.tu_voz": "Tu voz",
  "micuenta.tu_voz_medida":
    "Tu voz habla alrededor de {hz} Hz (lo aguda o grave que es) y se mueve {rango} semitonos (un " +
    "semitono es de una tecla del piano a la de al lado).",
  "micuenta.tu_voz_otra_vez":
    "Con eso elegimos qué ejercicios caben en tu voz. Si te has medido con prisa, con ruido " +
    "o hace mucho, vuelve a hacerlo: son treinta segundos.",
  "micuenta.tu_voz_sin_medir":
    "Todavía no te has medido, así que te ofrecemos el catálogo entero. Si te mides, dejamos fuera lo que " +
    "no le cabe a tu voz. Son treinta segundos y el audio no sale de aquí.",
  "micuenta.medir": "Medir mi voz",
  "micuenta.medir_otra_vez": "Medirme otra vez",
  "micuenta.correo": "Tu correo",
  /* 15/09/2026: aquí iba `micuenta.sin_confirmar`. Con el código, tener sesión es haberlo confirmado. */
  "micuenta.confirmado": "Confirmado.",
  "micuenta.suscripcion": "Tu suscripción",
  "micuenta.sin_suscripcion":
    "Todavía no tienes. Puedes ver tu plan cuando quieras; para practicarlo hace falta una suscripción o " +
    "un código de acceso.",
  "micuenta.activa": "Activa.",
  /* ── Los cinco estados de la suscripción ──────────────────────────
     Eran uno: cualquiera que no hubiera pedido la cancelación veía «Activa.»
     y un botón de cancelar, también con la suscripción vencida. */
  "micuenta.prueba": "Estás de prueba.",
  "micuenta.prueba_hasta": "La prueba termina el {fecha}.",
  "micuenta.garantia":
    "¿No te está sirviendo? Escríbenos por WhatsApp dentro de los catorce primeros días y te devolvemos " +
    "lo que pagaste, sin que tengas que explicar nada.",
  "micuenta.regalada": "Tu acceso es de cortesía.",
  "micuenta.regalada_hasta": "Hasta el {fecha}.",
  "micuenta.regalada_de_por_vida": "De por vida, sin fecha de renovación.",
  "micuenta.vencida": "Se te venció el acceso.",
  "micuenta.vencida_el": "Venció el {fecha}.",
  /*
    ══════════════════════════════════════════════════════════════════════
    UN SOLO NOMBRE PARA UNA SOLA COSA: «CÓDIGO DE ACCESO»
    ══════════════════════════════════════════════════════════════════════

    Se llamaba de dos maneras según la pantalla —«de acceso» en el registro y
    en el muro, «de regalo» aquí— y eso ya era confuso solo. Con el muro pasó
    a ser un callejón: su modal dice «si tienes un código de acceso, ya puedes
    canjearlo ahí» y manda a esta pantalla, donde lo que hay es un campo que
    pregunta por un «código de regalo». Quien lo tenga en la mano tiene que
    decidir si es el mismo, y decidir eso no es su trabajo.

    Gana «de acceso» y no «de regalo», que es el nombre de la tabla: un código
    lo puede haber regalado el dueño o haberlo comprado una empresa para su
    gente, y lo único cierto para quien lo escribe es que le da acceso.
  */
  "micuenta.vencida_que_hacer": "Si tienes un código de acceso, puedes canjearlo aquí mismo y vuelves a entrar.",
  "micuenta.ya_cancelada": "Cancelada.",
  "micuenta.estado_raro": "Ahora mismo no podemos decirte cómo está tu suscripción. Vuelve a mirar en un rato: no cambió nada.",
  "micuenta.renueva_el": "Se renueva el {fecha}.",
  "micuenta.acceso_hasta": "Cancelada. Tienes acceso hasta el {fecha}.",
  "micuenta.acceso_sin_fecha": "Cancelada. Tienes acceso hasta el final del periodo pagado.",
  "micuenta.codigo": "¿Tienes un código de acceso?",
  "micuenta.codigo_ayuda": "Se escribe tal cual viene, con guiones y todo.",
  "micuenta.canjear": "Canjear",
  "micuenta.codigo_canjeado": "Canjeado. Ya tienes acceso.",
  "micuenta.codigo_no_vale": "Ese código no vale, o ya se usó.",
  "micuenta.borrado": "Cuenta borrada. No queda nada tuyo aquí.",
  "micuenta.algo_fue_mal": "Algo falló. Prueba otra vez en un momento.",

  // ── El asistente ──────────────────────────────────────────────────────
  "ia.titulo": "Pregunta lo que quieras",
  "ia.escribe": "Escribe tu pregunta",
  "ia.enviar": "Enviar",
  /*
    ══════════════════════════════════════════════════════════════════════
    NI «195» A MANO NI «VUELVE MAÑANA», Y LAS DOS ERAN MENTIRA A SU MANERA
    ══════════════════════════════════════════════════════════════════════

    · «las 195 fichas» estaba escrito a mano en los tres idiomas, fuera del
      verificador de cifras que existe justo para esto. El día que el catálogo
      crezca, estas tres frases dicen un número que ya no es. Ahora va `{n}` y
      lo rellena la pantalla con `FICHAS` de `@vc/contenido/cifras`, que es lo
      que ya hace la página de venta.

    · «Vuelve mañana» era falso, y falso de la peor manera: **la ventana es
      MÓVIL de 24 horas**, no el día natural. Se eligió móvil a propósito —con
      el día natural, quien agota su cuota a las 23:50 la recupera diez minutos
      después—, y el texto decía justo lo contrario de la razón por la que se
      eligió. Quien pregunta a las nueve de la noche y lee «vuelve mañana»
      vuelve a las nueve de la mañana y sigue sin poder.

      Lo que se dice ahora es lo que pasa: las preguntas se van liberando según
      cumplen 24 horas. Sin prometer una hora exacta, que tampoco la sabemos
      sin mirar cuándo preguntó cada una.
  */
  "ia.hay_que_entrar":
    "Para preguntar hace falta una cuenta. La biblioteca entera sigue abierta sin ella, con las {n} fichas y sus fuentes.",
  "ia.demasiadas":
    "Llegaste al límite de preguntas de las últimas 24 horas. Cada pregunta se vuelve a liberar al " +
    "cumplir un día. Mientras tanto, el ejercicio completo está en la biblioteca.",
  "ia.tope_del_dia":
    "El asistente llegó a su límite de las últimas 24 horas y ahora no puede contestar. La biblioteca " +
    "sigue completa, con los ejercicios y sus fuentes.",
  "ia.de_donde_sale": "De dónde sale esto",
  "ia.sin_configurar":
    "El asistente no está disponible ahora mismo. Mientras tanto, el catálogo entero está en la " +
    "biblioteca con sus fichas completas y sus fuentes.",

  // ── La procedencia. Se RENDERIZA, no se documenta. ────────────────────
  "origen.F": "Este número está en la fuente, tal cual.",
  "origen.F~": "Este número lo sacamos de la fuente, aplicando nuestro criterio.",
  "origen.N": "Este número lo decidimos nosotros. No hay una fuente que lo respalde.",
} as const;

export type Clave = keyof typeof ES;

/* ─────────────────────────────────────────────────────────────────────────
   Y LOS OTROS DOS, TOTALES SOBRE LAS MISMAS CLAVES
   ───────────────────────────────────────────────────────────────────────── */

/* 14/09/2026 · PASADA DE ADAPTACIÓN en/pt: el dueño avisó (13/09) «estás traduciendo al pie de la
   letra, en lugar de adaptarlo a la realidad del idioma». En este bloque (inglés de EE. UU.):
   contracciones en todo lo que se lee como frase (don't, it's, you're, we'll…); fuera los moldes
   del castellano («It is not that… it is that…», «That is time.» → «Time's up.», «How it looks» →
   «Appearance», pasivas tipo «How it is done» → «How to do it»); británico → US («towards» →
   «toward», «carry on» → «keep going/continue», «leave it empty» → «leave it blank», «a quarter of
   an hour» → «15 minutes»/«Fifteen minutes»); «rung» y «step» nombraban el mismo peldaño → «level»
   en todas (y «step» queda solo para los pasos de un ejercicio); «tickle» → «tingle», «gesture» →
   «movement», «signs» → «puts its name to»; `pie.legal` «does not substitute a professional
   consultation» → «isn't a substitute for seeing a professional». Sin cambios de sentido, claves ni
   huecos. Lo que decía antes está en git (commit `c281a7e`) y el criterio, en
   `_auditoria-premium/recorridos/criterios-adaptacion.md`. */
const EN: Record<Clave, string> = {
  "app.nombre": "Voice Comfort",
  "app.biblioteca": "Library",
  "app.mi_plan": "My plan",
  "app.ajustes": "Settings",
  "app.idioma": "Language",
  "app.cambiar_tema": "Change theme",
  "app.tema_claro": "Light",
  "app.tema_oscuro": "Dark",
  "app.tema_sistema": "Match my system",
  "app.volver": "Back",
  "app.empezar": "Start",
  "app.seguir": "Continue",
  "app.cargando": "…",
  "app.montando_plan": "Putting your plan together.",
  "app.montando_sesion": "Putting today's session together.",
  "app.buscando_cuenta": "Fetching your details.",
  "app.reintentar": "Try again",
  "app.cerrar": "Close",
  "fallo.no_se_pudo_titulo": "We couldn't check.",
  "fallo.no_se_pudo_texto":
    "This doesn't mean you don't have a plan. We just couldn't check right now. " +
    "Try again in a moment.",
  "fallo.sin_puerta_titulo": "We're missing your safety answers.",
  "fallo.sin_puerta_texto":
    "Your plan is there, but we can't show it to you until the questions are answered. " +
    "They only take a moment, and you only do them once.",

  /* 14/09: «does not diagnose» sin contraer, a propósito. El filtro de palabras sanitarias deja pasar la
     negación «does not diagnose» y no «doesn't diagnose»; en el pie legal, además, la forma plena pesa más. */
  "pie.legal":
    "Voice Comfort trains a skill. It isn't a medical device, it does not diagnose, and it isn't " +
    "a substitute for seeing a professional. When something needs a closer look, we'll tell you and " +
    "point you to the right person.",

  // ── El onboarding, entero ─────────────────────────────────────────
  "onb.bienvenida_titulo": "Do this for twenty seconds.",
  "onb.bienvenida_p1":
    "Close your lips without pressing and make a comfortable mmm, like you're agreeing with someone. " +
    "You should feel a tingle in your lips or nose.",
  "onb.bienvenida_p2":
    "That's it. That's a real voice exercise, and you just did it without signing up, without " +
    "paying, and without telling us anything.",
  "onb.bienvenida_aviso":
    "A few questions come next. Some are about safety and can't be skipped — not to cover ourselves, but " +
    "because some things shouldn't be practiced at home, and we need to know before we build you " +
    "anything.",
  "onb.seguir": "Continue",
  "onb.hilo_paso": "Step {n} of {total}",
  "onb.hilo.ejercicio": "One exercise",
  "onb.hilo.voz": "Microphone and voice",
  "onb.hilo.seguridad": "Safety",
  "onb.hilo.plan": "Your plan",
  "onb.contar_veinte": "Count the twenty seconds",
  "onb.veinte_otra_vez": "Count them again",
  "mov.referencia_prestada":
    "The pitch in the drawing is based on a general reference, not on your voice. Measure your voice in " +
    "“Your account”, with “Measure my voice”: it takes thirty seconds, and the drawing adjusts to you.",
  "mov.llegaron": "{n} of {total} words came through.",
  "mov.no_te_oimos":
    "We didn't hear you. That doesn't mean you did it wrong: no recording came through to compare.",
  "aviso.titulo": "Your plan is ready. One thing first.",
  "aviso.seguimiento":
    "You told us someone is already keeping an eye on your voice. What that person tells you always " +
    "comes before anything this platform says.",
  "aviso.plan_ajustado":
    "Your plan starts easy: the exercises that ask the most of your voice are left out. Not because you " +
    "can't handle more — now just isn't the time.",
  "aviso.seguir": "Got it, show me my plan",
  "onb.tesitura": "Your voice",
  "onb.tesitura_titulo": "Thirty seconds, and you'll know your range.",
  "onb.tesitura_texto":
    "First, read a sentence out loud the way you normally talk. Then do one comfortable " +
    "siren, without pushing. With those two, we know the pitch you actually speak at, and every " +
    "exercise adjusts to that instead of to a generic number.",
  "onb.tesitura_empezar": "Start",
  "onb.tesitura_lectura": "Read it out loud, like you're telling someone.",
  "onb.tesitura_frase":
    "This morning I left early and walked down to the corner store. It was chilly out, but nice. I picked " +
    "up the usual coffee and came back through the park.",
  // 16/09: más corta, se lee con un contador delante; lo que decía, en el castellano.
  "onb.tesitura_sirena": "Now say “ooh”, sliding up and down, gently.",
  "onb.tesitura_calculando": "Looking at what you did…",
  "onb.tesitura_lista":
    "Done. Your voice speaks at around {hz} Hz (how high or low it is) and moves comfortably across " +
    "{rango} semitones (a semitone is one piano key to the next).",
  "onb.tesitura_otra_vez": "Try again",
  "onb.tesitura_no_guardada":
    "We got the measurement, but we couldn't save it. Try again whenever you want.",
  "onb.tesitura_seguir_sin": "Continue without measuring",
  "onb.tesitura_pocas_muestras":
    "We didn't hear enough. Move closer to the microphone or speak up a little.",
  "onb.tesitura_sin_voz_clara":
    "The sound didn't come through clearly. That's usually background noise: try somewhere quieter.",
  "onb.tesitura_sin_recorrido":
    "The siren came out flat. Go a little higher and lower, still without pushing.",
  "onb.microfono_titulo": "We're about to ask for your microphone, so here's what we do with it.",
  "onb.microfono_texto":
    "It's only used inside your browser, to draw what you're doing as you do it. The audio " +
    "isn't uploaded or stored, and it never reaches our servers. The only things that " +
    "leave are numbers: how many seconds, how many semitones.",
  "onb.perfil_ayuda": "This decides which exercises you get, and how many.",
  "onb.de": "of",
  // ── Las trece de seguridad · ninguna nombra una dolencia ───────────
  "pregunta.esAdulto": "Are you 18 or older?",
  "pregunta.esAdulto.ayuda": "This platform is for adults only, and we say so up front.",
  "pregunta.dificultadParaRespirar": "Is it hard to breathe right now, or is there a new noise when you breathe in?",
  "pregunta.dificultadParaRespirar.ayuda": "A new whistle when you breathe in, or feeling short of breath right now. Your usual chest wheeze, or a sound when you breathe out, doesn't count here.",
  "pregunta.sangreAlToser": "Have you seen blood when you cough, or in anything you cough up?",
  "pregunta.sangreAlToser.ayuda": "Blood in what comes up from your throat or chest. From your nose or gums doesn't count.",
  "pregunta.reposoVocalIndicado": "Has someone told you not to use your voice right now?",
  "pregunta.reposoVocalIndicado.ayuda": "Voice rest ordered by a professional, right now.",
  "pregunta.cirugiaReciente": "Have you had surgery on your throat or vocal cords in the last three months?",
  "pregunta.cirugiaReciente.ayuda": "Throat, vocal cords, neck or thyroid. Teeth or nose don't count.",
  "pregunta.roncoMasDeTresSemanas": "Has your voice been different for more than three weeks?",
  "pregunta.roncoMasDeTresSemanas.ayuda": "With no cold or allergy that explains it.",
  "pregunta.dificultadAlTragar": "Is it hard to swallow, or is there something in your throat that won't go away?",
  "pregunta.dificultadAlTragar.ayuda": "More than three weeks, and with no cold or sore throat to explain it.",
  "pregunta.dolorIrradiadoAlOido": "Does it hurt to speak or swallow, and does the pain reach your ear?",
  "pregunta.dolorIrradiadoAlOido.ayuda": "More than three weeks, and with no sore throat or ear infection to explain it.",
  "pregunta.bultoEnElCuello": "Have you noticed a lump in your neck?",
  "pregunta.bultoEnElCuello.ayuda": "One that's still there after three weeks. The ones that come up with a cold and go down on their own don't count.",
  "pregunta.perdidaRepentinaDeVoz": "Have you suddenly lost your voice completely?",
  "pregunta.perdidaRepentinaDeVoz.ayuda": "And it's still gone. If it came back on its own in a day or two and hasn't happened again, it doesn't count.",
  "pregunta.enSeguimientoProfesional": "Are you currently being seen by a voice specialist or an ENT?",
  "pregunta.enSeguimientoProfesional.ayuda": "That's not a problem: it changes how we build your plan.",
  "pregunta.diagnosticoNeurologico": "Are you being seen for anything neurological, or for your thyroid?",
  "pregunta.diagnosticoNeurologico.ayuda": "Parkinson\u2019s, stroke, ALS, an underactive thyroid. That's not a problem either: it adjusts the plan.",
  "pregunta.viaAerea": "Do you use an inhaler, have allergies right now, get heartburn, or sleep with a breathing machine?",
  "pregunta.viaAerea.ayuda": "Any of the four. It changes which exercises we offer you.",
  // ── Los siete perfiles ────────────────────────────────────────────
  "perfil.A1_carga_sostenida": "I talk all day",
  "perfil.A1_carga_sostenida.sub": "Teaching, front desk, healthcare, guiding",
  "perfil.A2_rendimiento": "I sing, act or narrate",
  "perfil.A2_rendimiento.sub": "My voice is the tool I work with",
  "perfil.A3_voz_conectada": "I'm on calls and mics all day",
  "perfil.A3_voz_conectada.sub": "Video calls, podcasts, live, online teaching",
  "perfil.A4_post_clinico": "I'm starting again after seeing a specialist",
  "perfil.A4_post_clinico.sub": "Done with speech and voice sessions, or keeping it up after surgery",
  "perfil.A5_voz_que_envejece": "My voice has lost power",
  "perfil.A5_voz_que_envejece.sub": "It's changed with age, and someone has checked it",
  "perfil.A6_exploracion": "I want my voice to sound different",
  "perfil.A6_exploracion.sub": "Exploring how it sounds, and whether it sounds like me",
  "perfil.A7_tension": "I catch myself tensing up",
  "perfil.A7_tension.sub": "A knot in the throat, tension, stage fright",
  // ── La portada de la aplicación ───────────────────────────────────
  "portada.etiqueta": "Spoken voice · for adults",
  // 16/09: la misma frase que la página de venta, que es la que el dueño aprobó (ver el castellano).
  "portada.titulo": "Your voice can be trained, too.",
  "portada.cuerpo":
    "We ask a few questions so we can pick, out of {n} exercises, the {min} to {max} that are right for " +
    "you. You'll see how much of each, where that number comes from, and when to stop.",
  "portada.ver_ejercicios": "See the exercises",
  // ── La aplicación, lo que quedaba escrito a mano ──────────────────
  "lib.titulo": "The library",
  "lib.cuerpo":
    "Everything we have, whether or not it's in your plan. Your plan came from your " +
    "answers; this is everything else.",
  "lib.sin_revisar_corto": "Drawings not yet reviewed",
  "lib.sin_carga": "asks nothing of your voice",
  "lib.carga_baja": "low load",
  "lib.carga_media": "medium load",
  "lib.carga_alta": "high load",

  "lib.buscar": "Search for an exercise",
  "lib.familia": "Family",
  "lib.todas_las_familias": "All families",
  "lib.filtro_sin_carga": "Easy on your voice",
  "lib.filtro_calladas": "Silent exercises",
  "lib.limpiar": "Clear the filters",
  "lib.cuantas": "{n} of {total}",
  "lib.nada": "Nothing matches. Try fewer filters or a different word.",

  "mov.hoy_ajustado":
    "Less today than the {base} on the exercise page, because of the level you're on. The amount goes up " +
    "on its own.",
  "ia.aviso":
    "It only talks about what's in our catalog, and when it doesn't know, it says so. It has no " +
    "opinion on how your voice sounds: it can't hear you, and even if it could, that wouldn't be " +
    "its job.",
  "mov.necesitas": "What you need",
  "mov.necesitas_espejo": "A mirror.",
  "mov.necesitas_silencio": "A quiet place.",
  "mov.asi_suena": "This is how it sounds",
  "mov.escuchar": "Listen",
  "mov.parar_de_escuchar": "Stop",
  "mov.que_te_lo_cuenten": "Have it read to you",
  "mov.que_te_lo_cuenten_pie": "A voice reads the steps out loud, so you don't have to look at the screen.",
  "mov.antes_de_escucharte": "Before you listen back",
  "mov.guion_de_la_escucha":
    "Your recorded voice will sound strange. That happens to almost everyone: when you talk, you also " +
    "hear yourself through the bones in your head, and a recording doesn't pick that up. This isn't about " +
    "liking your own voice. Listen for one thing only: which one is easier to understand?",
  "mov.que_mide": "What this measures, and what it doesn't",
  "mov.por_dentro": "What happens inside",
  "mov.en_este_para_si": "In this one, stop if…",
  "mov.como_se_hace": "How to do it",
  "mov.lo_que_te_dices": "What to tell yourself while you do it",
  "mov.es_imagen": "A picture to help you, not real anatomy",
  "mov.en_que_se_falla": "Where people usually go wrong",
  "mov.como_lo_notas": "How you can tell",
  "mov.como_se_arregla": "How to fix it",
  "mov.para_y_consulta": "Stop and get it checked if…",
  "mov.que_salio": "What measuring it showed",
  "mov.a_favor": "In favor",
  "mov.en_contra": "Against",
  "mov.quien_lo_propone": "Who proposes it",
  "mov.ver_la_fuente": "See the source",
  "mov.ver_las_fuentes": "See the sources",
  "mov.sin_atribucion":
    "We haven't found anyone who describes it this way. Plenty of people teach this movement, but we " +
    "couldn't find a published source behind it. We include it anyway, and we're upfront about that.",
  "mov.solo_manual":
    "It's described in the manual this project grew out of, and nowhere else we've found.",
  "mov.si_no_te_sale": "If you can't get it",
  "mov.tope":
    "Limit: {min} min, and the timer stops you. More isn't better, and that limit comes from a measured " +
    "number.",
  "mov.que_es_la_marca":
    "The letters next to each number tell you where it comes from. Tap them to see.",
  "sesion.sin_plan_titulo": "You don't have a plan yet.",
  "sesion.sin_plan_texto":
    "Your plan gets built after the questions. There are {n}, and they can't be skipped: some " +
    "things shouldn't be practiced at home, and we need to know before we build you anything. If " +
    "you already answered them on another device, those answers stayed there and don't carry over " +
    "to this account.",
  "escena.alt":
    "A chart of how the pitch goes up and down in this exercise. The full instructions are written out " +
    "below, in the steps.",
  "sello.sin_revisar":
    "The drawings and video on this page haven't been reviewed by a professional yet. We're telling " +
    "you here because we think you should know.",
  "sello.revisado": "Drawings and video reviewed by {quien}.",
  "mic.sin_https":
    "The microphone can't be used without a secure connection. That's a browser rule, not ours.",
  "mic.denegado":
    "We don't have your microphone. You can still do the exercises: the only thing you lose is seeing " +
    "your voice on screen while you do them.",
  // 16/09: línea de ayuda corta, no una etiqueta que compita con lo que hay que leer (ver el castellano).
  "mic.escuchando": "This is just so you can see yourself while you speak. It isn't a score.",
  "mic.en_reposo": "You'll see your voice here as you speak: the higher the pitch, the higher the line.",
  "mic.pidiendo": "Asking the browser for the microphone…",
  "temp.se_acabo": "Time's up.",
  "temp.tope_duro": "This exercise has a limit of {t}, and there's no extra time.",
  "temp.al_medirlo": "When this was measured, going past that point left voices tighter and less clear.",
  "temp.lo_de_hoy": "You've done today's part of this exercise.",
  "temp.parado":
    "Stopped. What you did is saved, and that's fine: stopping when you need to is part of the exercise, " +
    "not a failure.",
  "temp.parar": "Stop",
  "temp.otra_vez": "Start again",
  "lamina.donde.titulo": "The places you can feel for yourself",
  "lamina.donde.nariz":
    "Your nose. Pinch it shut with two fingers while you make the sound: if the sound changes, some of it was going out that way",
  "lamina.donde.labios": "Your lips",
  "lamina.donde.dientes": "Your top teeth",
  "lamina.donde.reborde":
    "The ridge just behind your top teeth. Touch it with the tip of your tongue",
  "lamina.donde.fondo":
    "The back of your mouth. Watch it in a mirror while you yawn: it gets wider",
  "lamina.donde.lengua": "Your tongue",
  "lamina.donde.bultito": "The little bump on your neck that goes up and down when you swallow",
  "lamina.donde.cuello":
    "The front of your neck. Put two fingers there: it buzzes when you make a sound and stays still when " +
    "it's only air",
  "lamina.donde.pecho": "The middle of your chest. Put an open hand there and hum a low “mmm”: it tingles",
  "lamina.donde.aire": "The air comes OUT here. Hold your palm a hand's width away and you'll feel it",
  "lamina.mandibula.titulo": "Your jaw and its hinge, seen from the front",
  "lamina.mandibula.bisagra":
    "The two hinges, just in front of your ear openings. That's where your jaw pivots, not at the chin",
  "lamina.mandibula.masetero":
    "The muscle that bulges in your cheek when you clench your teeth",
  "lamina.mandibula.arcada":
    "The arch, the whole horseshoe. It's a single piece with two hinges",
  "lamina.mandibula.pomulo": "Your cheekbone. That muscle starts just below here",
  "lamina.mandibula.caida": "Your jaw DROPS under its own weight, pivoting on the hinges",
  "lamina.mandibula.adelantar":
    "Pushing your chin forward isn't releasing: it's clenching somewhere else",
  "lamina.cuello.titulo": "Your neck and shoulders, seen from the front",
  "lamina.cuello.hioides":
    "A small floating bone: the only one that doesn't touch any other. Your tongue sits on top of it, and " +
    "what rises when you swallow hangs underneath",
  "lamina.cuello.esternocleido":
    "The two muscles running from behind your ear to the flat bone in your chest. They stand out when you turn your head",
  "lamina.cuello.trapecio": "The muscle running from your neck to your shoulder",
  "lamina.cuello.laringe": "The little bump that goes up and down when you swallow, hanging from that bone",
  "lamina.cuello.hombro": "Your collarbone and shoulder",
  "lamina.cuello.caen": "Your shoulders DROP. The move is to release, never to hunch",
  "lamina.cuello.sujetar":
    "With your head pushed forward, these muscles start holding it up. And they're the same ones you " +
    "want soft while you speak",
  "lamina.lengua.titulo": "Your whole tongue, seen from the side and inside",
  "lamina.lengua.punta": "The tip. It's what trills the R and makes L and N",
  "lamina.lengua.alveolos":
    "The ridge JUST BEHIND your top teeth. The ridge, not the teeth themselves",
  "lamina.lengua.dorso": "The middle part. It's what rises toward the back of the roof of your mouth for NG",
  "lamina.lengua.raiz":
    "The root. It's two thirds of your tongue, it doesn't show in a mirror, and it reaches down to the " +
    "floating bone",
  "lamina.lengua.suelo": "The floor of your mouth, underneath. It's what loosens when you release your jaw",
  "lamina.lengua.hioides": "The floating bone the root hangs from",
  "lamina.lengua.adelante": "When you bring the sound forward, your tongue COMES FORWARD",
  "lamina.lengua.retraer":
    "The base pulls back to help close things off. That's a crutch you almost never need",
  "lamina.caja.titulo": "Your ribcage and the air, seen from the front",
  "lamina.caja.subglotica": "This is the pressure that matters: just BELOW your vocal cords, at the top of your windpipe",
  "lamina.caja.traquea": "Your windpipe, the tube going down to your chest",
  "lamina.caja.costillas": "Your ribs. The box opens on both sides at once",
  "lamina.caja.diafragma":
    "The dome that sits under your ribs. It's a DOME, not a belt: at rest it sits high and curves upward",
  "lamina.caja.tripa": "Your belly wall. What you see moving there isn't the air",
  "lamina.caja.abren": "The ribs OPEN sideways, not forward",
  "lamina.caja.baja": "When you breathe in, the dome GOES DOWN and pushes on what's below",
  "lamina.caja.sale": "And the air GOES OUT upward",
  "lamina.caja.empujar":
    "Pushing with your belly doesn't raise the pressure up top. It just turns into clenching",
  "lamina.labios.titulo": "Your lips and the outlet, seen close up from the front",
  "lamina.labios.cierre": "The seal between both lips: M, B and P",
  "lamina.labios.labio_diente": "Bottom lip against top teeth: F and V",
  "lamina.labios.frunce": "Purse them and the way out narrows, without anything touching",
  "lamina.labios.comisuras": "The corners. This is where air escapes if they don't close",
  "lamina.labios.mejilla": "Your cheek, the side wall. Neither puffed nor clenched",
  "lamina.labios.cigomaticos": "The muscles that pull the corner up and back",
  "lamina.labios.salida": "The air goes out THROUGH THE MIDDLE, and that's the whole idea",
  "lamina.labios.escape":
    "If it escapes at a corner, there's no back-pressure, and the exercise stops working",
  // ── El planner ────────────────────────────────────────────────────
  "planner.etiqueta": "Your plan",
  "planner.titulo": "What's coming, and why.",
  "planner.peldano_es": "You're on level {n}.",
  "planner.que_es_el_peldano":
    "Your level is how much each exercise asks of you: at 0 you do exactly what the exercise page says, " +
    "and below 0 it asks for less. It isn't a grade.",
  "planner.por_progreso": "for what you've practiced — {n} session | for what you've practiced — {n} sessions",
  "planner.por_carga": "for your voice load — {h}",
  "planner.por_que_resta":
    "Yes: your voice load LOWERS your level. If you talk for hours, your voice has already done its work " +
    "for the day, and asking for more is exactly what tires it out. It isn't that we give you less: it's " +
    "that your talking day already counts.",
  "planner.falta_para_subir": "It goes up a level next session. | It goes up a level in {n} sessions.",
  "planner.en_el_techo":
    "This is the top. From here on, what changes is the variety, not the amount.",
  "planner.lo_que_viene": "The next six",
  "planner.sin_fechas":
    "There are no dates. It moves by sessions, so if you can't make it one day, you don't lose anything.",
  "planner.sesion_n": "Session {n}",
  "planner.forma": "{min} min · {n} exercise | {min} min · {n} exercises",
  "planner.descanso": "Rest",
  "planner.sube_aqui": "Your level goes up here",
  "planner.hacer_hoy": "Do this one",
  "planner.que_trae": "What's in it",
  "planner.tus_ejercicios": "Your exercise | Your {n} exercises",
  "planner.tus_ejercicios_ayuda":
    "The ones coming up in your next twelve sessions, and how many times. Each one is in the " +
    "library in full.",
  // 16/09: without access the library doesn't open; the why is in the Spanish block.
  "planner.tus_ejercicios_ayuda_sin_acceso": "The ones coming up in your next twelve sessions, and how many times.",
  "planner.veces": "{n} time | {n} times",
  "planner.ajustar_titulo": "If your day has changed",
  "planner.ajustar_ayuda":
    "Change your time or your voice hours and the plan is rebuilt starting next session. What you've " +
    "done stays, and you keep your level.",
  "planner.ajustado": "Done. Your next session uses this.",
  "planner.sin_racha":
    "There's no streak to break here and no percentage to complete. Look all you like: there isn't one, " +
    "and that's on purpose.",
  "app.planner": "My plan",
  // ── Las dos últimas del onboarding ────────────────────────────────
  "onb.horas_titulo": "How many hours a day do you use your voice?",
  "onb.horas_ayuda":
    "Count work and everything else. This one changes your plan the most, and not in " +
    "the direction you'd expect.",
  "horas.poco": "Less than two",
  "horas.poco.sub": "A call here and there, not much else",
  "horas.algo": "Between two and four",
  "horas.algo.sub": "Meetings most days",
  "horas.bastante": "Between four and six",
  "horas.bastante.sub": "Half the day talking",
  "horas.muchas": "More than six",
  "horas.muchas.sub": "Teaching, front desk, guiding: all day",
  "onb.rato_titulo": "How long do you have each day?",
  "onb.rato_ayuda":
    "If in doubt, go short. It's a hard limit: the timer stops there and doesn't ask if you " +
    "want one more minute.",
  "rato.bolsillo": "Three minutes",
  "rato.bolsillo.sub": "In the car, before heading in",
  "rato.corta": "Seven minutes",
  "rato.corta.sub": "A gap between things",
  "rato.media": "Fifteen minutes",
  "rato.media.sub": "What most people do",
  "rato.larga": "Twenty-five minutes",
  "rato.larga.sub": "Plenty of time, no rush",
  // ── El cuadro de la persona, los motivos y los compromisos (17/09) ─────────────────────────────────────────
  "onb.senales_titulo": "What do you notice in your voice?",
  "onb.senales_ayuda": "Check everything that applies. This decides which exercises come first.",
  "senal.cansancio": "It gets tired by the end of the day",
  "senal.ronquera": "Hoarseness or throat clearing",
  "senal.aire": "I run out of breath when I talk",
  "senal.volumen": "People can't hear me",
  "senal.tension": "I squeeze, or talking takes effort",
  "senal.quiebres": "It cracks, or won't go up",
  "senal.claridad": "People don't understand me",
  "senal.nervios": "Nerves when speaking in public",
  "senal.ninguna": "None of these",
  "onb.objetivo_titulo": "What do you want to get out of this?",
  "onb.objetivo_ayuda": "Just one. It's what weighs most when we build your plan.",
  "objetivo.aguantar": "Last the whole day",
  "objetivo.proyectar": "Be heard without effort",
  "objetivo.claro": "Speak more clearly",
  "objetivo.soltar": "Let go of the tension",
  "objetivo.rango": "More range or agility",
  "objetivo.mantener": "Keep what I've gained",
  "objetivo.explorar": "Explore how it sounds",
  "onb.donde_titulo": "Where will you practice most of the time?",
  "onb.donde_ayuda": "The usual place. Any day you can still mark “I can't make noise today”.",
  "donde.casa": "At home, alone",
  "donde.gente_cerca": "With people nearby",
  "donde.gente_cerca.sub": "Office, family, thin walls",
  "donde.carro": "In the car",
  "donde.cambia": "It changes every day",
  "onb.experiencia_titulo": "Have you done voice exercises before?",
  "onb.experiencia_ayuda": "So we don't start you on what takes an ear or prior technique.",
  "experiencia.nunca": "Never",
  "experiencia.algo": "A bit, on my own",
  "experiencia.profesional": "With a professional, or I sing",
  "onb.tubito_titulo": "Do you have a wide straw handy?",
  "onb.tubito_ayuda": "A milkshake straw. Fifteen exercises use one; without it, your plan leaves them out and tells you.",
  "onb.tubito_si": "Yes, I do",
  "onb.tubito_no": "No, I don't",
  "motivo.rotacion": "For variety",
  "motivo.etapa.preparacion": "To get started",
  "motivo.etapa.calentamiento": "To warm up your voice",
  "motivo.etapa.enfriamiento": "To wind down the session",
  "motivo.sin_voz": "It asks nothing of your voice",
  "motivo.senal.cansancio": "Because your voice gets tired by the end of the day",
  "motivo.senal.ronquera": "For the hoarseness or throat clearing",
  "motivo.senal.aire": "Because you run out of breath",
  "motivo.senal.volumen": "Because people can't hear you",
  "motivo.senal.tension": "Because you notice you squeeze",
  "motivo.senal.quiebres": "Because your voice cracks or won't go up",
  "motivo.senal.claridad": "Because people don't understand you",
  "motivo.senal.nervios": "For the nerves when speaking in public",
  "motivo.objetivo.aguantar": "To last the whole day",
  "motivo.objetivo.proyectar": "To be heard without effort",
  "motivo.objetivo.claro": "To speak more clearly",
  "motivo.objetivo.soltar": "To let go of the tension",
  "motivo.objetivo.rango": "To gain range and agility",
  "motivo.objetivo.mantener": "To keep what you've gained",
  "motivo.objetivo.explorar": "To explore how it sounds",
  "motivo.donde.gente_cerca": "Because you practice with people nearby",
  "motivo.donde.carro": "Because you practice in the car",
  "compromiso.sin_tubito": "With a wide straw, {n} more exercises would come in. Change it in “My plan”.",
  "compromiso.techo": "{n} more exercises come in from session {s}: they take an ear or technique, and they arrive when it's time.",
  "planner.tubito": "Do you have a wide straw?",
  "planner.donde": "Where do you practice most of the time?",
  // ── Lo que iba pegado a una llave y por eso no se veía ────────────
  "sesion.numero": "session {n}",
  "sesion.minutos": "{n} minute | {n} minutes",
  "sesion.voz_cansada_hoy": "My voice is tired today",
  "sesion.sin_ruido_hoy": "I can't make noise today",
  "sesion.sin_ruido_explicado":
    "The ones you can hear from across the room are out, and the quiet ones come first. " +
    "The plan stays whole: nothing is canceled.",
  "sesion.min": "{n} min",
  "sesion.hechos_hoy": "{n} of {total} done today",
  "sesion.seguir": "Continue",
  "sesion.terminada_titulo": "That's it. Session done.",
  "sesion.terminada_texto":
    "It's saved, and it moves you toward the next level. Don't do another one today: two in a row don't " +
    "add up — they get in each other's way.",
  "sesion.hoy_descanso": "Today, rest.",
  "sesion.me_equivoque": "My mistake — my voice is fine",

  "sesion.hoy_si_puedes": "If you feel like it, these are fine today",
  "sesion.hoy_si_puedes_ayuda":
    "{n} that ask nothing of your voice: breathing, loosening your neck, posture. " +
    "They don't count as a session and don't change your level.",
  "sesion.cortado_por_el_tope": "cut off by the limit",
  "sesion.hecho": "done",
  "sesion.silencioso": "silent",
  "sesion.parado_a": "stopped at {t}",
  "sesion.a_medias_titulo": "That's enough for today.",
  "sesion.a_medias_texto":
    "You practiced {hecho} of {total}. It's saved, but under half doesn't move you up a level: next time " +
    "you'll get this same session, and there's nothing to catch up on. Don't do another one today.",
  "sesion.siguiente": "Next: {nombre}",
  "sesion.ver_la_sesion": "See how the session went",
  "sesion.hoy_no_puedo_este": "I can't do this one today",
  // ── La cuenta ─────────────────────────────────────────────────────
  /* 16/09: «Save» / «Save this plan and come back anytime.» / «You already have it…». Signing up is
     how you get to practice now; the why is in the Spanish block. */
  "cuenta.guardar": "Create account",
  "cuenta.guardar_titulo": "Create your account to start practicing.",
  "cuenta.guardar_ayuda":
    "To practice your plan, you need access: enter your access code here, or subscribe below.",
  "cuenta.entrar_titulo": "Back to your plan.",
  /* 15/09/2026: sign-in by code. See the Spanish block for what left and why. */
  "cuenta.entrar_ayuda": "Enter your email and we'll send you a code to sign in. No password needed.",
  "cuenta.mandar_codigo": "Send me the code",
  "cuenta.codigo_titulo": "Enter the code from your email.",
  "cuenta.codigo_texto_nueva": "We've sent a six-digit number to {correo}.",
  "cuenta.codigo_texto": "If {correo} has an account, a six-digit number is on its way.",
  "cuenta.codigo_campo": "Code",
  "cuenta.codigo_caduca": "Six digits, good for ten minutes.",
  "cuenta.codigo_otro": "Send me another code",
  "cuenta.codigo_otro_en": "You can ask for another in {s} s",
  "cuenta.codigo_otro_mandado": "We've sent another one. The previous code no longer works.",
  "cuenta.codigo_no_vale":
    "That code doesn't work. It may be mistyped, expired or already used: ask for another if you need to.",
  "cuenta.otro_correo": "Use a different email",
  "cuenta.ya_tengo": "I already have an account",
  "cuenta.no_tengo": "I don't have an account yet",
  "cuenta.mira_spam": "If it doesn't arrive in a couple of minutes, check your spam folder.",
  "cuenta.codigo_va_con_la_cuenta":
    "The access code is tied to this account: you'll see it under “Your account” once you're in.",
  "cuenta.error_adulto": "You need to confirm you're 18 or older.",
  "cuenta.demasiados_intentos":
    "Too many tries in a row. Wait 15 minutes and try again: we've locked sign-in for a bit to keep your " +
    "account safe.",
  "cuenta.bloqueada":
    "Your code is correct — we're the ones who closed this account. Message us on WhatsApp or at the help " +
    "email below and we'll look into it.",
  "cuenta.error_generico": "That didn't work. Try again in a moment.",
  // 16/09: «Right now this plan only lives in this browser. Save it…»; the why is in the Spanish block.
  "planner.guardalo":
    "To practice this plan, create your account: that's where you enter your access code or subscribe. " +
    "Your plan follows you to your phone.",
  // ── El gesto dibujado ─────────────────────────────────────────────
  "gesto.esquema": "A diagram, not a photo",
  "gesto.asi_si": "Like this",
  "gesto.asi_no": "Not like this",
  "gesto.paso": "Step",
  // ── Las familias del catálogo, y lo que quedaba suelto ────────────
  "familia.respiracion": "Breathing and support",
  "familia.sovt_estable": "Half-open mouth",
  "familia.sovt_fluctuante": "Half-open mouth with vibration",
  "familia.resonancia": "Resonance",
  "familia.articulacion": "Articulation and diction",
  "familia.rango": "Range and flexibility",
  "familia.proyeccion": "Power and projection",
  "familia.relajacion": "Release and posture",
  "familia.calentamiento": "Warm-up",
  "familia.enfriamiento": "Cool-down",
  "familia.higiene": "Habits",
  "familia.entorno": "Surroundings",
  "familia.evaluacion": "Self-check",
  "familia.voz_profesional": "Working voice",
  "familia.escucha": "Listening and ear",
  "familia.habla_clara": "Clear speech",
  "familia.protocolo": "Published sequence",
  "familia.contexto": "For a specific moment",
  "escena.grave": "low",
  "escena.agudo": "high",
  "temp.empezar": "Start",
  "temp.quedan": "{t} left",
  "mov.cuanto": "How much",
  "mov.repeticiones": "{n} rep | {n} reps",
  // ── El tubo con dos puertas ───────────────────────────────────────
  "tubo.esquema": "A diagram, not a photo",
  "tubo.titulo": "Why a half-closed mouth takes care of your voice.",
  "tubo.abierto": "Mouth open",
  "tubo.estrecho": "Narrow way out",
  "tubo.puerta_arriba": "The top door: your lips, or the straw",
  "tubo.puerta_abajo": "The bottom door: where the voice starts",
  "tubo.aire": "The air",
  "tubo.choca_fuerte": "The bottom door bangs hard.",
  "tubo.choca_suave": "The bottom door closes gently.",
  "tubo.moraleja":
    "If you narrow the way out, air builds up inside and pushes back down. So the bottom door doesn't " +
    "have to slam shut to make the same sound.",
  "app.secciones": "Sections",
  "app.ajustes_nav": "Settings",
  "gesto.anterior": "Previous step",
  "gesto.siguiente": "Next step",
  "onb.antes_de_nada": "First of all",
  "onb.el_microfono": "The microphone",
  "onb.seguridad": "Safety",
  "onb.lo_ultimo": "Last one",
  "onb.si": "Yes",
  "onb.no": "No",
  "onb.conceder": "Allow the microphone",
  "onb.ahora_no": "Not now",
  "onb.sin_microfono_seguimos":
    "The browser didn't let us use the microphone. We'll keep going anyway: you can measure your " +
    "voice anytime from “Your account”, and your plan will adjust to it.",
  "onb.que_te_trae": "What brings you here?",
  "onb.ver_mi_plan": "See my plan",
  "onb.montando": "Building your plan…",

  // ── Lo que la persona contó, tal y como se lo devolvemos ──────────
  "motivo.R-MENOR": "This platform is for adults only.",
  "motivo.R-RESPIRACION": "Trouble breathing, or a noise on the way in.",
  "motivo.R-SANGRE": "Blood when coughing, or in something you coughed up.",
  "motivo.R-REPOSO": "Voice rest has been ordered by a professional.",
  "motivo.R-CIRUGIA-3M": "Surgery on the throat or vocal cords in the last three months.",
  "motivo.R-DISFAGIA": "Trouble swallowing, or something in the throat that won't go away.",
  "motivo.R-OTALGIA": "Pain when speaking or swallowing that reaches the ear.",
  "motivo.R-BULTO": "A lump in the neck.",
  "motivo.R-AFONIA-SUBITA": "Sudden and complete loss of voice.",
  "puerta.no_hay_plan": "We're not going to build you a plan.",
  "puerta.me_equivoque": "I answered something wrong. Take me back to the questions",
  "puerta.lo_que_contaste": "What you told us:",
  "cierre.clinico.porque":
    "We're not saying something's wrong with you. We're saying someone who can see you in person needs " +
    "to take a look, and we can't. A set of exercises on top of that doesn't help: it just covers it up.",
  "cierre.clinico.vuelve":
    "If they take a look and tell you it's OK to practice, come back anytime. We haven't " +
    "charged you anything, and we haven't left a half-built plan waiting.",
  "cierre.edad.porque":
    "This isn't about your voice. It's a house rule: the platform is for adults only, and " +
    "we'd rather tell you here than after charging you.",
  "cierre.edad.vuelve":
    "Come back when you turn eighteen. We haven't charged you anything, and we haven't left a " +
    "half-built plan waiting.",
  // ── Cuánta prisa corre · tarjetas al estilo NHS ────────────────────
  "urgencia.ahora": "Go to the ER now.",
  "urgencia.ahora_texto": "Don't wait until tomorrow, and don't drive yourself. This needs to be checked today, right now.",
  "urgencia.hoy": "Get seen today.",
  "urgencia.hoy_texto": "Call your doctor or an urgent care clinic today. Today, not this week.",
  "urgencia.esta_semana": "Make an appointment this week.",
  "urgencia.esta_semana_texto":
    "It's not an emergency for today, but it's not a “whenever I remember” either. This needs to be " +
    "looked at in the next few weeks, not months.",
  "urgencia.cuando_puedas": "Mention it at your next appointment.",
  "urgencia.cuando_puedas_texto": "There's no rush, but whoever sees you should know.",
  "urgencia.no_molestas":
    "You're not wasting anyone\u2019s time by asking to have it checked. That's exactly what " +
    "it's for.",
  "urgencia.numero": "The emergency number where you are is {n}.",
  "urgencia.mientras_tanto":
    "In the meantime, you can keep going here. We'll take the load off your voice and " +
    "teach you to use it without pushing.",
  "urgencia.sigue_aqui": "Keep going with my plan",
  "puerta.urgencias": "Go to the ER.",
  "puerta.medico": "Make an appointment with your doctor.",
  "puerta.otorrino": "Make an appointment with an ENT specialist.",
  "puerta.quien_indico_reposo": "Talk to whoever ordered the voice rest before practicing anything.",
  "puerta.quien_opero": "Talk to whoever operated on you before practicing anything.",

  "bandera.paro_aqui": "I'm going to stop here for a moment.",
  "bandera.no_es_lo_mio":
    "You've described **{que}**, and that's not something I can help with through voice " +
    "exercises. I'm not saying something's wrong with you: I'm saying someone who can see you in " +
    "person needs to look at it, and I can't.",
  "bandera.aqui_estare":
    "Once they've looked at it and told you it's OK to practice, I'll be here.",

  "bandera.respiracion": "trouble breathing",
  "bandera.sangre": "blood when you cough",
  "bandera.bulto": "a lump in your neck",
  "bandera.tragar": "trouble swallowing",
  "bandera.oido": "pain when speaking or swallowing that reaches your ear",
  "bandera.ronquera": "a change in your voice that has lasted weeks",
  "bandera.sin_voz": "a sudden loss of your voice",
  "bandera.dolor": "pain when speaking that doesn't go away",

  "asistente.no_lo_se":
    "I don't know that one. I can only talk about what's in our catalog, and that isn't in it. If " +
    "something about your voice is worrying you, see someone who can listen to you in person.",
  "asistente.no_puedo_ahora":
    "I can't answer right now. That's on us, not on you. In the meantime, the library has the whole " +
    "catalog, with every exercise in full.",

  "ficha.cuanto": "How much",
  "ficha.que_pasa_por_dentro": "What happens inside",
  "ficha.como_se_hace": "How to do it",
  "ficha.lo_que_te_dices": "What to tell yourself while you do it",
  "ficha.en_que_se_falla": "Where people usually go wrong",
  "ficha.para_y_consulta": "Stop and get it checked if…",
  "ficha.que_salio_al_medirlo": "What measuring it showed",
  "ficha.quien_lo_propone": "Who proposes it",
  "ficha.si_no_te_sale": "If you can't get it",
  "ficha.a_favor": "In favor",
  "ficha.en_contra": "Against",
  "ficha.como_lo_notas": "How you can tell",
  "ficha.como_se_arregla": "How to fix it",
  "ficha.es_una_imagen": "A picture to help you, not real anatomy",
  "ficha.sin_fuente":
    "We haven't found anyone who describes it this way. Plenty of people teach this movement, but we " +
    "couldn't find a published source behind it. We include it anyway, and we're upfront about that.",
  "ficha.solo_el_manual": "It's described in the manual this project grew out of, and nowhere else we've found.",
  "ficha.sin_revisar":
    "The drawings and video for this exercise haven't been reviewed by a professional yet. We're " +
    "telling you here because we think you should know.",

  "reloj.parar": "Stop",
  "reloj.se_acabo": "Time's up.",
  "reloj.hay_tope": "When this was measured, going past that point left voices tighter and less clear.",
  "reloj.hiciste_lo_que_tocaba": "You've done today's part.",
  "reloj.parado":
    "Stopped. What you did is saved, and that's fine: stopping when you need to is part of the exercise, " +
    "not a failure.",

  "mic.grave": "low",
  "mic.agudo": "high",

  "sesion.de_hoy": "Today's session",
  "sesion.preparacion": "Preparation",
  "sesion.calentamiento": "Warm-up",
  "sesion.trabajo": "Work",
  "sesion.enfriamiento": "Cool-down",
  "sesion.descanso_voz_cansada":
    "Today is a rest day. You said so and the plan listened: easing off when your voice is tired is " +
    "part of training, not a break from it.",
  "sesion.descanso_reciente":
    "You did a session recently. Two in a row don't add up: they get in each other's way. Come back " +
    "in a few hours.",
  "sesion.descanso_cadencia": "Nothing today. Rest is part of the plan.",
  "sesion.descanso_sin_presupuesto":
    "The length you picked isn't enough for a full session with a warm-up and a cool-down, and those two " +
    "can't be skipped. Pick a longer one.",

  "cuenta.correo": "Email",
  "cuenta.crear": "Create account",
  "cuenta.entrar": "Sign in",
  "cuenta.salir": "Sign out",
  "cuenta.soy_adulto": "I'm 18 or older",
  "cuenta.borrar": "Delete my account",
  "cuenta.borrar_aviso":
    "Your account is deleted for good, and it can't be undone. Your answers to the safety questions go " +
    "with it.",
  "cuenta.borrar_teclea": "Type {p} to confirm",
  "precio.al_ano": "per year",
  "correo.confirmado_titulo": "You're in.",
  "correo.confirmado_texto":
    "Your email is confirmed and you're signed in. Taking you to your plan now.",
  "correo.confirmado_boton": "Go to my plan",
  "correo.confirmado_de_por_vida": "Your code is already applied: you have all of Voice Comfort, for life, with no payments or renewals.",
  "correo.confirmado_regalada": "Your code is already applied: you have all of Voice Comfort, without paying.",
  "cuenta.codigo_opcional": "Have an access code? (optional)",
  "cuenta.codigo_ayuda": "Type it exactly as you got it, hyphens and all. If you don't have one, leave it blank.",
  "cuenta.error_codigo":
    "That code isn't valid, or it's already been used. Your account wasn't created: try again with the " +
    "right code, or leave the code box empty.",
  "cuenta.acepto": "I accept the {a} and the {b}.",
  "cuenta.aceptoUno": "I accept the {a}.",
  "cuenta.legalEnOtroIdioma": "It's published in another language, and that's the version you're accepting.",
  "cuenta.legalNoSeSabe": "We couldn't check what needs to be accepted.",
  "cuenta.legalReintentar": "Try again",
  "cuenta.acepto_hay_que": "You need to accept them to create your account.",
  "precio.lanzamiento": "Launch price",
  // 15/09: `cta.sin_tarjeta` («no card, no account») se quita; el porqué, en el castellano.
  "precio.normal": "Standard price",
  // 16/09: terminaba en «, and we'll let you know before we charge you.»; el porqué, en el castellano.
  "precio.renovacion": "A single yearly payment. The price doesn't go up: you keep this rate while your account stays active.",
  // 18/09 · La moneda del cobro. Obligatoria, no es letra pequeña; el porqué, en el castellano.
  "precio.nota_moneda": "You'll be charged in Colombian pesos: {cop}.",
  "precio.cancelar": "Cancel subscription",
  "precio.cancelada":
    "Canceled. You keep access until your renewal date: we don't cut off something you've already " +
    "paid for.",
  "precio.un_clic": "Cancel in one click from your account. No emails, no phone calls.",

  "pagar.boton": "Subscribe",
  "pagar.cerradas_titulo": "You can't pay yet.",
  "pagar.desactivadas": "Subscriptions are turned off right now.",
  "pagar.sinSesion": "Your session expired. Sign in again, then tap Subscribe.",
  // 18/09 · La pasarela no contesta, que no es lo mismo que estar cerrada; el porqué, en el castellano.
  "pagar.no_disponible":
    "We couldn't open the payment page. Nothing was charged: try again in a minute.",
  "pagar.desactivadas_en_la_cuenta":
    "In the meantime, if you have an access code, you can redeem it below.",
  // 16/09: sin sesiones gratis; lo que decía cada una, en el castellano.
  "pagar.desactivadas_en_la_venta":
    "In the meantime, you can build your plan and, if you have an access code, enter it when you create your account.",

  "muro.titulo": "Subscriptions aren't open yet.",
  // 18/09 · El titular cuando sí se puede pagar; el porqué, en el castellano.
  "muro.titulo_hace_falta": "You need access to practice",
  "muro.se_puede":
    "You can subscribe now and carry on with your plan, or redeem an access code in your account.",
  "muro.sin_acceso": "To practice, you need a subscription or an access code.",
  "muro.mientras":
    "Once you can subscribe, you'll find it in your account. If you have an " +
    "access code, you can redeem it there now.",

  "contacto.titulo": "Write to us",
  "contacto.whatsapp": "WhatsApp",
  "contacto.correo": "Email",
  // 15/09: `contacto.direccion` («Address») se quita; el porqué, en el castellano.
  "contacto.horario": "Hours",
  "contacto.entre_semana": "Monday to Friday, {a} to {b}",
  "contacto.sabado": "Saturdays, {a} to {b}",
  "contacto.zona": "Bogotá time ({zona})",
  "contacto.whatsapp_flotante": "Write to us on WhatsApp",
  "pie.legales": "Legal documents",
  "salud.guardar_y_hablar": "Save this and talk to someone",
  "salud.desde_la_puerta":
    "Leave us your details and we'll go over it with you. Your answers stay saved. You get in touch, and " +
    "once everything checks out, we open your account. You won't have to start over.",
  "salud.desde_la_puerta_con_cuenta":
    "We can save your answers and have someone on our team go over them. You get in touch, and once " +
    "everything checks out, we open your account. You won't have to start over.",
  "salud.titulo": "Before you start, let's talk",
  "salud.texto":
    "Based on what you've told us, we'd like someone on our team to go over " +
    "it with you before you start practicing. Your account is created and your " +
    "answers are saved: you haven't lost anything.",
  "salud.que_pasa_ahora":
    "Write to us and we'll tell you what we need. It's usually a note from " +
    "whoever is seeing you; as soon as we have it, we'll activate your account.",
  "salud.pausado_titulo": "We've paused the exercises",
  "salud.pausado_texto":
    "We noticed something worth checking before you keep practicing, " +
    "so we've paused the exercises to be safe. Your account, your plan and " +
    "everything you've done are still here.",
  "salud.escribenos_para_seguir": "Write to us and we'll figure it out together:",
  // 16/09: sin sesiones gratis, el desbloqueo ya no dice que se puede practicar; lo que decía, en el castellano.
  "desbloqueo.titulo": "Your account is open",
  "desbloqueo.texto": "We've reviewed what you sent us.",
  "desbloqueo.pasarela":
    "One thing is still open from before: how you keep going. Subscriptions aren't open yet; when they " +
    "are, you'll see them in your account.",
  "desbloqueo.mientras":
    "If you have a code, type it in the box above and start today.",
  // ── Tu cuenta ─────────────────────────────────────────────────────────
  // ── La cabecera de la pagina de venta ─────────────────────────────────
  // 14/09: said "Voice Comfort · your exercises, and how much of each"; the new page leads with its headline.
  // 15/09: "no card, no account" is gone (the owner: it isn't true); the guarantee goes instead.
  "cabecera.titulo": "Voice Comfort · “Having a voice” says it all",
  "cabecera.descripcion":
    "A voice plan built around you, a few minutes a day on your phone, and an assistant that " +
    "knows voice inside out. Backed by a fourteen-day guarantee.",
  "cabecera.og_descripcion":
    "A plan built around you, a few minutes a day on your phone, and an assistant that knows " +
    "voice inside out. Backed by a fourteen-day guarantee.",
  // ── The sales page: menu, buttons, the sample chat and the footer (15/09) ──
  "venta.nav_secciones": "Page sections",
  "venta.nav_como_funciona": "How it works",
  "venta.nav_para_quien": "Who it's for",
  "venta.nav_asistente": "Assistant",
  "venta.nav_precio": "Pricing",
  "venta.nav_preguntas": "FAQ",
  "venta.menu_abrir": "Open menu",
  "venta.menu_cerrar": "Close menu",
  "venta.crear_plan": "Build my plan",
  "venta.ver_como_funciona": "See how it works",
  "venta.sello_garantia": "Fourteen-day guarantee",
  "venta.sello_cancelar": "Cancel anytime",
  "venta.sello_movil": "On your phone, nothing to install",
  "venta.chat_nombre": "Voice Comfort assistant",
  "venta.chat_pregunta": "I'm teaching for six hours tomorrow. How do I get my voice ready?",
  "venta.chat_respuesta":
    "Keep today's session as it is; don't add anything extra. Tomorrow, before your first class, do the " +
    "warm-up in your plan, and rest your voice between classes.",
  "venta.precio_plan": "Annual plan",
  "venta.precio_incluye": "What's included",
  "venta.solo_adultos": "Adults only.",
  "venta.otra_pregunta": "Still have a question?",
  "venta.escribenos": "Message us on WhatsApp",
  "venta.pie_lema": "The best, most complete way to take care of your voice.",
  "venta.pie_ayuda": "Help",
  "venta.pie_derechos": "All rights reserved.",
  "micuenta.titulo": "Your account",

  "micuenta.ajustes": "Settings",
  "micuenta.salir_de_todos": "Sign out on all devices",
  "micuenta.salir_de_todos_ayuda":
    "You'll be signed out of Voice Comfort everywhere you've signed in, including here. When you sign " +
    "back in, we'll email you a code.",
  "micuenta.tema": "Appearance",

  "micuenta.tu_voz": "Your voice",
  "micuenta.tu_voz_medida":
    "Your voice speaks at around {hz} Hz (how high or low it is) and moves across {rango} semitones " +
    "(a semitone is one piano key to the next).",
  "micuenta.tu_voz_otra_vez":
    "That's what we use to pick the exercises that fit your voice. If you measured in a hurry, " +
    "somewhere noisy, or a long time ago, do it again: it takes thirty seconds.",
  "micuenta.tu_voz_sin_medir":
    "You haven't measured yet, so we're showing you the whole catalog. Once you measure, we leave out " +
    "what's outside your range. Thirty seconds, and the audio never leaves this device.",
  "micuenta.medir": "Measure my voice",
  "micuenta.medir_otra_vez": "Measure again",
  "micuenta.correo": "Your email",
  "micuenta.confirmado": "Confirmed.",
  "micuenta.suscripcion": "Your subscription",
  "micuenta.sin_suscripcion":
    "You don't have one yet. Your plan and your answers are saved here. To start practicing, subscribe or " +
    "redeem an access code.",
  "micuenta.activa": "Active.",
  "micuenta.prueba": "You're on a trial.",
  "micuenta.prueba_hasta": "The trial ends on {fecha}.",
  "micuenta.garantia":
    "Not working for you? Message us on WhatsApp within the first fourteen days and we'll refund what you " +
    "paid, with nothing to explain.",
  "micuenta.regalada": "Your access was a gift.",
  "micuenta.regalada_hasta": "Until {fecha}.",
  "micuenta.regalada_de_por_vida": "For life, with no renewal date.",
  "micuenta.vencida": "Your access has run out.",
  "micuenta.vencida_el": "It ran out on {fecha}.",
  "micuenta.vencida_que_hacer":
    "If you have an access code, you can redeem it right here and get access back.",
  "micuenta.ya_cancelada": "Canceled.",
  "micuenta.estado_raro":
    "We can't tell where your subscription stands right now. Check back in a bit; nothing has been changed.",
  "micuenta.renueva_el": "It renews on {fecha}.",
  "micuenta.acceso_hasta": "Canceled. You have access until {fecha}.",
  "micuenta.acceso_sin_fecha": "Canceled. You have access until the end of the paid period.",
  "micuenta.codigo": "Have an access code?",
  "micuenta.codigo_ayuda": "Type it exactly as you got it, hyphens and all.",
  "micuenta.canjear": "Redeem",
  "micuenta.codigo_canjeado": "Redeemed. You have access now.",
  "micuenta.codigo_no_vale": "That code isn't valid, or it's already been used.",
  "micuenta.borrado": "Account deleted. Nothing of yours is left here.",
  "micuenta.algo_fue_mal": "That didn't go through. Try again in a moment.",

  "ia.titulo": "Ask anything",
  "ia.escribe": "Type your question",
  "ia.enviar": "Send",
  "ia.hay_que_entrar":
    "You need an account to ask. The whole library stays open without one, with all {n} exercises and their sources.",
  "ia.demasiadas":
    "You've used up your questions for the last 24 hours. Each one frees up again a day after you asked " +
    "it; in the meantime, the full exercise is in the library.",
  "ia.tope_del_dia":
    "The assistant has hit its limit for the last 24 hours and can't answer right now. The whole library is still there, with every exercise in full and its sources.",
  "ia.de_donde_sale": "Where this comes from",
  "ia.sin_configurar":
    "The assistant isn't available right now. In the meantime, the whole catalog is in the " +
    "library, with every exercise in full and its sources.",

  "origen.F": "This number comes straight from the source.",
  "origen.F~": "We worked this number out from the source, using our own judgment.",
  "origen.N": "We decided this number ourselves. There is no source behind it.",
};

/* 14/09/2026 · PASADA DE ADAPTACIÓN en/pt: el dueño avisó (13/09) «estás traduciendo al pie de la
   letra, en lugar de adaptarlo a la realidad del idioma». En este bloque (portugués de Brasil):
   hablar a «você» en vez del impersonal con «-se» («Cancela-se» → «Cancele», «Escreve-se» →
   «Digite», «Gasta-se» → «A força vai») y sin imperativo de *tu* mezclado («sobe e desce» → «suba
   e desça»); «Custa respirar/engolir» → «Você tem dificuldade para…»; «faz falta» → «precisa»;
   «Seguir» en botones → «Continuar»; «guardar» en UI → «salvar»; «Apagar minha conta» → «Excluir
   minha conta»; «Escreva {p}» → «Digite {p}»; «não vale» → «não funciona / não é válido»; «{normal}
   ao ano» → «{normal} por ano»; «Não corre pressa» → «Não tem pressa»; «Resfriamento» →
   «Desaquecimento»; `tubo.titulo` «meia boca» (= chapuza en Brasil) → «boca semifechada»;
   `planner.por_que_resta` «DIMINUI» → «DESCONTA» (y «recebe» → «receba»); «amigdalite» en las
   ayudas → «dor de garganta» (no se nombra una dolencia donde no hace falta); «cronômetro» →
   «timer», «teto» de la boca → «céu da boca», «calombinho» → «gogó», «esta ficha» → «este
   exercício», «um quarto de hora» → «quinze/15 minutos». Sin cambios de sentido, claves ni huecos.
   Lo que decía antes está en git (commit `c281a7e`) y el criterio, en
   `_auditoria-premium/recorridos/criterios-adaptacion.md`. */
const PT: Record<Clave, string> = {
  "app.nombre": "Voice Comfort",
  "app.biblioteca": "Biblioteca",
  "app.mi_plan": "Meu plano",
  "app.ajustes": "Ajustes",
  "app.idioma": "Idioma",
  "app.cambiar_tema": "Mudar o tema",
  "app.tema_claro": "Claro",
  "app.tema_oscuro": "Escuro",
  "app.tema_sistema": "Como o sistema",
  "app.volver": "Voltar",
  "app.empezar": "Começar",
  "app.seguir": "Continuar",
  "app.cargando": "…",
  "app.montando_plan": "Montando o seu plano.",
  "app.montando_sesion": "Montando a sessão de hoje.",
  "app.buscando_cuenta": "Buscando os seus dados.",
  "app.reintentar": "Tentar de novo",
  "app.cerrar": "Fechar",
  "fallo.no_se_pudo_titulo": "Não conseguimos verificar.",
  "fallo.no_se_pudo_texto":
    "Isso não quer dizer que você não tenha plano: só não conseguimos confirmar agora. " +
    "Tente de novo daqui a pouco.",
  "fallo.sin_puerta_titulo": "Faltam as respostas de segurança.",
  "fallo.sin_puerta_texto":
    "Seu plano está aí, mas não dá para mostrá-lo sem as perguntas respondidas. " +
    "Leva só um minutinho, e você só responde uma vez.",

  "pie.legal":
    /* 16/09: decía «produto de saúde». El castellano aprobado dice «dispositivo médico» y el inglés
       «medical device»; en Brasil el término de la ANVISA es también «dispositivo médico». */
    "O Voice Comfort treina uma habilidade. Não é um dispositivo médico, não diagnostica e não " +
    "substitui a consulta com um profissional. Quando algo precisa ser avaliado, avisamos e " +
    "indicamos a pessoa certa.",

  // ── El onboarding, entero ─────────────────────────────────────────
  "onb.bienvenida_titulo": "Faça isto por vinte segundos.",
  "onb.bienvenida_p1":
    "Junte os lábios sem apertar e faça um mmm confortável, como se estivesse concordando. Você " +
    "deve sentir um formigamento nos lábios ou no nariz.",
  "onb.bienvenida_p2":
    "Pronto. Isso é um exercício de voz de verdade, e você acabou de fazer um sem se cadastrar, sem pagar " +
    "e sem contar nada para a gente.",
  "onb.bienvenida_aviso":
    "Agora vêm algumas perguntas. Uma parte delas é de segurança e não dá para pular — não é para nos " +
    "proteger: é que tem exercício que não se faz em casa, e precisamos saber disso antes de montar o seu " +
    "plano.",
  "onb.seguir": "Continuar",
  "onb.hilo_paso": "Etapa {n} de {total}",
  "onb.hilo.ejercicio": "Um exercício",
  "onb.hilo.voz": "Microfone e voz",
  "onb.hilo.seguridad": "Segurança",
  "onb.hilo.plan": "Seu plano",
  "onb.contar_veinte": "Contar os vinte segundos",
  "onb.veinte_otra_vez": "Contar de novo",
  "mov.referencia_prestada":
    "A altura do desenho usa uma referência geral, não a da sua voz. Meça a sua voz em “Sua conta”, com " +
    "“Medir minha voz”: são trinta segundos, e o desenho se ajusta a ela.",
  "mov.llegaron": "Chegaram {n} de {total} palavras.",
  "mov.no_te_oimos":
    "Não ouvimos você. Não quer dizer que você fez errado: não chegou nenhuma gravação para comparar.",
  "aviso.titulo": "Seu plano está pronto, mas antes tem uma coisa.",
  "aviso.seguimiento":
    "Você nos disse que já tem alguém acompanhando sua voz. O que essa pessoa disser vem antes do " +
    "que esta plataforma disser, sempre.",
  "aviso.plan_ajustado":
    "Seu plano vem com a carga reduzida: ficam de fora os exercícios que mais forçam a voz. Não é " +
    "falta de capacidade: agora só não é a hora.",
  "aviso.seguir": "Entendi, ver meu plano",
  "onb.tesitura": "Sua voz",
  "onb.tesitura_titulo": "Trinta segundos, e você descobre sua extensão.",
  "onb.tesitura_texto":
    "Primeiro você lê uma frase em voz alta, do jeito que fala normalmente. Depois faz uma " +
    "sirene confortável, sem forçar. Com essas duas coisas sabemos em que altura você fala, e " +
    "todos os exercícios se ajustam a ela, e não a um número qualquer.",
  "onb.tesitura_empezar": "Começar",
  "onb.tesitura_lectura": "Leia em voz alta, como se estivesse contando para alguém.",
  "onb.tesitura_frase":
    "Hoje de manhã saí cedo e fui andando até a praça. Estava fresco, mas gostoso. " +
    "Comprei o pão de sempre e voltei pelo parque.",
  // 16/09: mais curta, lê-se com um contador na frente; o que dizia, no bloco em espanhol.
  "onb.tesitura_sirena": "Agora diga “uuu” subindo e descendo, suave.",
  "onb.tesitura_calculando": "Olhando o que você fez…",
  "onb.tesitura_lista":
    "Pronto. Sua voz fala em torno de {hz} Hz (o quanto ela é aguda ou grave) e se move confortável por " +
    "{rango} semitons (um semitom é de uma tecla do piano para a do lado).",
  "onb.tesitura_otra_vez": "Tentar de novo",
  "onb.tesitura_no_guardada":
    "A medida saiu, mas não conseguimos salvá-la. Tente de novo quando quiser.",
  "onb.tesitura_seguir_sin": "Continuar sem medir",
  "onb.tesitura_pocas_muestras":
    "Não ouvimos você o suficiente. Aproxime o microfone ou fale um pouco mais alto.",
  "onb.tesitura_sin_voz_clara":
    "O som não veio claro. Costuma ser ruído de fundo: tente em um lugar mais tranquilo.",
  "onb.tesitura_sin_recorrido":
    "A sirene saiu plana. Suba e desça um pouco mais, sempre sem forçar.",
  "onb.microfono_titulo": "Vamos pedir acesso ao seu microfone, e é bom você saber o que fazemos com ele.",
  "onb.microfono_texto":
    "Ele é usado só dentro do seu navegador, para desenhar o que você está fazendo enquanto faz. O " +
    "áudio não é enviado nem salvo, e não chega a nenhum servidor nosso. A única coisa que " +
    "sai daqui são números: quantos segundos, quantos semitons.",
  "onb.perfil_ayuda": "É isto que decide quais exercícios você recebe, e quantos.",
  "onb.de": "de",
  // ── Las trece de seguridad · ninguna nombra una dolencia ───────────
  "pregunta.esAdulto": "Você tem 18 anos ou mais?",
  "pregunta.esAdulto.ayuda": "Esta plataforma é só para pessoas adultas, e deixamos isso claro.",
  "pregunta.dificultadParaRespirar": "Você tem dificuldade para respirar agora, ou faz um ruído novo ao inspirar?",
  "pregunta.dificultadParaRespirar.ayuda": "Um apito novo ao inspirar, ou falta de ar agora. O chiado do peito de sempre, ou ao soltar o ar, não conta aqui.",
  "pregunta.sangreAlToser": "Você viu sangue ao tossir ou ao expectorar?",
  "pregunta.sangreAlToser.ayuda": "Sangue no que sai da garganta ou do peito. O do nariz ou o da gengiva não conta.",
  "pregunta.reposoVocalIndicado": "Alguém disse para você não usar a voz neste momento?",
  "pregunta.reposoVocalIndicado.ayuda": "Repouso vocal indicado por um profissional, neste momento.",
  "pregunta.cirugiaReciente": "Você fez cirurgia na garganta ou nas cordas vocais nos últimos três meses?",
  "pregunta.cirugiaReciente.ayuda": "Da garganta, das cordas vocais, do pescoço ou da tireoide. De dente ou de nariz, não.",
  "pregunta.roncoMasDeTresSemanas": "Sua voz está diferente há mais de três semanas?",
  "pregunta.roncoMasDeTresSemanas.ayuda": "Sem um resfriado ou uma alergia que explique.",
  "pregunta.dificultadAlTragar": "Você tem dificuldade para engolir, ou sente algo na garganta que não vai embora?",
  "pregunta.dificultadAlTragar.ayuda": "Mais de três semanas, e sem um resfriado ou uma dor de garganta que explique.",
  "pregunta.dolorIrradiadoAlOido": "Dói ao falar ou ao engolir, e a dor chega até o ouvido?",
  "pregunta.dolorIrradiadoAlOido.ayuda": "Mais de três semanas, e sem dor de garganta nem infecção de ouvido que explique.",
  "pregunta.bultoEnElCuello": "Você notou um caroço no pescoço?",
  "pregunta.bultoEnElCuello.ayuda": "Um que continua ali depois de três semanas. Os que aparecem com um resfriado e somem sozinhos não contam.",
  "pregunta.perdidaRepentinaDeVoz": "Você perdeu a voz de repente e por completo?",
  "pregunta.perdidaRepentinaDeVoz.ayuda": "E continua assim. Se voltou sozinha em um ou dois dias e não aconteceu de novo, não conta.",
  "pregunta.enSeguimientoProfesional": "Algum fonoaudiólogo ou otorrino está acompanhando você agora?",
  "pregunta.enSeguimientoProfesional.ayuda": "Não tem problema: muda como montamos seu plano.",
  "pregunta.diagnosticoNeurologico": "Alguém te acompanha por algo neurológico ou de tireoide?",
  "pregunta.diagnosticoNeurologico.ayuda": "Parkinson, AVC, ELA, hipotireoidismo. Também não tem problema: ajusta o plano.",
  "pregunta.viaAerea":
    "Você usa bombinha, está com alergia esses dias, sente ácido subindo do estômago, ou dorme com " +
    "aparelho de respiração (CPAP)?",
  "pregunta.viaAerea.ayuda": "Qualquer uma das quatro. Muda quais exercícios oferecemos.",
  // ── Los siete perfiles ────────────────────────────────────────────
  "perfil.A1_carga_sostenida": "Falo o dia inteiro",
  "perfil.A1_carga_sostenida.sub": "Aula, atendimento ao público, saúde, guia",
  /* 16/09: decía «locuto», que no existe como verbo en portugués: un locutor «faz locução». */
  "perfil.A2_rendimiento": "Canto, atuo ou faço locução",
  "perfil.A2_rendimiento.sub": "A voz é meu instrumento de trabalho",
  "perfil.A3_voz_conectada": "Reuniões e microfone",
  "perfil.A3_voz_conectada.sub": "Videochamadas, podcast, ao vivo, aula online",
  "perfil.A4_post_clinico": "Estou voltando depois de uma avaliação",
  "perfil.A4_post_clinico.sub": "Alta da fonoaudiologia, ou manutenção depois de uma cirurgia",
  "perfil.A5_voz_que_envejece": "Minha voz perdeu força",
  "perfil.A5_voz_que_envejece.sub": "Com os anos, e já foi avaliada",
  "perfil.A6_exploracion": "Quero que minha voz soe de outro jeito",
  "perfil.A6_exploracion.sub": "Explorar como soa e como me representa",
  "perfil.A7_tension": "Percebo que aperto",
  "perfil.A7_tension.sub": "Nó na garganta, tensão, medo de palco",
  // ── La portada de la aplicación ───────────────────────────────────
  "portada.etiqueta": "Voz falada · para adultos",
  // 16/09: a mesma frase da página de venda, que é a que o dono aprovou (ver o bloco em espanhol).
  "portada.titulo": "A sua voz também se treina.",
  "portada.cuerpo":
    "Fazemos as perguntas necessárias para escolher, entre {n} exercícios, os {min} a {max} certos " +
    "para você. Com quanto de cada um, de onde vem esse número e quando parar.",
  "portada.ver_ejercicios": "Ver os exercícios",
  // ── La aplicación, lo que quedaba escrito a mano ──────────────────
  "lib.titulo": "A biblioteca",
  "lib.cuerpo":
    "Tudo o que existe, esteja ou não no seu plano. O que está no seu plano veio das suas " +
    "respostas; isto é o resto.",
  "lib.sin_revisar_corto": "Desenhos ainda não revisados",
  "lib.sin_carga": "não pede nada à sua voz",
  "lib.carga_baja": "carga baixa",
  "lib.carga_media": "carga média",
  "lib.carga_alta": "carga alta",

  "lib.buscar": "Buscar um exercício",
  "lib.familia": "Família",
  "lib.todas_las_familias": "Todas as famílias",
  "lib.filtro_sin_carga": "Não pedem nada à sua voz",
  "lib.filtro_calladas": "Sem ninguém te ouvir",
  "lib.limpiar": "Limpar os filtros",
  "lib.cuantas": "{n} de {total}",
  "lib.nada": "Nenhum resultado. Tente com menos filtros ou outra palavra.",

  "mov.hoy_ajustado":
    "Hoje é menos do que os {base} da página do exercício, por causa do degrau em que você está. O degrau " +
    "sobe sozinho, sessão a sessão.",
  "ia.aviso":
    "Ele só fala do que está no nosso catálogo, e quando não sabe, avisa. Não opina sobre como sua voz " +
    "soa: não consegue te ouvir, e mesmo que conseguisse, esse não seria o trabalho dele.",
  "mov.necesitas": "O que você precisa",
  "mov.necesitas_espejo": "Um espelho.",
  "mov.necesitas_silencio": "Um lugar silencioso.",
  "mov.asi_suena": "É assim que soa",
  "mov.escuchar": "Ouvir",
  "mov.parar_de_escuchar": "Parar",
  "mov.que_te_lo_cuenten": "Deixe a voz contar",
  "mov.que_te_lo_cuenten_pie":
    "A voz lê os passos para você fazer sem olhar para a tela.",
  "mov.antes_de_escucharte": "Antes de se ouvir",
  "mov.guion_de_la_escucha":
    "Sua voz gravada vai soar estranha. Acontece com quase todo mundo: por dentro você também se ouve " +
    "pelos ossos, e a gravação não. Isto não é um concurso de quem gosta mais da própria voz. Preste " +
    "atenção em uma coisa só: onde dá para entender melhor as palavras, na sua voz ao vivo ou na " +
    "gravação?",
  "mov.que_mide": "O que isto mede, e o que não mede",
  "mov.por_dentro": "O que acontece por dentro",
  "mov.en_este_para_si": "Neste, pare se…",
  "mov.como_se_hace": "Como fazer",
  "mov.lo_que_te_dices": "O que você fala para você mesmo enquanto faz",
  "mov.es_imagen": "É uma imagem, não anatomia",
  "mov.en_que_se_falla": "Onde costuma dar errado",
  "mov.como_lo_notas": "Como você percebe",
  "mov.como_se_arregla": "Como corrigir",
  "mov.para_y_consulta": "Pare e procure avaliação se…",
  "mov.que_salio": "O que as medições mostraram",
  "mov.a_favor": "A favor",
  "mov.en_contra": "Contra",
  "mov.quien_lo_propone": "Quem propõe",
  "mov.ver_la_fuente": "Ver a fonte",
  "mov.ver_las_fuentes": "Ver as fontes",
  "mov.sin_atribucion":
    "Não encontramos ninguém que descreva este exercício assim. Muita gente ensina esse movimento, mas " +
    "não achamos nenhuma fonte assinada. Incluímos mesmo assim, e avisamos você.",
  "mov.solo_manual":
    "Está descrito no manual que deu origem a este projeto. Em nenhum outro lugar que a gente tenha " +
    "encontrado.",
  "mov.si_no_te_sale": "Se não conseguir",
  "mov.tope": "Limite: {min} min, e o cronômetro corta. Mais não é melhor, e por isso o limite tem número.",
  "mov.que_es_la_marca":
    "As letras ao lado de cada número dizem de onde ele vem. Toque nelas para ver.",
  "sesion.sin_plan_titulo": "Você ainda não tem plano.",
  "sesion.sin_plan_texto":
    "O plano é montado depois das perguntas. São {n} e não dá para pular: tem exercício que não se faz em " +
    "casa, e precisamos saber disso antes de montar o seu plano. Se você já respondeu em outro aparelho, " +
    "aquelas respostas ficaram lá e não vêm para esta conta.",
  "escena.alt":
    "Gráfico da curva de tom do exercício. As instruções completas estão escritas abaixo, no passo " +
    "a passo.",
  "sello.sin_revisar":
    "Os desenhos e o vídeo deste exercício ainda não foram revisados por um profissional. Avisamos aqui " +
    "porque achamos que você deve saber.",
  "sello.revisado": "Desenhos e vídeo revisados por {quien}.",
  "mic.sin_https":
    "Sem conexão segura não dá para usar o microfone. É uma regra do navegador, não nossa.",
  "mic.denegado":
    "Sem microfone. Você pode fazer os exercícios do mesmo jeito: o que você perde é poder se ver " +
    "enquanto faz, não o exercício.",
  // 16/09: linha de ajuda curta, não uma etiqueta que compita com o que há para ler (ver o espanhol).
  "mic.escuchando": "Isto é só para você se ver enquanto fala. Não estamos te avaliando.",
  "mic.en_reposo": "Aqui você vai ver sua voz enquanto fala: quanto mais aguda, mais alto sobe a linha.",
  "mic.pidiendo": "Pedindo o microfone ao navegador…",
  "temp.se_acabo": "Acabou o tempo.",
  "temp.tope_duro": "Este exercício tem um limite de {t} e aqui não tem tempo extra.",
  "temp.al_medirlo": "Nas medições, passado esse ponto a voz ficava mais apertada e menos clara.",
  "temp.lo_de_hoy": "Você fez a parte de hoje deste exercício.",
  "temp.parado":
    "Parado. O que você fez fica registrado, e pronto: parar quando precisa faz parte do exercício — não " +
    "é falhar nele.",
  "temp.parar": "Parar",
  "temp.otra_vez": "Começar de novo",
  "lamina.donde.titulo": "Os lugares que você mesmo pode sentir",
  "lamina.donde.nariz":
    "O nariz. Tape com dois dedos enquanto faz o som: se o som mudar, uma parte estava saindo por ali",
  "lamina.donde.labios": "Os lábios",
  "lamina.donde.dientes": "Os dentes de cima",
  "lamina.donde.reborde":
    "A saliência logo atrás dos dentes de cima. Toque com a ponta da língua",
  "lamina.donde.fondo":
    "O fundo da boca. Olhe no espelho enquanto boceja: ele se alarga",
  "lamina.donde.lengua": "A língua",
  "lamina.donde.bultito": "O gogó, que sobe e desce quando você engole",
  "lamina.donde.cuello":
    "A frente do pescoço. Ponha dois dedos ali: vibra quando tem voz e fica parada quando só tem ar",
  "lamina.donde.pecho":
    "O meio do peito. Com a mão aberta em cima e um “mmm” grave, dá um formigamento",
  "lamina.donde.aire": "Por aqui SAI o ar. Ponha a palma da mão a um palmo de distância e você sente",
  "lamina.mandibula.titulo": "A mandíbula e sua dobradiça, vistas de frente",
  "lamina.mandibula.bisagra":
    "As duas dobradiças, bem na frente do buraco da orelha. É ali que a mandíbula gira, não no queixo",
  "lamina.mandibula.masetero":
    "O músculo que estufa na bochecha quando você aperta os dentes",
  "lamina.mandibula.arcada": "O arco, a ferradura inteira. É uma peça só, com duas dobradiças",
  "lamina.mandibula.pomulo": "A maçã do rosto. Esse músculo começa logo abaixo daqui",
  "lamina.mandibula.caida": "A mandíbula CAI pelo próprio peso, girando nas dobradiças",
  "lamina.mandibula.adelantar":
    "Projetar o queixo para a frente não é soltar: é apertar em outro lugar",
  "lamina.cuello.titulo": "O pescoço e os ombros, vistos de frente",
  "lamina.cuello.hioides":
    "Um osso pequeno que flutua: é o único que não encosta em nenhum outro. A língua fica presa nele " +
    "por cima, e por baixo fica pendurado o que sobe quando você engole",
  "lamina.cuello.esternocleido":
    "Os dois músculos que vão de trás da orelha até o osso plano do peito. São os que aparecem quando você vira a cabeça",
  "lamina.cuello.trapecio": "O músculo que vai do pescoço ao ombro",
  "lamina.cuello.laringe": "O gogó, que sobe e desce quando você engole, pendurado nesse osso",
  "lamina.cuello.hombro": "A clavícula e o ombro",
  "lamina.cuello.caen": "Os ombros CAEM. O movimento é soltar, nunca encolher",
  "lamina.cuello.sujetar":
    "Com a cabeça projetada para a frente, estes músculos passam a segurar o peso. E são os mesmos " +
    "que você quer soltos ao falar",
  "lamina.lengua.titulo": "A língua inteira, vista de lado e por dentro",
  "lamina.lengua.punta": "A ponta. É ela que vibra no erre e faz o ele e o ene",
  "lamina.lengua.alveolos":
    "A saliência que fica LOGO ATRÁS dos dentes de cima. A saliência, não os dentes",
  "lamina.lengua.dorso": "A parte do meio. É ela que sobe até o fundo do céu da boca no nh de “banho”",
  "lamina.lengua.raiz": "A raiz. São dois terços da língua, que não aparecem no espelho e chegam até esse osso",
  "lamina.lengua.suelo": "O assoalho da boca, embaixo. É o que afrouxa quando você solta a mandíbula",
  "lamina.lengua.hioides": "O osso que flutua, onde a raiz fica presa",
  "lamina.lengua.adelante": "Quando você leva o som para a frente, a língua VEM PARA A FRENTE",
  "lamina.lengua.retraer": "A base puxa para trás para ajudar a fechar. É um apoio que você quase nunca precisa usar",
  "lamina.caja.titulo": "A caixa torácica e o ar, vistos de frente",
  "lamina.caja.subglotica": "Aqui está a pressão que importa: logo ABAIXO das cordas vocais, que ficam bem no alto do tubo",
  "lamina.caja.traquea": "A traqueia, o tubo que desce até o peito",
  "lamina.caja.costillas": "As costelas. A caixa abre pelos dois lados ao mesmo tempo",
  "lamina.caja.diafragma":
    "A cúpula que fica embaixo das costelas. É uma CÚPULA, não uma cinta: parada, ela fica lá em cima, " +
    "arredondada para cima",
  "lamina.caja.tripa": "A parede da barriga. O que se move ali não é o ar",
  "lamina.caja.abren": "As costelas ABREM para os lados, não para a frente",
  "lamina.caja.baja": "Quando você inspira, a cúpula DESCE e empurra o que está embaixo",
  "lamina.caja.sale": "E o ar SAI para cima",
  "lamina.caja.empujar": "Empurrar com a barriga não aumenta a pressão lá em cima. Essa força toda vira aperto",
  "lamina.labios.titulo": "Os lábios e a saída, vistos de frente e de perto",
  "lamina.labios.cierre": "O fechamento entre os dois lábios: o eme, o bê e o pê",
  "lamina.labios.labio_diente": "Lábio de baixo contra dentes de cima: o efe e o vê",
  "lamina.labios.frunce": "Franzidos, a saída fica estreita e se ajusta sem encostar em nada",
  "lamina.labios.comisuras": "Os cantos. É por aqui que o ar escapa se não fecharem",
  "lamina.labios.mejilla": "A bochecha, a parede dos lados. Nem estufada nem apertada",
  "lamina.labios.cigomaticos": "Os músculos que puxam o canto para cima e para trás",
  "lamina.labios.salida": "O ar sai PELO MEIO, e essa é toda a ideia",
  "lamina.labios.escape":
    "Se escapar por um canto, não tem pressão de volta, e o exercício deixa de funcionar",
  // ── El planner ────────────────────────────────────────────────────
  "planner.etiqueta": "Seu plano",
  "planner.titulo": "O que vem aí, e por quê.",
  "planner.peldano_es": "Você está no degrau {n}.",
  "planner.que_es_el_peldano":
    "O degrau é quanto cada exercício pede de você: o 0 é o que diz a página do exercício, e abaixo dele " +
    "pede menos. Não é uma avaliação sua.",
  "planner.por_progreso": "pelo que você praticou — {n} sessão | pelo que você praticou — {n} sessões",
  "planner.por_carga": "pela sua carga de voz — {h}",
  "planner.por_que_resta":
    "Sim: a carga de voz DESCONTA. Quem fala muitas horas já gastou a voz que tinha para hoje, e " +
    "pedir mais é justamente o que cansa. Não é que você receba menos por fazer menos: seu dia " +
    "já conta.",
  "planner.falta_para_subir": "Sobe um degrau na próxima sessão. | Sobe um degrau daqui a {n} sessões.",
  "planner.en_el_techo": "Você já está no topo. Daqui em diante muda a variedade, não a quantidade.",
  "planner.lo_que_viene": "As próximas seis",
  "planner.sin_fechas":
    "Não tem datas. O plano avança por sessões, então se um dia você não puder, não perde nada.",
  "planner.sesion_n": "Sessão {n}",
  "planner.forma": "{min} min · {n} exercício | {min} min · {n} exercícios",
  "planner.descanso": "Descanso",
  "planner.sube_aqui": "Aqui o degrau sobe",
  "planner.hacer_hoy": "Fazer esta",
  "planner.que_trae": "O que vem nela",
  "planner.tus_ejercicios": "Seu exercício | Seus {n} exercícios",
  "planner.tus_ejercicios_ayuda":
    "Os que vão aparecer nas próximas doze sessões, e quantas vezes. Cada um está completo na " +
    "biblioteca.",
  // 16/09: sem acesso a biblioteca não abre; o porquê, no bloco em espanhol.
  "planner.tus_ejercicios_ayuda_sin_acceso": "Os que vão aparecer nas próximas doze sessões, e quantas vezes.",
  "planner.veces": "{n} vez | {n} vezes",
  "planner.ajustar_titulo": "Se o seu dia mudou",
  "planner.ajustar_ayuda":
    "Mude o tempo ou as horas de voz e o plano se refaz a partir da próxima sessão. O que você já " +
    "fez fica, e você continua no seu degrau.",
  "planner.ajustado": "Pronto. A próxima sessão já vem com isto.",
  "planner.sin_racha":
    "Aqui não tem sequência para quebrar nem porcentagem para completar. Pode procurar: não tem, e é de " +
    "propósito.",
  "app.planner": "Meu plano",
  // ── Las dos últimas del onboarding ────────────────────────────────
  "onb.horas_titulo": "Quantas horas por dia você usa a voz?",
  "onb.horas_ayuda":
    "Contando trabalho e tudo mais. É a que mais muda seu plano, e não na direção que você espera.",
  "horas.poco": "Menos de duas",
  "horas.poco.sub": "Uma ligação ou outra, e pouco mais",
  "horas.algo": "Entre duas e quatro",
  "horas.algo.sub": "Reuniões quase todo dia",
  "horas.bastante": "Entre quatro e seis",
  "horas.bastante.sub": "Metade do dia falando",
  "horas.muchas": "Mais de seis",
  "horas.muchas.sub": "Aula, atendimento, guia: o dia inteiro",
  "onb.rato_titulo": "Quanto tempo você tem por dia?",
  "onb.rato_ayuda":
    "Na dúvida, escolha o mais curto. É um limite rígido: o timer corta ali e não pergunta se " +
    "você quer mais um minuto.",
  "rato.bolsillo": "Três minutos",
  "rato.bolsillo.sub": "No carro, antes de entrar",
  "rato.corta": "Sete minutos",
  "rato.corta.sub": "Um intervalo entre um compromisso e outro",
  "rato.media": "Quinze minutos",
  "rato.media.sub": "O que a maioria faz",
  "rato.larga": "Vinte e cinco minutos",
  "rato.larga.sub": "Com tempo e sem pressa",
  // ── El cuadro de la persona, los motivos y los compromisos (17/09) ─────────────────────────────────────────
  "onb.senales_titulo": "O que você nota na sua voz?",
  "onb.senales_ayuda": "Marque tudo o que acontece com você. Com isso escolhemos quais exercícios vêm primeiro.",
  "senal.cansancio": "Cansa no fim do dia",
  "senal.ronquera": "Rouquidão ou pigarro",
  "senal.aire": "Fico sem ar ao falar",
  "senal.volumen": "Não me ouvem",
  "senal.tension": "Aperto, ou falar me custa",
  "senal.quiebres": "Falha, ou não sobe",
  "senal.claridad": "Não me entendem",
  "senal.nervios": "Nervosismo ao falar em público",
  "senal.ninguna": "Nada disso",
  "onb.objetivo_titulo": "O que você quer conseguir?",
  "onb.objetivo_ayuda": "Só um. É o que mais pesa na hora de montar o seu plano.",
  "objetivo.aguantar": "Aguentar o dia",
  "objetivo.proyectar": "Que me ouçam sem esforço",
  "objetivo.claro": "Falar mais claro",
  "objetivo.soltar": "Soltar a tensão",
  "objetivo.rango": "Mais extensão ou agilidade",
  "objetivo.mantener": "Manter o que ganhei",
  "objetivo.explorar": "Explorar como soa",
  "onb.donde_titulo": "Onde você vai praticar quase sempre?",
  "onb.donde_ayuda": "O habitual. Em qualquer dia você pode marcar “hoje não posso fazer barulho”.",
  "donde.casa": "Em casa, sozinho",
  "donde.gente_cerca": "Com gente por perto",
  "donde.gente_cerca.sub": "Escritório, família, parede fina",
  "donde.carro": "No carro",
  "donde.cambia": "Muda a cada dia",
  "onb.experiencia_titulo": "Você já fez exercícios de voz antes?",
  "onb.experiencia_ayuda": "Serve para não te dar no começo o que pede ouvido ou técnica.",
  "experiencia.nunca": "Nunca",
  "experiencia.algo": "Um pouco, por conta própria",
  "experiencia.profesional": "Com um profissional, ou canto",
  "onb.tubito_titulo": "Você tem um canudo largo à mão?",
  "onb.tubito_ayuda": "Um canudo de milk-shake. Quinze exercícios usam um; sem ele, seu plano os deixa de fora e avisa.",
  "onb.tubito_si": "Sim, tenho",
  "onb.tubito_no": "Não tenho",
  "motivo.rotacion": "Para variar",
  "motivo.etapa.preparacion": "Para começar",
  "motivo.etapa.calentamiento": "Para aquecer a voz",
  "motivo.etapa.enfriamiento": "Para fechar a sessão",
  "motivo.sin_voz": "Não pede nada da sua voz",
  "motivo.senal.cansancio": "Porque a voz cansa no fim do dia",
  "motivo.senal.ronquera": "Pela rouquidão ou pelo pigarro",
  "motivo.senal.aire": "Porque você fica sem ar",
  "motivo.senal.volumen": "Porque não te ouvem",
  "motivo.senal.tension": "Porque você nota que aperta",
  "motivo.senal.quiebres": "Porque a voz falha ou não sobe",
  "motivo.senal.claridad": "Porque não te entendem",
  "motivo.senal.nervios": "Pelo nervosismo ao falar em público",
  "motivo.objetivo.aguantar": "Para aguentar o dia",
  "motivo.objetivo.proyectar": "Para que te ouçam sem esforço",
  "motivo.objetivo.claro": "Para falar mais claro",
  "motivo.objetivo.soltar": "Para soltar a tensão",
  "motivo.objetivo.rango": "Para ganhar extensão e agilidade",
  "motivo.objetivo.mantener": "Para manter o que ganhou",
  "motivo.objetivo.explorar": "Para explorar como soa",
  "motivo.donde.gente_cerca": "Porque você pratica com gente por perto",
  "motivo.donde.carro": "Porque você pratica no carro",
  "compromiso.sin_tubito": "Com um canudo largo entrariam mais {n} exercícios. Você muda isso em “Meu plano”.",
  "compromiso.techo": "Mais {n} exercícios entram a partir da sessão {s}: pedem ouvido ou técnica, e chegam na hora certa.",
  "planner.tubito": "Você tem canudo largo?",
  "planner.donde": "Onde você pratica quase sempre?",
  // ── Lo que iba pegado a una llave y por eso no se veía ────────────
  "sesion.numero": "sessão {n}",
  "sesion.minutos": "{n} minuto | {n} minutos",
  "sesion.voz_cansada_hoy": "Hoje minha voz está cansada",
  "sesion.sin_ruido_hoy": "Hoje não posso fazer barulho",
  "sesion.sin_ruido_explicado":
    "Saem os exercícios que dá para ouvir de longe, e vêm primeiro os silenciosos. " +
    "O plano continua inteiro: nada é cancelado.",
  "sesion.min": "{n} min",
  "sesion.hechos_hoy": "{n} de {total} feitos hoje",
  "sesion.seguir": "Continuar",
  "sesion.terminada_titulo": "Pronto. Sessão feita.",
  "sesion.terminada_texto":
    "Fica registrada, e com ela você avança para o próximo degrau. Não faça outra hoje: duas " +
    "seguidas não somam, uma atrapalha a outra.",
  "sesion.hoy_descanso": "Hoje, descanso.",
  "sesion.me_equivoque": "Me enganei, minha voz está bem",

  "sesion.hoy_si_puedes": "E, se você quiser, dá para fazer estes",
  "sesion.hoy_si_puedes_ayuda":
    "{n} que não pedem nada à sua voz: respiração, soltar o pescoço, postura. Não contam como sessão e " +
    "não mudam o seu degrau.",
  "sesion.cortado_por_el_tope": "cortado pelo limite",
  "sesion.hecho": "feito",
  "sesion.silencioso": "silencioso",
  "sesion.parado_a": "parado em {t}",
  "sesion.a_medias_titulo": "Por hoje, chega.",
  "sesion.a_medias_texto":
    "Você praticou {hecho} de {total}. Fica registrado, mas com menos da metade você não sobe de degrau: " +
    "da próxima vez vem esta mesma sessão de novo, do começo. Não faça outra hoje.",
  "sesion.siguiente": "Próximo: {nombre}",
  "sesion.ver_la_sesion": "Ver como foi a sessão",
  "sesion.hoy_no_puedo_este": "Hoje não consigo fazer este",
  // ── La cuenta ─────────────────────────────────────────────────────
  /* 16/09: «Salvar» / «Salve este plano e volte quando quiser.» / «Você já tem o plano…». Criar a
     conta agora é o caminho para praticar; o porquê está no bloco em espanhol. */
  "cuenta.guardar": "Criar conta",
  "cuenta.guardar_titulo": "Crie sua conta para começar a praticar.",
  "cuenta.guardar_ayuda":
    "Para praticar o seu plano, você precisa de uma assinatura ou de um código: digite o código aqui, ou " +
    "assine mais abaixo.",
  "cuenta.entrar_titulo": "Volte ao seu plano.",
  /* 15/09/2026: entrar com código. O porquê está no bloco em espanhol. */
  "cuenta.entrar_ayuda": "Digite seu e-mail e mandamos um código para entrar. Não precisa de senha.",
  "cuenta.mandar_codigo": "Enviar o código",
  "cuenta.codigo_titulo": "Digite o código do e-mail.",
  "cuenta.codigo_texto_nueva": "Mandamos um número de seis dígitos para {correo}.",
  "cuenta.codigo_texto": "Se {correo} tiver conta, vai chegar um número de seis dígitos.",
  "cuenta.codigo_campo": "Código",
  "cuenta.codigo_caduca": "São seis dígitos e valem por dez minutos.",
  "cuenta.codigo_otro": "Enviar outro código",
  "cuenta.codigo_otro_en": "Você pode pedir outro em {s} s",
  "cuenta.codigo_otro_mandado": "Mandamos outro. O anterior não vale mais.",
  "cuenta.codigo_no_vale":
    "Esse código não vale. Pode estar digitado errado, ter expirado ou já ter sido usado: peça outro se precisar.",
  "cuenta.otro_correo": "Usar outro e-mail",
  "cuenta.ya_tengo": "Já tenho conta",
  "cuenta.no_tengo": "Ainda não tenho conta",
  "cuenta.mira_spam": "Se não chegar em uns dois minutos, olhe na caixa de spam.",
  "cuenta.codigo_va_con_la_cuenta": "O código de acesso fica vinculado a esta conta: quando você entrar, ele aparece em “Sua conta”.",
  "cuenta.error_adulto": "Você precisa confirmar que tem 18 anos ou mais.",
  "cuenta.demasiados_intentos":
    "Muitas tentativas seguidas. Espere 15 minutos e tente de novo: a sua conta está inteira, só o acesso " +
    "ficou travado por um tempo.",
  "cuenta.bloqueada":
    "Fomos nós que fechamos o acesso a esta conta — o seu código está certo. Fale com a gente pelo " +
    "WhatsApp ou pelo e-mail de ajuda aqui embaixo e a gente verifica.",
  "cuenta.error_generico": "Não deu certo. Tente de novo daqui a pouco.",
  // 16/09: «Por enquanto este plano só existe neste navegador. Salve…»; o porquê, no bloco em espanhol.
  "planner.guardalo":
    "Para praticar este plano, crie sua conta: é lá que você digita o código de acesso ou assina. E aí o " +
    "plano fica também no seu celular.",
  // ── El gesto dibujado ─────────────────────────────────────────────
  "gesto.esquema": "Esquema, não uma foto",
  "gesto.asi_si": "Assim sim",
  "gesto.asi_no": "Assim não",
  "gesto.paso": "Passo",
  // ── Las familias del catálogo, y lo que quedaba suelto ────────────
  "familia.respiracion": "Respiração e apoio",
  "familia.sovt_estable": "Boca semiaberta",
  "familia.sovt_fluctuante": "Boca semiaberta com vibração",
  "familia.resonancia": "Ressonância",
  "familia.articulacion": "Articulação e dicção",
  "familia.rango": "Extensão e flexibilidade",
  "familia.proyeccion": "Potência e projeção",
  "familia.relajacion": "Relaxamento e postura",
  "familia.calentamiento": "Aquecimento",
  "familia.enfriamiento": "Desaquecimento",
  "familia.higiene": "Hábitos",
  "familia.entorno": "Ambiente",
  "familia.evaluacion": "Autoteste",
  "familia.voz_profesional": "Voz de trabalho",
  "familia.escucha": "Escuta e ouvido",
  "familia.habla_clara": "Fala clara",
  "familia.protocolo": "Sequência publicada",
  "familia.contexto": "Para um momento específico",
  "escena.grave": "grave",
  "escena.agudo": "agudo",
  "temp.empezar": "Começar",
  "temp.quedan": "Faltam {t}",
  "mov.cuanto": "Quanto",
  "mov.repeticiones": "{n} repetição | {n} repetições",
  // ── El tubo con dos puertas ───────────────────────────────────────
  "tubo.esquema": "Esquema, não uma foto",
  "tubo.titulo": "Por que a boca semiaberta cuida da voz.",
  "tubo.abierto": "Boca aberta",
  "tubo.estrecho": "Saída estreita",
  "tubo.puerta_arriba": "A porta de cima: os lábios, ou o canudo",
  "tubo.puerta_abajo": "A porta de baixo: onde a voz nasce",
  "tubo.aire": "O ar",
  "tubo.choca_fuerte": "A porta de baixo bate forte.",
  "tubo.choca_suave": "A porta de baixo fecha suave.",
  "tubo.moraleja":
    "Se você estreita a saída, o ar se acumula dentro e empurra para baixo. Aí a porta de baixo não " +
    "precisa bater com força para fazer o mesmo som.",
  "app.secciones": "Seções",
  "app.ajustes_nav": "Ajustes",
  "gesto.anterior": "Passo anterior",
  "gesto.siguiente": "Próximo passo",
  "onb.antes_de_nada": "Antes de mais nada",
  "onb.el_microfono": "O microfone",
  "onb.seguridad": "Segurança",
  "onb.lo_ultimo": "A última pergunta",
  "onb.si": "Sim",
  "onb.no": "Não",
  "onb.conceder": "Permitir o microfone",
  "onb.ahora_no": "Agora não",
  "onb.sin_microfono_seguimos":
    "O navegador não deixou a gente usar o microfone. Vamos continuar mesmo assim: você pode medir sua voz quando " +
    "quiser em “Sua conta”, e o plano se ajusta a ela.",
  "onb.que_te_trae": "O que te traz aqui?",
  "onb.ver_mi_plan": "Ver meu plano",
  "onb.montando": "Montando seu plano…",

  // ── Lo que la persona contó, tal y como se lo devolvemos ──────────
  "motivo.R-MENOR": "A plataforma é só para adultos.",
  "motivo.R-RESPIRACION": "Dificuldade para respirar ou ruído ao puxar o ar.",
  "motivo.R-SANGRE": "Sangue ao tossir ou expectorar.",
  "motivo.R-REPOSO": "Alguém indicou repouso vocal para você.",
  "motivo.R-CIRUGIA-3M": "Cirurgia de garganta ou de cordas vocais nos últimos três meses.",
  "motivo.R-DISFAGIA": "Dificuldade para engolir ou sensação de algo na garganta que não vai embora.",
  "motivo.R-OTALGIA": "Dor ao falar ou engolir que chega até o ouvido.",
  "motivo.R-BULTO": "Um caroço no pescoço.",
  "motivo.R-AFONIA-SUBITA": "Perda de voz repentina e completa.",
  "puerta.no_hay_plan": "Aqui não vamos montar um plano para você.",
  "puerta.me_equivoque": "Respondi algo errado. Voltar às perguntas",
  "puerta.lo_que_contaste": "O que você contou:",
  "cierre.clinico.porque":
    "Não estamos dizendo que você tem alguma coisa. Estamos dizendo que isso precisa ser visto por " +
    "alguém que possa te examinar, e nós não podemos. Um plano de exercícios em cima disso não " +
    "ajuda: encobre.",
  "cierre.clinico.vuelve":
    "Se te avaliarem e disserem que você pode praticar, volte quando quiser. Não cobramos nada de você e " +
    "não deixamos um plano pela metade.",
  "cierre.edad.porque":
    "Isto não é sobre a sua voz. É uma regra da casa: a plataforma é só para adultos, e preferimos dizer " +
    "isso agora, e não depois de ter cobrado de você.",
  "cierre.edad.vuelve":
    "Volte quando fizer dezoito anos. Não cobramos nada e não deixamos nenhum plano pela metade esperando você.",
  // ── Cuánta prisa corre · tarjetas al estilo NHS ────────────────────
  "urgencia.ahora": "Vá ao pronto-socorro agora.",
  "urgencia.ahora_texto": "Não espere até amanhã e não vá dirigindo. Isso precisa ser visto hoje, agora.",
  "urgencia.hoy": "Procure atendimento hoje.",
  "urgencia.hoy_texto": "Ligue hoje mesmo para seu médico ou para o pronto atendimento. Hoje, não esta semana.",
  "urgencia.esta_semana": "Marque consulta esta semana.",
  "urgencia.esta_semana_texto":
    "Não é urgência de hoje, mas também não é de “quando eu lembrar”. Isso precisa ser visto em semanas, " +
    "não em meses.",
  "urgencia.cuando_puedas": "Comente na sua próxima consulta.",
  "urgencia.cuando_puedas_texto": "Não tem pressa, mas quem te acompanha precisa saber.",
  "urgencia.no_molestas": "Você não está fazendo ninguém perder tempo ao pedir uma avaliação. É exatamente para isso que ela serve.",
  "urgencia.numero": "O número de emergência onde você está é o {n}.",
  "urgencia.mientras_tanto":
    "Enquanto isso, você pode continuar aqui. Vamos aliviar a carga da sua voz e te ensinar a " +
    "usá-la sem forçar.",
  "urgencia.sigue_aqui": "Continuar com meu plano",
  "puerta.urgencias": "Vá ao pronto-socorro.",
  "puerta.medico": "Marque consulta com seu médico.",
  "puerta.otorrino": "Marque consulta com um otorrinolaringologista.",
  "puerta.quien_indico_reposo": "Fale com quem indicou o repouso antes de fazer qualquer exercício.",
  "puerta.quien_opero": "Fale com quem te operou antes de fazer qualquer exercício.",

  "bandera.paro_aqui": "Vou parar aqui um momento.",
  "bandera.no_es_lo_mio":
    "Você descreveu **{que}**, e com exercícios de voz eu não consigo te ajudar nisso. Não estou dizendo " +
    "que você tem alguma coisa: estou dizendo que isso tem que ser visto por alguém que possa te " +
    "examinar, e eu não posso.",
  "bandera.aqui_estare": "Quando te avaliarem e disserem que você pode praticar, estarei aqui.",

  "bandera.respiracion": "dificuldade para respirar",
  "bandera.sangre": "sangue ao tossir",
  "bandera.bulto": "um caroço no pescoço",
  "bandera.tragar": "dificuldade para engolir",
  "bandera.oido": "dor ao falar ou ao engolir que chega ao ouvido",
  "bandera.ronquera": "uma mudança na voz que dura semanas",
  "bandera.sin_voz": "uma perda de voz repentina",
  "bandera.dolor": "dor ao falar que não passa",

  "asistente.no_lo_se":
    "Isso eu não sei. Só posso falar do que está no nosso catálogo, e isso não está lá. Se a sua voz está " +
    "te preocupando, o melhor é procurar alguém que possa te ouvir.",
  "asistente.no_puedo_ahora":
    "Não consigo te responder agora. O problema não é com você. Enquanto isso, a biblioteca tem " +
    "o catálogo inteiro, com os exercícios completos.",

  "ficha.cuanto": "Quanto",
  "ficha.que_pasa_por_dentro": "O que acontece por dentro",
  "ficha.como_se_hace": "Como fazer",
  "ficha.lo_que_te_dices": "O que você fala para você mesmo enquanto faz",
  "ficha.en_que_se_falla": "Onde costuma dar errado",
  "ficha.para_y_consulta": "Pare e procure avaliação se…",
  "ficha.que_salio_al_medirlo": "O que as medições mostraram",
  "ficha.quien_lo_propone": "Quem propõe",
  "ficha.si_no_te_sale": "Se não conseguir",
  "ficha.a_favor": "A favor",
  "ficha.en_contra": "Contra",
  "ficha.como_lo_notas": "Como você percebe",
  "ficha.como_se_arregla": "Como corrigir",
  "ficha.es_una_imagen": "Isto é uma imagem, não anatomia",
  "ficha.sin_fuente":
    "Não encontramos ninguém que descreva este exercício assim. Muita gente ensina esse movimento, mas " +
    "não achamos nenhuma fonte assinada. Incluímos mesmo assim, e avisamos você.",
  "ficha.solo_el_manual":
    "Está descrito no manual que deu origem a este projeto. Em nenhum outro lugar que a gente tenha " +
    "encontrado.",
  "ficha.sin_revisar":
    "Os desenhos e o vídeo deste exercício ainda não foram revisados por um profissional. Avisamos aqui " +
    "porque achamos que você deve saber.",

  "reloj.parar": "Parar",
  "reloj.se_acabo": "Acabou o tempo.",
  "reloj.hay_tope": "Nas medições, passado esse ponto a voz ficava mais apertada e menos clara.",
  "reloj.hiciste_lo_que_tocaba": "Você fez a parte de hoje.",
  "reloj.parado":
    "Parado. O que você fez fica registrado, e pronto: parar quando precisa faz parte do exercício — não " +
    "é falhar nele.",

  "mic.grave": "grave",
  "mic.agudo": "agudo",

  "sesion.de_hoy": "A sessão de hoje",
  "sesion.preparacion": "Preparação",
  "sesion.calentamiento": "Aquecimento",
  "sesion.trabajo": "Trabalho",
  "sesion.enfriamiento": "Desaquecimento",
  "sesion.descanso_voz_cansada":
    "Hoje é dia de descanso. Você avisou e o plano ouviu: pegar leve quando a voz está cansada faz parte " +
    "do treino, não é uma interrupção.",
  "sesion.descanso_reciente":
    "Você fez uma sessão há pouco. Duas seguidas não somam: uma atrapalha a outra. Volte daqui a " +
    "algumas horas.",
  "sesion.descanso_cadencia": "Hoje não tem sessão. Descansar faz parte do plano.",
  "sesion.descanso_sin_presupuesto":
    "O tempo de hoje não dá para uma sessão completa com aquecimento e desaquecimento, e esses dois " +
    "não podem ficar de fora. Escolha um tempo maior.",

  "cuenta.correo": "E-mail",
  "cuenta.crear": "Criar conta",
  "cuenta.entrar": "Entrar",
  "cuenta.salir": "Sair",
  "cuenta.soy_adulto": "Tenho 18 anos ou mais",
  "cuenta.borrar": "Excluir minha conta",
  "cuenta.borrar_aviso":
    "A exclusão é definitiva e não dá para desfazer. Suas respostas sobre saúde são excluídas junto.",
  "cuenta.borrar_teclea": "Digite {p} para confirmar",
  "precio.al_ano": "por ano",
  "correo.confirmado_titulo": "Você entrou.",
  "correo.confirmado_texto":
    "Seu e-mail está confirmado e você já entrou. Estamos levando você ao seu plano.",
  "correo.confirmado_boton": "Ir para o meu plano",
  "correo.confirmado_de_por_vida":
    "E seu código já está aplicado: você tem o Voice Comfort inteiro, para sempre, sem pagamentos nem renovações.",
  "correo.confirmado_regalada": "E seu código já está aplicado: você tem o Voice Comfort inteiro, sem pagar.",
  "cuenta.codigo_opcional": "Tem um código de acesso? (opcional)",
  "cuenta.codigo_ayuda": "Digite exatamente como veio, com hifens e tudo. Se não tiver, deixe em branco.",
  "cuenta.error_codigo": "Esse código não é válido, ou já foi usado. A conta não foi criada: tente de novo com o código certo, ou sem nenhum.",
  "cuenta.acepto": "Aceito o {a} e a {b}.",
  "cuenta.aceptoUno": "Aceito o {a}.",
  "cuenta.legalEnOtroIdioma": "Está publicado em outro idioma, e é essa versão que você aceita.",
  "cuenta.legalNoSeSabe": "Não foi possível verificar o que é preciso aceitar.",
  "cuenta.legalReintentar": "Tentar de novo",
  "cuenta.acepto_hay_que": "Você precisa aceitar para criar a conta.",
  "precio.lanzamiento": "Preço de lançamento",
  // 15/09: `cta.sin_tarjeta` («sem cartão, sem conta») se quita; el porqué, en el castellano.
  "precio.normal": "Preço normal",
  // 16/09: terminaba en «, e avisamos antes de cobrar.»; el porqué, en el castellano.
  "precio.renovacion": "Pagamento único por ano. O preço não sobe: você mantém esta tarifa enquanto sua conta estiver ativa.",
  // 18/09 · A moeda da cobrança. Obrigatória, não é letra miúda; o porquê, no castelhano.
  "precio.nota_moneda": "A cobrança é feita em pesos colombianos: {cop}.",
  "precio.cancelar": "Cancelar a assinatura",
  "precio.cancelada":
    "Cancelada. Você continua com acesso até a data de renovação: não cortamos algo que você já " +
    "pagou.",
  "precio.un_clic": "Cancele com um clique na sua conta, sem precisar escrever para ninguém.",

  "pagar.boton": "Assinar",
  "pagar.cerradas_titulo": "Ainda não dá para pagar.",
  "pagar.desactivadas": "As assinaturas estão desativadas neste momento.",
  "pagar.sinSesion": "Sua sessão expirou. Entre de novo e toque no botão outra vez.",
  // 18/09 · A pasarela não responde, que não é o mesmo que estar fechada; o porquê, no castelhano.
  "pagar.no_disponible":
    "Não conseguimos abrir a página de pagamento. Nada foi cobrado: tente de novo em um minuto.",
  "pagar.desactivadas_en_la_cuenta":
    "Enquanto isso, se você tiver um código de acesso, pode resgatá-lo aqui embaixo.",
  // 16/09: sem sessões grátis; o que dizia cada uma, no bloco em espanhol.
  "pagar.desactivadas_en_la_venta":
    "Enquanto isso, você pode montar o seu plano e, se tiver um código de acesso, digitá-lo ao criar sua conta.",

  "muro.titulo": "As assinaturas ainda não estão abertas.",
  // 18/09 · O título quando dá para pagar; o porquê, no castelhano.
  "muro.titulo_hace_falta": "Falta o acesso para praticar",
  "muro.se_puede":
    "Você pode assinar agora e seguir com o seu plano, ou resgatar um código de acesso na sua conta.",
  "muro.sin_acceso": "Para praticar, você precisa de uma assinatura ou de um código de acesso.",
  "muro.mientras":
    "Quando for possível assinar, a opção vai aparecer na sua conta. Se você tiver um código " +
    "de acesso, já pode resgatá-lo lá.",

  "contacto.titulo": "Fale com a gente",
  "contacto.whatsapp": "WhatsApp",
  "contacto.correo": "E-mail",
  // 15/09: `contacto.direccion` («Endereço») se quita; el porqué, en el castellano.
  "contacto.horario": "Horário",
  "contacto.entre_semana": "De segunda a sexta, das {a} às {b}",
  "contacto.sabado": "Sábados, das {a} às {b}",
  "contacto.zona": "Horário de Bogotá ({zona})",
  "contacto.whatsapp_flotante": "Fale com a gente pelo WhatsApp",
  "pie.legales": "Documentos legais",
  "salud.guardar_y_hablar": "Salvar as minhas respostas e falar com alguém",
  "salud.desde_la_puerta":
    "Você pode deixar os seus dados e a gente olha isso com você. Guardamos as " +
    "suas respostas, você fala com a gente e, se estiver tudo certo, ativamos a " +
    "sua conta. Não precisa começar de novo.",
  "salud.desde_la_puerta_con_cuenta":
    "Podemos guardar o que você respondeu e pedir que alguém da equipe veja. " +
    "Você fala com a gente e, se estiver tudo certo, ativamos a sua conta. Não " +
    "precisa começar de novo.",
  "salud.titulo": "Antes de começar, vamos conversar",
  "salud.texto":
    "Pelo que você contou, preferimos que alguém da nossa equipe olhe isso com " +
    "você antes de começar a praticar. A sua conta está criada e as suas " +
    "respostas estão salvas: você não perdeu nada.",
  "salud.que_pasa_ahora":
    "Fale com a gente e dizemos o que precisamos. Normalmente é um atestado de quem cuida de você; assim " +
    "que ele chegar, ativamos a conta.",
  "salud.pausado_titulo": "Pausamos os exercícios",
  "salud.pausado_texto":
    "Notamos algo que vale a pena avaliar antes de você continuar praticando, " +
    "então pausamos os exercícios por segurança. A sua conta, o seu plano e tudo " +
    "o que você já fez continuam aqui.",
  "salud.escribenos_para_seguir": "Fale com a gente e resolvemos isso juntos:",
  // 16/09: sem sessões grátis, o desbloqueio já não diz que dá para praticar; o que dizia, no bloco em espanhol.
  "desbloqueo.titulo": "Sua conta já está aberta.",
  "desbloqueo.texto": "Revisamos o que você mandou.",
  "desbloqueo.pasarela":
    "Falta combinar só uma coisa: como você segue daqui. As assinaturas ainda não estão abertas; quando " +
    "abrirem, elas aparecem na sua conta.",
  "desbloqueo.mientras":
    "Se você tiver um código, digite no campo acima e comece hoje mesmo.",
  // ── Tu cuenta ─────────────────────────────────────────────────────────
  // ── La cabecera de la pagina de venta ─────────────────────────────────
  // 14/09: dizia «Voice Comfort · seus exercícios, e quanto de cada um»; a página nova abre com o título do dono.
  // 15/09: sai «sem cartão e sem cadastro» (o dono: não é verdade); entra a garantia.
  "cabecera.titulo": "Voice Comfort · “Ter voz” diz tudo. Cuide da sua.",
  "cabecera.descripcion":
    "Um plano de voz feito para você, alguns minutos por dia no celular e um assistente " +
    "especialista em voz. Com garantia de catorze dias.",
  "cabecera.og_descripcion":
    "Um plano feito para você, alguns minutos por dia no celular e um assistente especialista em " +
    "voz sempre à mão. Com garantia de catorze dias.",
  // ── A página de vendas: menu, botões, o chat de exemplo e o rodapé (15/09) ──
  "venta.nav_secciones": "Seções da página",
  "venta.nav_como_funciona": "Como funciona",
  "venta.nav_para_quien": "Para quem",
  "venta.nav_asistente": "Assistente",
  "venta.nav_precio": "Preço",
  "venta.nav_preguntas": "Dúvidas",
  "venta.menu_abrir": "Abrir o menu",
  "venta.menu_cerrar": "Fechar o menu",
  "venta.crear_plan": "Criar meu plano",
  "venta.ver_como_funciona": "Ver como funciona",
  "venta.sello_garantia": "Garantia de catorze dias",
  "venta.sello_cancelar": "Cancele quando quiser",
  "venta.sello_movil": "No celular, sem instalar nada",
  "venta.chat_nombre": "Assistente do Voice Comfort",
  "venta.chat_pregunta": "Amanhã tenho seis horas de aula. Como preparo a voz?",
  "venta.chat_respuesta":
    "Hoje faça a sua sessão sem esticar. Amanhã, antes da primeira aula, faça o aquecimento do seu " +
    "plano, e entre uma aula e outra deixe a voz descansar.",
  "venta.precio_plan": "Plano anual",
  "venta.precio_incluye": "O que está incluído",
  "venta.solo_adultos": "Só para maiores de idade.",
  "venta.otra_pregunta": "Ficou com alguma dúvida?",
  "venta.escribenos": "Chame a gente no WhatsApp",
  "venta.pie_lema": "A melhor e mais completa plataforma para cuidar da sua voz.",
  "venta.pie_ayuda": "Ajuda",
  "venta.pie_derechos": "Todos os direitos reservados.",
  "micuenta.titulo": "Sua conta",

  "micuenta.ajustes": "Ajustes",
  "micuenta.salir_de_todos": "Sair em todos os dispositivos",
  "micuenta.salir_de_todos_ayuda":
    "Você sai do Voice Comfort em todos os lugares onde entrou, inclusive neste. Para entrar de novo, mandamos um código para o seu e-mail.",
  "micuenta.tema": "Aparência",

  "micuenta.tu_voz": "Sua voz",
  "micuenta.tu_voz_medida":
    "Sua voz fala em torno de {hz} Hz (o quanto ela é aguda ou grave) e se move por {rango} semitons " +
    "(um semitom é de uma tecla do piano para a do lado).",
  "micuenta.tu_voz_otra_vez":
    "É com isso que escolhemos os exercícios que combinam com a sua voz. Se você mediu com pressa, com " +
    "barulho em volta, ou se faz muito tempo que mediu, meça de novo: são trinta segundos.",
  "micuenta.tu_voz_sin_medir":
    "Você ainda não mediu sua voz, então mostramos o catálogo inteiro. Ao medir, deixamos de fora o " +
    "que passa da sua extensão. São trinta segundos, e o áudio não sai deste aparelho.",
  "micuenta.medir": "Medir minha voz",
  "micuenta.medir_otra_vez": "Medir de novo",
  "micuenta.correo": "Seu e-mail",
  "micuenta.confirmado": "Confirmado.",
  "micuenta.suscripcion": "Sua assinatura",
  "micuenta.sin_suscripcion":
    "Você ainda não tem. Dá para ver o seu plano e a biblioteca inteira; para praticar, é preciso uma " +
    "assinatura ou um código de acesso.",
  "micuenta.activa": "Ativa.",
  "micuenta.prueba": "Você está no período de teste.",
  "micuenta.prueba_hasta": "O teste termina em {fecha}.",
  "micuenta.garantia":
    "Não está funcionando para você? Fale com a gente no WhatsApp nos primeiros catorze dias e devolvemos " +
    "o que você pagou, sem precisar explicar nada.",
  "micuenta.regalada": "Seu acesso é um presente.",
  "micuenta.estado_raro":
    "Não conseguimos dizer agora como está a sua assinatura. Confira de novo daqui a pouco — nada foi " +
    "alterado na sua conta.",
  "micuenta.regalada_hasta": "Até {fecha}.",
  "micuenta.regalada_de_por_vida": "Para sempre, sem data de renovação.",
  "micuenta.vencida": "Seu acesso venceu.",
  "micuenta.vencida_el": "Venceu em {fecha}.",
  "micuenta.vencida_que_hacer": "Se você tiver um código de acesso, resgate aqui mesmo e a sua conta volta a abrir.",
  "micuenta.ya_cancelada": "Cancelada.",
  "micuenta.renueva_el": "Renova em {fecha}.",
  "micuenta.acceso_hasta": "Cancelada. Você tem acesso até {fecha}.",
  "micuenta.acceso_sin_fecha": "Cancelada. Você tem acesso até o fim do período pago.",
  "micuenta.codigo": "Tem um código de acesso?",
  "micuenta.codigo_ayuda": "Digite exatamente como veio, com hifens e tudo.",
  "micuenta.canjear": "Resgatar",
  "micuenta.codigo_canjeado": "Resgatado. Você já tem acesso.",
  "micuenta.codigo_no_vale": "Esse código não é válido, ou já foi usado.",
  "micuenta.borrado": "Conta excluída. Não sobrou nada seu aqui.",
  "micuenta.algo_fue_mal": "Não deu certo. Tente de novo daqui a pouco.",

  "ia.titulo": "Pergunte o que quiser",
  "ia.escribe": "Digite sua pergunta",
  "ia.enviar": "Enviar",
  "ia.hay_que_entrar":
    "Você precisa de uma conta para perguntar. A biblioteca inteira continua aberta sem ela, com os {n} exercícios e suas fontes.",
  "ia.demasiadas":
    "Você chegou ao limite de perguntas das últimas 24 horas. Cada pergunta libera uma vaga 24 horas " +
    "depois de você fazer. Enquanto isso, os exercícios completos estão na biblioteca.",
  "ia.tope_del_dia":
    "O assistente chegou ao limite das últimas 24 horas e não pode responder agora. A biblioteca continua inteira, com os exercícios completos e suas fontes.",
  "ia.de_donde_sale": "De onde isto vem",
  "ia.sin_configurar":
    "O assistente não está disponível agora. Enquanto isso, o catálogo inteiro está na biblioteca, " +
    "com os exercícios completos e suas fontes.",

  "origen.F": "Este número vem direto da fonte.",
  "origen.F~": "Tiramos este número da fonte, com o nosso próprio critério.",
  "origen.N": "Este número foi decidido por nós, e não tem fonte por trás dele.",
};

/* ─────────────────────────────────────────────────────────────────────────
   LA FUNCIÓN
   ───────────────────────────────────────────────────────────────────────── */

export const DICCIONARIOS: Readonly<Record<Idioma, Record<Clave, string>>> = {
  es: ES,
  en: EN,
  pt: PT,
};

/**
 * Traduce. La clave está tipada, así que un error de escritura no compila —
 * y por tanto NUNCA sale a pantalla una clave sin traducir.
 */
export function traducir(idioma: Idioma, clave: Clave): string {
  return DICCIONARIOS[idioma][clave];
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * «1 SESSIONS»
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Eso decía la pantalla del plan después de la primera sesión, y su hermana de
 * al lado decía «It goes up a rung in 1 sessions». En castellano, «1 sesiones»;
 * en portugués, «1 sessões». Las tres iguales de mal, porque las tres claves
 * traían el plural escrito a mano.
 *
 * Salió caminando el producto: hacer una sesión y mirar el planner. Ninguna
 * comprobación lo iba a ver, porque la cadena está bien formada — es el número
 * el que no encaja con ella.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * DOS FORMAS EN LA MISMA CLAVE, SEPARADAS POR UNA BARRA
 *
 *     "planner.por_progreso": "por lo practicado — {n} sesión | {n} sesiones"
 *
 * Y no dos claves, que es la otra manera de hacerlo. Con dos claves, quien
 * añade un idioma o reescribe una frase tiene que acordarse de tocar las dos, y
 * el día que se le olvide una el singular y el plural dirán cosas distintas.
 * Juntas se leen de un vistazo y se traducen a la vez.
 *
 * Vale para los tres idiomas de este producto, que separan el uno del resto y
 * nada más. El día que entre uno con más formas —el ruso tiene tres, el árabe
 * seis— esto no sirve, y entonces lo que hay que meter es una biblioteca, no
 * una barra más.
 *
 * `comprobar-i18n` exige que la barra esté en los tres idiomas o en ninguno:
 * una clave con dos formas en castellano y una en inglés es el fallo silencioso
 * que esto viene a impedir.
 */
export const SEPARADOR_DE_PLURAL = " | ";

export function contar(idioma: Idioma, clave: Clave, n: number): string {
  const [una, varias] = DICCIONARIOS[idioma][clave].split(SEPARADOR_DE_PLURAL);
  /*
    Sin barra se devuelve la cadena tal cual, con su `{n}` sustituido. Así una
    clave que todavía no tenga plural no revienta la pantalla: se ve mal, como
    antes, y `comprobar-i18n` es quien lo caza.
  */
  const forma = (n === 1 ? una : varias ?? una) ?? "";
  return forma.replace("{n}", String(n));
}

/** Todas las claves, para las pruebas. */
export const CLAVES = Object.keys(ES) as readonly Clave[];
